/**
 * BRSR Answer Manager Utility
 * Handles saving/loading section data and converting to step4 format for PDF generation
 */

// Get report-specific localStorage key to isolate data between reports
export const getStorageKey = (reportId, key) => {
  if (!reportId) {
    console.warn(`getStorageKey called without reportId for key: ${key}. This may cause data isolation issues.`);
    // Return an invalid key to prevent data pollution across reports
    // This will effectively prevent saving/loading when no reportId is provided
    return `brsr_NO_REPORT_ID_${key}`;
  }
  return `brsr_${reportId}_${key}`;
};

// Save section data to localStorage
export const saveSectionData = (reportId, section, data) => {
  const key = getStorageKey(reportId, `section${section}_data`);
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (e) {
    console.error(`Failed to save section ${section} data:`, e);
  }
};

// Load section data from localStorage
export const loadSectionData = (reportId, section) => {
  const key = getStorageKey(reportId, `section${section}_data`);
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.error(`Failed to load section ${section} data:`, e);
    return null;
  }
};

// Clear report-specific data from localStorage
export const clearReportData = (reportId) => {
  const keys = ["sectionA_data", "sectionB_data", "sectionC_data"];
  keys.forEach((key) => {
    localStorage.removeItem(getStorageKey(reportId, key));
  });
};

// Convert generic table data to HTML string for PDF
export const convertTableToHtml = (tableData, headers, valueKeys) => {
  if (!tableData || tableData.length === 0) return "";

  let html = "<table><thead><tr>";
  headers.forEach((h) => {
    html += `<th>${h}</th>`;
  });
  html += "</tr></thead><tbody>";

  tableData.forEach((row, index) => {
    html += "<tr>";
    // Add S.No. if not in valueKeys
    if (headers[0].toLowerCase().includes("s.") || headers[0].toLowerCase().includes("no")) {
      html += `<td>${index + 1}</td>`;
      valueKeys.forEach((key) => {
        html += `<td>${row[key] || ""}</td>`;
      });
    } else {
      valueKeys.forEach((key) => {
        html += `<td>${row[key] || ""}</td>`;
      });
    }
    html += "</tr>";
  });

  html += "</tbody></table>";
  return html;
};

// Convert Company Details form data to HTML table
export const convertCompanyDetailsToHtml = (formData) => {
  if (!formData) return "";

  const toMultilineList = (value) => {
    if (value === undefined || value === null) return "";
    return String(value)
      .split(/\r?\n|,/)
      .map((item) => item.trim())
      .filter(Boolean)
      .join("\n");
  };

  const contactLines = [];
  if (formData.contactPersonName) contactLines.push(`Name: ${formData.contactPersonName}`);
  if (formData.contactPersonTelephone) contactLines.push(`Telephone: ${formData.contactPersonTelephone}`);
  if (formData.contactPersonEmail) contactLines.push(`E-mail: ${formData.contactPersonEmail}`);

  const fields = [
    { label: "Corporate Identity Number (CIN) of the Listed Entity", value: formData.cin },
    { label: "Name of the Listed Entity", value: formData.name },
    { label: "Year of Incorporation", value: formData.yearOfIncorporation },
    { label: "Registered Office Address", value: formData.registeredOfficeAddress },
    { label: "Corporate Address", value: formData.corporateAddress },
    { label: "E-mail", value: formData.email },
    { label: "Telephone", value: formData.telephoneCountryCode ? `${formData.telephoneCountryCode} ${formData.telephoneNumber || ""}` : formData.telephoneNumber },
    { label: "Website", value: formData.website },
    { label: "Financial year for which reporting is being done", value: formData.financialYear },
    { label: "Name of the Stock Exchange(s) where shares are listed", value: toMultilineList(formData.stockExchange) },
    { label: "Paid-up Capital", value: formData.paidUpCapitalCurrency ? `${formData.paidUpCapitalCurrency} ${formData.paidUpCapitalAmount || ""}` : formData.paidUpCapitalAmount },
    ...(contactLines.length
      ? [
          {
            label: "Name and contact details (telephone, e-mail address) of the person who may be contacted in case of any queries on the BRSR report:",
            value: contactLines.join("\n"),
          },
        ]
      : []),
    { label: "Reporting boundary - Are the disclosures under this report made on a standalone basis (i.e. only for the entity) or on a consolidated basis (i.e. for the entity and all the entities which form a part of its consolidated financial statements, taken together).", value: formData.reportingBoundary ? formData.reportingBoundary.charAt(0).toUpperCase() + formData.reportingBoundary.slice(1) : "" },
    { label: "Name of assurance provider", value: formData.assuranceProviderName },
    { label: "Type of assurance obtained", value: formData.assuranceType },
  ];

  let html = "<table><thead><tr><th>S. No.</th><th>Field</th><th>Value</th></tr></thead><tbody>";
  let rowNo = 0;
  fields.forEach((field) => {
    const v = field.value;
    const hasValue = v !== undefined && v !== null && String(v).trim() !== "";
    if (!hasValue) return;
    rowNo += 1;
    const valueCell = String(v).includes("\n") ? String(v).replace(/\n/g, "<br/>") : String(v);
    html += `<tr><td>${rowNo}</td><td>${field.label}</td><td>${valueCell}</td></tr>`;
  });
  html += "</tbody></table>";

  return html;
};

// Convert Business Activities table to HTML
export const convertBusinessActivitiesToHtml = (businessActivities) => {
  if (!businessActivities || businessActivities.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Description of Main Activity</th>";
  html += "<th>Description of Business Activity</th>";
  html += "<th>% of Turnover of the Entity</th>";
  html += "</tr></thead><tbody>";

  let total = 0;
  businessActivities.forEach((row, index) => {
    const turnover = parseFloat(row.turnoverPercent) || 0;
    total += turnover;
    html += `<tr>`;
    html += `<td>${index + 1}</td>`;
    html += `<td>${row.mainActivity || ""}</td>`;
    html += `<td>${row.businessActivity || ""}</td>`;
    html += `<td>${row.turnoverPercent || ""}%</td>`;
    html += `</tr>`;
  });

  html += `<tr><td colspan="3"><strong>Total Turnover Coverage:</strong></td><td><strong>${total.toFixed(2)}%</strong></td></tr>`;
  html += "</tbody></table>";

  return html;
};

// Convert Products/Services table to HTML
export const convertProductsServicesToHtml = (productsServices) => {
  if (!productsServices || productsServices.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Product/Service</th>";
  html += "<th>NIC Code</th>";
  html += "<th>% of Total Turnover Contributed</th>";
  html += "</tr></thead><tbody>";

  let total = 0;
  productsServices.forEach((row, index) => {
    const turnover = parseFloat(row.turnoverPercent) || 0;
    total += turnover;
    html += `<tr>`;
    html += `<td>${index + 1}</td>`;
    html += `<td>${row.productService || ""}</td>`;
    html += `<td>${row.nicCode || ""}</td>`;
    html += `<td>${row.turnoverPercent || ""}%</td>`;
    html += `</tr>`;
  });

  html += `<tr><td colspan="3"><strong>Total Turnover Coverage:</strong></td><td><strong>${total.toFixed(2)}%</strong></td></tr>`;
  html += "</tbody></table>";

  return html;
};

// Convert Operations/Locations data to HTML
export const convertOperationsToHtml = (operationsData) => {
  if (!operationsData) return "";

  const plantsNational = parseInt(operationsData.plantsNational) || 0;
  const plantsInternational = parseInt(operationsData.plantsInternational) || 0;
  const officesNational = parseInt(operationsData.officesNational) || 0;
  const officesInternational = parseInt(operationsData.officesInternational) || 0;

  let html = "<table><thead><tr>";
  html += "<th>Category</th>";
  html += "<th>National</th>";
  html += "<th>International</th>";
  html += "<th>Total</th>";
  html += "</tr></thead><tbody>";

  html += `<tr>`;
  html += `<td>Number of Plants</td>`;
  html += `<td>${plantsNational}</td>`;
  html += `<td>${plantsInternational}</td>`;
  html += `<td>${plantsNational + plantsInternational}</td>`;
  html += `</tr>`;

  html += `<tr>`;
  html += `<td>Number of Offices</td>`;
  html += `<td>${officesNational}</td>`;
  html += `<td>${officesInternational}</td>`;
  html += `<td>${officesNational + officesInternational}</td>`;
  html += `</tr>`;

  html += `<tr>`;
  html += `<td><strong>Total</strong></td>`;
  html += `<td><strong>${plantsNational + officesNational}</strong></td>`;
  html += `<td><strong>${plantsInternational + officesInternational}</strong></td>`;
  html += `<td><strong>${plantsNational + plantsInternational + officesNational + officesInternational}</strong></td>`;
  html += `</tr>`;

  html += "</tbody></table>";

  return html;
};

/** Item 19a — National / International markets (states & countries) from saved operationsData. */
export const convertMarketsServedToHtml = (operationsData) => {
  if (!operationsData) return "";
  const hasMarkets = operationsData._locationsAutoPopulated || (operationsData.nationalLocations !== undefined && operationsData.nationalLocations !== null && String(operationsData.nationalLocations).trim() !== "") || (operationsData.internationalLocations !== undefined && operationsData.internationalLocations !== null && String(operationsData.internationalLocations).trim() !== "");
  if (!hasMarkets) return "";

  const nn = parseInt(operationsData.nationalLocations, 10);
  const ii = parseInt(operationsData.internationalLocations, 10);
  const national = Number.isNaN(nn) ? 0 : nn;
  const international = Number.isNaN(ii) ? 0 : ii;

  let html = "<table><thead><tr><th>Locations</th><th>Number</th></tr></thead><tbody>";
  html += `<tr><td>National (No. of States)</td><td>${national}</td></tr>`;
  html += `<tr><td>International (No. of Countries)</td><td>${international}</td></tr>`;
  html += "</tbody></table>";
  return html;
};

// Convert Employees data to HTML
export const convertEmployeesToHtml = (employeesData) => {
  if (!employeesData) return "";

  const calculatePercentage = (num, total) => {
    if (!total || total === 0) return "0.00";
    return ((parseFloat(num) / parseFloat(total)) * 100).toFixed(2);
  };

  const calculateTotal = (a, b) => (parseInt(a) || 0) + (parseInt(b) || 0);

  let html = "<table><thead>";
  html += `
<tr>
  <th style="width:50px" rowspan="2">S. No.</th>
  <th style="width:200px" rowspan="2">Particulars</th>
  <th style="width:100px" rowspan="2">Total (A)</th>
  <th colspan="2" style="text-align:center">Male</th>
  <th colspan="2" style="text-align:center">Female</th>
</tr>
<tr>
  <th style="width:90px">No. (B)</th>
  <th style="width:90px">% (B/A)</th>
  <th style="width:90px">No. (C)</th>
  <th style="width:90px">% (C/A)</th>
</tr>
`;
  html += "</thead><tbody>";

  // Employees section header
  html += '<tr style="background-color:#f5f5f5"><td colspan="7"><strong>EMPLOYEES</strong></td></tr>';

  // Permanent employees
  html += `<tr><td>1</td><td>Permanent (D)</td>`;
  html += `<td>${employeesData.empPermanentTotal || 0}</td>`;
  html += `<td>${employeesData.empPermanentMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.empPermanentMale, employeesData.empPermanentTotal)}%</td>`;
  html += `<td>${employeesData.empPermanentFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.empPermanentFemale, employeesData.empPermanentTotal)}%</td></tr>`;

  // Other than permanent employees
  html += `<tr><td>2</td><td>Other than Permanent (E)</td>`;
  html += `<td>${employeesData.empOtherTotal || 0}</td>`;
  html += `<td>${employeesData.empOtherMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.empOtherMale, employeesData.empOtherTotal)}%</td>`;
  html += `<td>${employeesData.empOtherFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.empOtherFemale, employeesData.empOtherTotal)}%</td></tr>`;

  // Total employees
  const totalEmpTotal = calculateTotal(employeesData.empPermanentTotal, employeesData.empOtherTotal);
  const totalEmpMale = calculateTotal(employeesData.empPermanentMale, employeesData.empOtherMale);
  const totalEmpFemale = calculateTotal(employeesData.empPermanentFemale, employeesData.empOtherFemale);
  html += `<tr><td>3</td><td><strong>Total employees (D + E)</strong></td>`;
  html += `<td><strong>${totalEmpTotal}</strong></td>`;
  html += `<td><strong>${totalEmpMale}</strong></td>`;
  html += `<td><strong>${calculatePercentage(totalEmpMale, totalEmpTotal)}%</strong></td>`;
  html += `<td><strong>${totalEmpFemale}</strong></td>`;
  html += `<td><strong>${calculatePercentage(totalEmpFemale, totalEmpTotal)}%</strong></td></tr>`;

  // Workers section header
  html += '<tr style="background-color:#f5f5f5"><td colspan="7"><strong>WORKERS</strong></td></tr>';

  // Permanent workers
  html += `<tr><td>4</td><td>Permanent (F)</td>`;
  html += `<td>${employeesData.workerPermanentTotal || 0}</td>`;
  html += `<td>${employeesData.workerPermanentMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.workerPermanentMale, employeesData.workerPermanentTotal)}%</td>`;
  html += `<td>${employeesData.workerPermanentFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.workerPermanentFemale, employeesData.workerPermanentTotal)}%</td></tr>`;

  // Other than permanent workers
  html += `<tr><td>5</td><td>Other than Permanent (G)</td>`;
  html += `<td>${employeesData.workerOtherTotal || 0}</td>`;
  html += `<td>${employeesData.workerOtherMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.workerOtherMale, employeesData.workerOtherTotal)}%</td>`;
  html += `<td>${employeesData.workerOtherFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.workerOtherFemale, employeesData.workerOtherTotal)}%</td></tr>`;

  // Total workers
  const totalWorkerTotal = calculateTotal(employeesData.workerPermanentTotal, employeesData.workerOtherTotal);
  const totalWorkerMale = calculateTotal(employeesData.workerPermanentMale, employeesData.workerOtherMale);
  const totalWorkerFemale = calculateTotal(employeesData.workerPermanentFemale, employeesData.workerOtherFemale);
  html += `<tr><td>6</td><td><strong>Total workers (F + G)</strong></td>`;
  html += `<td><strong>${totalWorkerTotal}</strong></td>`;
  html += `<td><strong>${totalWorkerMale}</strong></td>`;
  html += `<td><strong>${calculatePercentage(totalWorkerMale, totalWorkerTotal)}%</strong></td>`;
  html += `<td><strong>${totalWorkerFemale}</strong></td>`;
  html += `<td><strong>${calculatePercentage(totalWorkerFemale, totalWorkerTotal)}%</strong></td></tr>`;

  html += "</tbody></table>";

  return html;
};

/** Item 20b — Differently abled employees & workers (same layout as Section A UI). */
export const convertDifferentlyAbledToHtml = (employeesData) => {
  if (!employeesData) return "";

  const calculatePercentage = (num, total) => {
    if (!total || total === 0) return "0.00";
    return ((parseFloat(num) / parseFloat(total)) * 100).toFixed(2);
  };

  const calculateTotal = (a, b) => (parseInt(a, 10) || 0) + (parseInt(b, 10) || 0);

  let html = "<table><thead>";
  html += `
<tr>
  <th rowspan="2">S. No.</th>
  <th rowspan="2">Particulars</th>
  <th rowspan="2">Total (A)</th>
  <th colspan="2" style="text-align:center">Male</th>
  <th colspan="2" style="text-align:center">Female</th>
</tr>
<tr>
  <th>No. (B)</th>
  <th>% (B/A)</th>
  <th>No. (C)</th>
  <th>% (C/A)</th>
</tr>
`;
  html += "</thead><tbody>";

  html += '<tr style="background-color:#f5f5f5"><td colspan="7"><strong>DIFFERENTLY ABLED EMPLOYEES</strong></td></tr>';

  html += `<tr><td>1</td><td>Permanent (D)</td>`;
  html += `<td>${employeesData.diffAbledEmpPermanentTotal || 0}</td>`;
  html += `<td>${employeesData.diffAbledEmpPermanentMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledEmpPermanentMale, employeesData.diffAbledEmpPermanentTotal)}%</td>`;
  html += `<td>${employeesData.diffAbledEmpPermanentFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledEmpPermanentFemale, employeesData.diffAbledEmpPermanentTotal)}%</td></tr>`;

  html += `<tr><td>2</td><td>Other than Permanent (E)</td>`;
  html += `<td>${employeesData.diffAbledEmpOtherTotal || 0}</td>`;
  html += `<td>${employeesData.diffAbledEmpOtherMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledEmpOtherMale, employeesData.diffAbledEmpOtherTotal)}%</td>`;
  html += `<td>${employeesData.diffAbledEmpOtherFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledEmpOtherFemale, employeesData.diffAbledEmpOtherTotal)}%</td></tr>`;

  const teT = calculateTotal(employeesData.diffAbledEmpPermanentTotal, employeesData.diffAbledEmpOtherTotal);
  const teM = calculateTotal(employeesData.diffAbledEmpPermanentMale, employeesData.diffAbledEmpOtherMale);
  const teF = calculateTotal(employeesData.diffAbledEmpPermanentFemale, employeesData.diffAbledEmpOtherFemale);
  html += `<tr><td>3</td><td><strong>Total differently abled employees (D + E)</strong></td>`;
  html += `<td><strong>${teT}</strong></td><td><strong>${teM}</strong></td><td><strong>${calculatePercentage(teM, teT)}%</strong></td>`;
  html += `<td><strong>${teF}</strong></td><td><strong>${calculatePercentage(teF, teT)}%</strong></td></tr>`;

  html += '<tr style="background-color:#f5f5f5"><td colspan="7"><strong>DIFFERENTLY ABLED WORKERS</strong></td></tr>';

  html += `<tr><td>4</td><td>Permanent (F)</td>`;
  html += `<td>${employeesData.diffAbledWorkerPermanentTotal || 0}</td>`;
  html += `<td>${employeesData.diffAbledWorkerPermanentMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledWorkerPermanentMale, employeesData.diffAbledWorkerPermanentTotal)}%</td>`;
  html += `<td>${employeesData.diffAbledWorkerPermanentFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledWorkerPermanentFemale, employeesData.diffAbledWorkerPermanentTotal)}%</td></tr>`;

  html += `<tr><td>5</td><td>Other than permanent (G)</td>`;
  html += `<td>${employeesData.diffAbledWorkerOtherTotal || 0}</td>`;
  html += `<td>${employeesData.diffAbledWorkerOtherMale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledWorkerOtherMale, employeesData.diffAbledWorkerOtherTotal)}%</td>`;
  html += `<td>${employeesData.diffAbledWorkerOtherFemale || 0}</td>`;
  html += `<td>${calculatePercentage(employeesData.diffAbledWorkerOtherFemale, employeesData.diffAbledWorkerOtherTotal)}%</td></tr>`;

  const twT = calculateTotal(employeesData.diffAbledWorkerPermanentTotal, employeesData.diffAbledWorkerOtherTotal);
  const twM = calculateTotal(employeesData.diffAbledWorkerPermanentMale, employeesData.diffAbledWorkerOtherMale);
  const twF = calculateTotal(employeesData.diffAbledWorkerPermanentFemale, employeesData.diffAbledWorkerOtherFemale);
  html += `<tr><td>6</td><td><strong>Total differently abled workers (F + G)</strong></td>`;
  html += `<td><strong>${twT}</strong></td><td><strong>${twM}</strong></td><td><strong>${calculatePercentage(twM, twT)}%</strong></td>`;
  html += `<td><strong>${twF}</strong></td><td><strong>${calculatePercentage(twF, twT)}%</strong></td></tr>`;

  html += "</tbody></table>";
  return html;
};

// Convert women participation table (Section A - 21) to HTML
export const convertWomenParticipationToHtml = (employeesData) => {
  if (!employeesData) return "";

  const calcPercent = (count, total) => {
    const c = parseFloat(count) || 0;
    const t = parseFloat(total) || 0;
    if (!t) return "0.00";
    return ((c / t) * 100).toFixed(2);
  };

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th><th>Particulars</th><th>Total (A)</th><th>No. (B)</th><th>% (B/A)</th>";
  html += "</tr></thead><tbody>";

  html += `<tr><td>1</td><td>Board of Directors</td><td>${employeesData.womenBoardTotal || 0}</td><td>${employeesData.womenBoardCount || 0}</td><td>${calcPercent(employeesData.womenBoardCount, employeesData.womenBoardTotal)}%</td></tr>`;
  html += `<tr><td>2</td><td>Senior Management</td><td>${employeesData.womenSeniorTotal || 0}</td><td>${employeesData.womenSeniorCount || 0}</td><td>${calcPercent(employeesData.womenSeniorCount, employeesData.womenSeniorTotal)}%</td></tr>`;
  html += `<tr><td>3</td><td>Middle Management</td><td>${employeesData.womenMiddleTotal || 0}</td><td>${employeesData.womenMiddleCount || 0}</td><td>${calcPercent(employeesData.womenMiddleCount, employeesData.womenMiddleTotal)}%</td></tr>`;
  html += `<tr><td>4</td><td>Junior Management</td><td>${employeesData.womenJuniorTotal || 0}</td><td>${employeesData.womenJuniorCount || 0}</td><td>${calcPercent(employeesData.womenJuniorCount, employeesData.womenJuniorTotal)}%</td></tr>`;

  html += "</tbody></table>";
  return html;
};

// Convert turnover rate table (Section A - 22) to HTML
export const convertTurnoverRateToHtml = (employeesData, financialYears = {}) => {
  if (!employeesData) return "";

  const L = buildFyHeaderLabels(financialYears);
  const total = (m, f) => ((parseFloat(m) || 0) + (parseFloat(f) || 0)).toFixed(2);

  let html = "<table><thead>";
  html += `<tr><th rowspan="2"> </th><th colspan="3">${L.colCurrent}</th><th colspan="3">${L.colPrevious}</th><th colspan="3">${L.colPrior}</th></tr>`;
  html += "<tr><th>Male</th><th>Female</th><th>Total</th><th>Male</th><th>Female</th><th>Total</th><th>Male</th><th>Female</th><th>Total</th></tr>";
  html += "</thead><tbody>";

  html += "<tr>";
  html += "<td>Permanent Employees</td>";
  html += `<td>${employeesData.turnoverEmpCurrentMale || 0}</td><td>${employeesData.turnoverEmpCurrentFemale || 0}</td><td>${total(employeesData.turnoverEmpCurrentMale, employeesData.turnoverEmpCurrentFemale)}</td>`;
  html += `<td>${employeesData.turnoverEmpPreviousMale || 0}</td><td>${employeesData.turnoverEmpPreviousFemale || 0}</td><td>${total(employeesData.turnoverEmpPreviousMale, employeesData.turnoverEmpPreviousFemale)}</td>`;
  html += `<td>${employeesData.turnoverEmpPriorMale || 0}</td><td>${employeesData.turnoverEmpPriorFemale || 0}</td><td>${total(employeesData.turnoverEmpPriorMale, employeesData.turnoverEmpPriorFemale)}</td>`;
  html += "</tr>";

  html += "<tr>";
  html += "<td>Permanent Workers</td>";
  html += `<td>${employeesData.turnoverWorkerCurrentMale || 0}</td><td>${employeesData.turnoverWorkerCurrentFemale || 0}</td><td>${total(employeesData.turnoverWorkerCurrentMale, employeesData.turnoverWorkerCurrentFemale)}</td>`;
  html += `<td>${employeesData.turnoverWorkerPreviousMale || 0}</td><td>${employeesData.turnoverWorkerPreviousFemale || 0}</td><td>${total(employeesData.turnoverWorkerPreviousMale, employeesData.turnoverWorkerPreviousFemale)}</td>`;
  html += `<td>${employeesData.turnoverWorkerPriorMale || 0}</td><td>${employeesData.turnoverWorkerPriorFemale || 0}</td><td>${total(employeesData.turnoverWorkerPriorMale, employeesData.turnoverWorkerPriorFemale)}</td>`;
  html += "</tr>";

  html += "</tbody></table>";
  return html;
};

// Convert Holding Companies data to HTML
export const convertHoldingCompaniesToHtml = (holdingCompanies) => {
  if (!holdingCompanies || holdingCompanies.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Name of Holding/Subsidiary/Associate Company</th>";
  html += "<th>Type (Holding/Subsidiary/Associate)</th>";
  html += "<th>% of Shares Held</th>";
  html += "<th>Participates in BRSR (Yes/No)</th>";
  html += "</tr></thead><tbody>";

  const holdingTypeLabels = { holding: "Holding", subsidiary: "Subsidiary", associate: "Associate company" };
  const participatesLabels = { yes: "Yes", no: "No" };

  holdingCompanies.forEach((row, index) => {
    html += `<tr>`;
    html += `<td>${index + 1}</td>`;
    html += `<td>${row.name || ""}</td>`;
    html += `<td>${holdingTypeLabels[row.type] || row.type || ""}</td>`;
    html += `<td>${row.sharePercentage || ""}%</td>`;
    html += `<td>${participatesLabels[row.participates] || row.participates || ""}</td>`;
    html += `</tr>`;
  });

  html += "</tbody></table>";

  return html;
};

/** CSR Q24(i)–(iii) text for PDF / exports (ASCII-safe units for jsPDF). */
export const formatCsrApplicableAnswerForPdf = (csr) => {
  if (!csr) return "";
  const app = String(csr.csrApplicable || "").toLowerCase();
  if (app === "yes") {
    const details = String(csr.csrPolicyWebLink || "").trim();
    return details ? `Yes\n${details}` : "Yes";
  }
  if (app === "no") return "No";
  return csr.csrApplicable || "";
};

// Convert CSR data to HTML
export const convertCsrDataToHtml = (csrData) => {
  if (!csrData) return "";

  let html = "<table><thead><tr>";
  html += "<th>Field</th>";
  html += "<th>Value</th>";
  html += "</tr></thead><tbody>";

  html += `<tr><td>CSR Applicable</td><td>${formatCsrApplicableAnswerForPdf(csrData) || "Not specified"}</td></tr>`;
  html += `<tr><td>Turnover (in Rs.)</td><td>${csrData.turnover ? `Rs. ${csrData.turnover}` : ""}</td></tr>`;
  html += `<tr><td>Net Worth (in Rs.)</td><td>${csrData.netWorth ? `Rs. ${csrData.netWorth}` : ""}</td></tr>`;

  html += "</tbody></table>";

  return html;
};

// Convert Complaints/Grievances data to HTML
export const convertComplaintsDataToHtml = (complaintsData, financialYears = {}) => {
  if (!complaintsData) return "";

  const L = buildFyHeaderLabels(financialYears);
  const stakeholders = ["communities", "investors", "shareholders", "employees", "customers", "valueChain", "other"];
  const labels = {
    communities: "Communities",
    investors: "Investors (other than shareholders)",
    shareholders: "Shareholders",
    employees: "Employees and workers",
    customers: "Customers",
    valueChain: "Value Chain Partners",
    other: "Other (please specify)",
  };

  let html = "<table><thead><tr>";
  html += '<th rowspan="2">Stakeholder group from whom complaint is received</th>';
  html += '<th rowspan="2">Grievance Redressal Mechanism in Place (Yes/No)</th>';
  html += '<th rowspan="2">(If Yes, then provide web-link for grievance redress policy)</th>';
  html += `<th colspan="3">${L.colCurrentLong}</th>`;
  html += `<th colspan="3">${L.colPreviousLong}</th>`;
  html += "</tr><tr>";
  html += "<th>Number of complaints filed during the year</th><th>Number of complaints pending resolution at close of the year</th><th>Remarks</th>";
  html += "<th>Number of complaints filed during the year</th><th>Number of complaints pending resolution at close of the year</th><th>Remarks</th>";
  html += "</tr></thead><tbody>";

  stakeholders.forEach((key) => {
    const data = complaintsData[key];
    if (data) {
      html += `<tr>`;
      html += `<td>${labels[key] || key}</td>`;
      html += `<td>${data.mechanism || "-"}</td>`;
      html += `<td>${data.webLink || "-"}</td>`;
      html += `<td>${data.currentFiled || "0"}</td>`;
      html += `<td>${data.currentPending || "0"}</td>`;
      html += `<td>${data.currentRemarks || "-"}</td>`;
      html += `<td>${data.previousFiled || "0"}</td>`;
      html += `<td>${data.previousPending || "0"}</td>`;
      html += `<td>${data.previousRemarks || "-"}</td>`;
      html += `</tr>`;
    }
  });

  html += "</tbody></table>";
  return html;
};

// Convert Material Issues data to HTML
export const convertMaterialIssuesToHtml = (materialIssues) => {
  if (!materialIssues || materialIssues.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Material issue identified</th>";
  html += "<th>Indicate whether risk or opportunity (R/O)</th>";
  html += "<th>Rationale for identifying the risk / opportunity</th>";
  html += "<th>In case of risk, approach to adapt or mitigate</th>";
  html += "<th>Financial implications of the risk or opportunity (Indicate positive or negative implications)</th>";
  html += "</tr></thead><tbody>";

  materialIssues.forEach((row, index) => {
    html += `<tr>`;
    html += `<td>${index + 1}</td>`;
    html += `<td>${row.issue || ""}</td>`;
    html += `<td>${row.type ? row.type.charAt(0).toUpperCase() + row.type.slice(1) : ""}</td>`;
    html += `<td>${row.rationale || ""}</td>`;
    html += `<td>${row.approach || ""}</td>`;
    html += `<td>${row.financialImplications || ""}</td>`;
    html += `</tr>`;
  });

  html += "</tbody></table>";

  return html;
};

// ============================================================================
// SECTION C: Question Text Mappings for PDF Generation
// ============================================================================

// Principle 1 Essential Questions
const PRINCIPLE_1_ESSENTIAL_QUESTIONS = {
  q1_training: "1. Percentage coverage by training and awareness programmes on any of the Principles during the financial year:",
  q2_fines: "2. Details of fines / penalties /punishment/ award/ compounding fees/ settlement amount paid in proceedings (by the entity or by directors / KMPs) with regulators/ law enforcement agencies/ judicial institutions, in the financial year:",
  q3_appeals: "3. Of the instances disclosed in Question 2 above, details of the Appeal/ Revision preferred in cases where monetary or non-monetary action has been appealed.",
  q4_antiCorruption: "4. Does the entity have an anti-corruption or anti-bribery policy? If yes, provide details in brief and if available, provide a web-link to the policy.",
  q5_disciplinary: "5. Number of Directors/KMPs/employees/workers against whom disciplinary action was taken by any law enforcement agency for the charges of bribery/ corruption:",
  q6_conflict: "6. Details of complaints with regard to conflict of interest:",
  q7_corrective: "7. Provide details of any corrective action taken or underway on issues related to fines / penalties / action taken by regulators/ law enforcement agencies/ judicial institutions, on cases of corruption and conflicts of interest.",
  q8_accountsPayable: "8. Number of days of accounts payables ((Accounts payable *365) / Cost of goods/services procured) in the following format:",
  q9_openness: "9. Open-ness of business:",
};

// Principle 1 Leadership Questions
const PRINCIPLE_1_LEADERSHIP_QUESTIONS = {
  q1_valueChainAwareness: "1. Awareness programmes conducted for value chain partners on any of the Principles during the financial year:",
  q2_conflictProcesses: "2. Does the entity have processes in place to avoid/ manage conflict of interests involving members of the Board? (Yes/No) If Yes, provide details of the same.",
};

// Principle 2 Essential Questions
const PRINCIPLE_2_ESSENTIAL_QUESTIONS = {
  q1_rdCapex: "1. Percentage of R&D and capital expenditure (capex) investments in specific technologies to improve the environmental and social impacts of product and processes to total R&D and capex investments made by the entity, respectively.",
  q2_sustainableSourcing: "2a. Does the entity have procedures in place for sustainable sourcing? (Yes/No)",
  q3_reclaimProcesses: "3. Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for (a) Plastics (including packaging), (b) E-waste, (c) Hazardous waste and (d) other waste.",
  q4_epr: "4. Whether Extended Producer Responsibility (EPR) is applicable to the entity's activities (Yes / No). If yes, whether the waste collection plan is in line with the Extended Producer Responsibility (EPR) plan submitted to Pollution Control Boards? If not, provide steps taken to address the same.",
};

// Principle 2 Leadership Questions
const PRINCIPLE_2_LEADERSHIP_QUESTIONS = {
  q1_lca: "1. Has the entity conducted Life Cycle Perspective / Assessments (LCA) for any of its products (for manufacturing industry) or for its services (for service industry)? If yes, provide details in the following format?",
  q2_concerns: "2. If there are any significant social or environmental concerns and/or risks arising from production or disposal of your products / services, as identified in the Life Cycle Perspective / Assessments (LCA) or through any other means, briefly describe the same along-with action taken to mitigate the same.",
  q3_recycledMaterial: "3. Percentage of recycled or reused input material to total material (by value) used in production (for manufacturing industry) or providing services (for service industry).",
  q4_reclaimed: "4. Of the products and packaging reclaimed at end of life of products, amount (in metric tonnes) reused, recycled, and safely disposed, as per the following format:",
  q5_reclaimedPercentage: "5. Reclaimed products and their packaging materials (as percentage of products sold) for each product category.",
};

// Principle 3 Essential Questions
const PRINCIPLE_3_ESSENTIAL_QUESTIONS = {
  q1a_employeeWellbeing: "1a. Details of measures for the well-being of employees:",
  q1b_workerWellbeing: "1b. Details of measures for the well-being of workers:",
  q1c_wellbeingSpending: "1c. Spending on measures towards well-being of employees and workers (including health, well-being and safety) as a percentage of total revenue of the company:",
  q2_retirementBenefits: "2. Details of retirement benefits, for Current FY and Previous Financial Year.",
  q3_accessibility: "3. Accessibility of workplaces - Are the premises / offices of the entity accessible to differently abled employees and workers, as per the requirements of the Rights of Persons with Disabilities Act, 2016? If not, whether any steps are being taken by the entity in this regard.",
  q4_equalOpportunity: "4. Does the entity have an equal opportunity policy as per the Rights of Persons with Disabilities Act, 2016? If so, provide a web-link to the policy.",
  q5_returnRetention: "5. Return to work and Retention rates of permanent employees and workers that took parental leave.",
  q6_grievance: "6. Is there a mechanism available to receive and redress grievances for the following categories of employees and worker? If yes, give details of the mechanism in brief.",
  q7_membership: "7. Membership of employees and worker in association(s) or Unions recognised by the listed entity:",
  q8_training: "8. Details of training given to employees and workers:",
  q9_performanceReviews: "9. Details of performance and career development reviews of employees and worker:",
  q10_healthSafety: "10. Health and safety management system:",
  q11_safetyIncidents: "11. Details of safety related incidents:",
  q12_safeWorkplace: "12. Describe the measures taken by the entity to ensure a safe and healthy work place.",
  q13_complaints: "13. Number of Complaints on the following made by employees and workers:",
  q14_assessments: "14. Assessments for the year:",
  q15_correctiveAction: "15. Provide details of any corrective action taken or underway to address safety-related incidents (if any) and on significant risks / concerns arising from assessments of health & safety practices and working conditions.",
};

// Principle 3 Leadership Questions
const PRINCIPLE_3_LEADERSHIP_QUESTIONS = {
  q1_lifeInsurance: "1. Does the entity extend any life insurance or any compensatory package in the event of death of (A) Employees (Y/N) (B) Workers (Y/N).",
  q2_statutoryDues: "2. Provide the measures undertaken by the entity to ensure that statutory dues have been deducted and deposited by the value chain partners.",
  q3_rehabilitation: "3. Provide the number of employees / workers having suffered high consequence work related injury / ill-health / fatalities (as reported in Q11 of Essential Indicators above), who have been are rehabilitated and placed in suitable employment or whose family members have been placed in suitable employment:",
  q4_transitionAssistance: "4. Does the entity provide transition assistance programs to facilitate continued employability and the management of career endings resulting from retirement or termination of employment? (Yes/ No)",
  q5_valueChainAssessment: "5. Details on assessment of value chain partners:",
  q6_valueChainCorrective: "6. Provide details of any corrective actions taken or underway to address significant risks / concerns arising from assessments of health and safety practices and working conditions of value chain partners.",
};

// Principle 4 Essential Questions
const PRINCIPLE_4_ESSENTIAL_QUESTIONS = {
  q1_identifyingStakeholders: "1. Describe the processes for identifying key stakeholder groups of the entity.",
  q2_stakeholderGroups: "2. List stakeholder groups identified as key for your entity and the frequency of engagement with each stakeholder group.",
};

// Principle 4 Leadership Questions
const PRINCIPLE_4_LEADERSHIP_QUESTIONS = {
  q1_consultationProcesses: "1. Provide the processes for consultation between stakeholders and the Board on economic, environmental, and social topics or if consultation is delegated, how is feedback from such consultations provided to the Board.",
  q2_stakeholderConsultation: "2. Whether stakeholder consultation is used to support the identification and management of environmental, and social topics (Yes / No). If so, provide details of instances as to how the inputs received from stakeholders on these topics were incorporated into policies and activities of the entity.",
  q3_vulnerableEngagement: "3. Provide details of instances of engagement with, and actions taken to, address the concerns of vulnerable/ marginalized stakeholder groups.",
};

// Principle 5 Essential Questions
const PRINCIPLE_5_ESSENTIAL_QUESTIONS = {
  q1_humanRightsTraining: "1. Employees and workers who have been provided training on human rights issues and policy(ies) of the entity, in the following format:",
  q2_minimumWages: "2. Details of minimum wages paid to employees and workers, in the following format:",
  q3a_medianRemuneration: "3a. Details of remuneration/salary/wages - a. Median remuneration / wages:",
  q3b_grossWagesFemales: "3b. Gross wages paid to females as % of total wages paid by the entity, in the following format:",
  q4_focalPoint: "4. Do you have a focal point (Individual/ Committee) responsible for addressing human rights impacts or issues caused or contributed to by the business? (Yes/No)",
  q5_internalMechanisms: "5. Describe the internal mechanisms in place to redress grievances related to human rights issues.",
  q6_complaints: "6. Number of Complaints on the following made by employees and workers:",
  q7_poshComplaints: "7. Complaints filed under the Sexual Harassment of Women at Workplace (Prevention, Prohibition and Redressal) Act, 2013, in the following format:",
  q8_preventAdverse: "8. Mechanisms to prevent adverse consequences to the complainant in discrimination and harassment cases.",
  q9_businessAgreements: "9. Do human rights requirements form part of your business agreements and contracts? (Yes/No)",
  q10_assessments: "10. Assessments for the year:",
  q11_correctiveActions: "11. Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 10 above.",
};

// Principle 5 Leadership Questions
const PRINCIPLE_5_LEADERSHIP_QUESTIONS = {
  q1_businessProcessModifications: "1. Details of a business process being modified / introduced as a result of addressing human rights grievances/complaints.",
  q2_dueDiligenceScope: "2. Details of the scope and coverage of any Human rights due-diligence conducted.",
  q3_accessibilityVisitors: "3. Is the premise/office of the entity accessible to differently abled visitors, as per the requirements of the Rights of Persons with Disabilities Act, 2016?",
  q4_valueChainAssessment: "4. Details on assessment of value chain partners:",
  q5_correctiveActions: "5. Provide details of any corrective actions taken or underway to address significant risks / concerns arising from the assessments at Question 4 above.",
};

// Principle 6 Essential Questions
const PRINCIPLE_6_ESSENTIAL_QUESTIONS = {
  q1_energyConsumption: "1. Details of total energy consumption (in Joules or multiples) and energy intensity, in the following format:",
  q2_patScheme: "2. Does the entity have any sites / facilities identified as designated consumers (DCs) under the Performance, Achieve and Trade (PAT) Scheme of the Government of India? (Y/N) If yes, disclose whether targets set under the PAT scheme have been achieved. In case targets have not been achieved, provide the remedial action taken, if any.",
  q3_waterDetails: "3. Provide the following details related to water:",
  q4_waterDischarge: "4. Provide the following details related to water discharged:",
  q5_zeroLiquidDischarge: "5. Has the entity implemented a mechanism for Zero Liquid Discharge? If yes, provide details of its coverage and implementation.",
  q6_airEmissions: "6. Please provide details of air emissions (other than GHG emissions) by the entity, in the following format:",
  q6_airEmissionsExternalAssessment: "",
  q7_ghgEmissions: "7. Provide details of greenhouse gas emissions (Scope 1 and Scope 2 emissions) & its intensity, in the following format:",
  q8_ghgReductionProjects: "8. Does the entity have any project related to reducing Green House Gas emission? If Yes, then provide details.",
  q9_wasteManagement: "9. Provide details related to waste management by the entity, in the following format:",
  q10_wasteManagementPractices: "10. Briefly describe the waste management practices adopted in your establishments. Describe the strategy adopted by your company to reduce usage of hazardous and toxic chemicals in your products and processes and the practices adopted to manage such wastes.",
  q11_ecologicallySensitive: "11. If the entity has operations/offices in/around ecologically sensitive areas (such as national parks, wildlife sanctuaries, biosphere reserves, wetlands, biodiversity hotspots, forests, coastal regulation zones etc.) where environmental approvals / clearances are required, please specify details in the following format:",
  q12_eia: "12. Details of environmental impact assessments of projects undertaken by the entity based on applicable laws, in the current financial year:",
  q13_environmentalCompliance: "13. Is the entity compliant with the applicable environmental law/ regulations/ guidelines in India; such as the Water (Prevention and Control of Pollution) Act, Air (Prevention and Control of Pollution) Act, Environment protection act and rules thereunder (Y/N). If not, provide details of all such non-compliances, in the following format:",
};

// Principle 6 Leadership Questions
const PRINCIPLE_6_LEADERSHIP_QUESTIONS = {
  q1_waterStress: "1. Water withdrawal, consumption and discharge in areas of water stress (in kilolitres): For each facility / plant located in areas of water stress, provide the following information:",
  q2_scope3Emissions: "2. Please provide details of total Scope 3 emissions & its intensity, in the following format:",
  q3_biodiversityImpact: "3. With respect to the ecologically sensitive areas reported at Question 11 of Essential Indicators above, provide details of significant direct & indirect impact of the entity on biodiversity in such areas along-with prevention and remediation activities.",
  q4_resourceEfficiency: "4. If the entity has undertaken any specific initiatives or used innovative technology or solutions to improve resource efficiency, or reduce impact due to emissions / effluent discharge / waste generated, please provide details of the same as well as outcome of such initiatives, as per the following format:",
  q5_businessContinuity: "5. Does the entity have a business continuity and disaster management plan? Give details in 100 words/ web link.",
  q6_valueChainImpact: "6. Disclose any significant adverse impact to the environment, arising from the value chain of the entity. What mitigation or adaptation measures have been taken by the entity in this regard.",
  q7_valueChainAssessment: "7. Percentage of value chain partners (by value of business done with such partners) that were assessed for environmental impacts.",
};

// Principle 7 Essential Questions
const PRINCIPLE_7_ESSENTIAL_QUESTIONS = {
  q1a_numberOfAffiliations: "1a. Number of affiliations with trade and industry chambers/ associations.",
  q1b_tradeChambers: "1b. List the top 10 trade and industry chambers/ associations (determined based on the total members of such body) the entity is a member of/ affiliated to.",
  q2_correctiveAction: "2. Provide details of corrective action taken or underway on any issues related to anti competitive conduct by the entity, based on adverse orders from regulatory authorities.",
};

// Principle 7 Leadership Questions
const PRINCIPLE_7_LEADERSHIP_QUESTIONS = {
  q1_publicPolicy: "1. Details of public policy positions advocated by the entity:",
};

// Principle 8 Essential Questions
const PRINCIPLE_8_ESSENTIAL_QUESTIONS = {
  q1_sia: "1. Details of Social Impact Assessments (SIA) of projects undertaken by the entity based on applicable laws, in the current financial year.",
  q2_rrProjects: "2. Provide information on project(s) for which ongoing Rehabilitation and Resettlement (R&R) is being undertaken by your entity, in the following format:",
  q3_grievanceMechanisms: "3. Describe the mechanisms to receive and redress grievances of the community.",
  q4_inputMaterial: "4. Percentage of input material (inputs to total inputs by value) sourced from suppliers:",
  q5_jobCreation: "5. Job creation in smaller towns – Disclose wages paid to persons employed (including employees or workers employed on a permanent or non-permanent / on contract basis) in the following locations, as % of total wage cost:",
};

// Principle 8 Leadership Questions
const PRINCIPLE_8_LEADERSHIP_QUESTIONS = {
  q1_mitigateImpacts: "1. Provide details of actions taken to mitigate any negative social impacts identified in the Social Impact Assessments (Reference: Question 1 of Essential Indicators above):",
  q2_csrAspirations: "2. Provide the following information on CSR projects undertaken by your entity in designated aspirational districts as identified by government bodies:",
  q3a_preferentialPolicy: "3a. Do you have a preferential procurement policy where you give preference to purchase from suppliers comprising marginalized /vulnerable groups? (Yes/No)",
  q3b_marginalized: "3b. From which marginalized /vulnerable groups do you procure?",
  q3c_procurementPercentage: "3c. What percentage of total procurement (by value) does it constitute?",
  q4_ipBenefits: "4. Details of the benefits derived and shared from the intellectual properties owned or acquired by your entity (in the current financial year), based on traditional knowledge:",
  q5_ipDisputes: "5. Details of corrective actions taken or underway, based on any adverse order in intellectual property related disputes wherein usage of traditional knowledge is involved.",
  q6_csrBeneficiaries: "6. Details of beneficiaries of CSR Projects:",
};

// Principle 9 Essential Questions
const PRINCIPLE_9_ESSENTIAL_QUESTIONS = {
  q1_consumerComplaints: "1. Describe the mechanisms in place to receive and respond to consumer complaints and feedback.",
  q2_productInfoTurnover: "2. Turnover of products and/ services as a percentage of turnover from all products/service that carry information about:",
  q3_complaintNumbers: "3. Number of consumer complaints in respect of the following:",
  q4_productRecalls: "4. Details of instances of product recalls on account of safety issues:",
  q5_cyberSecurityFramework: "5. Does the entity have a framework/ policy on cyber security and risks related to data privacy? (Yes/No) If available, provide a web-link of the policy.",
  q6_correctiveActions: "6. Provide details of any corrective actions taken or underway on issues relating to advertising, and delivery of essential services; cyber security and data privacy of customers; re-occurrence of instances of product recalls; penalty / action taken by regulatory authorities on safety of products / services.",
  q7a_dataBreachesInstances: "7a. Provide the following information relating to data breaches: a. Number of instances of data breaches",
  q7b_dataBreachesPercentage: "7b. Percentage of data breaches involving personally identifiable information of customers",
  q7c_dataBreachesImpact: "7c. Impact, if any, of the data breaches",
};

// Principle 9 Leadership Questions
const PRINCIPLE_9_LEADERSHIP_QUESTIONS = {
  q1_informationChannels: "1. Channels / platforms where information on products and services of the entity can be accessed (provide web link, if available).",
  q2_consumerEducation: "2. Steps taken to inform and educate consumers about safe and responsible usage of products and/or services.",
  q3_riskCommunication: "3. Mechanisms in place to inform consumers of any risk of disruption/discontinuation of essential services.",
  q4_productInfoSurveys: "4. Does the entity display product information on the product over and above what is mandated as per local laws? (Yes/No/Not Applicable) If yes, provide details in brief. Did your entity carry out any survey with regard to consumer satisfaction relating to the major products / services of the entity, significant locations of operation of the entity or the entity as a whole? (Yes/No)",
};

// Combined question mappings for easy lookup
const SECTION_C_QUESTION_MAPS = {
  1: { essential: PRINCIPLE_1_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_1_LEADERSHIP_QUESTIONS },
  2: { essential: PRINCIPLE_2_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_2_LEADERSHIP_QUESTIONS },
  3: { essential: PRINCIPLE_3_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_3_LEADERSHIP_QUESTIONS },
  4: { essential: PRINCIPLE_4_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_4_LEADERSHIP_QUESTIONS },
  5: { essential: PRINCIPLE_5_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_5_LEADERSHIP_QUESTIONS },
  6: { essential: PRINCIPLE_6_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_6_LEADERSHIP_QUESTIONS },
  7: { essential: PRINCIPLE_7_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_7_LEADERSHIP_QUESTIONS },
  8: { essential: PRINCIPLE_8_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_8_LEADERSHIP_QUESTIONS },
  9: { essential: PRINCIPLE_9_ESSENTIAL_QUESTIONS, leadership: PRINCIPLE_9_LEADERSHIP_QUESTIONS },
};

// ============================================================================

const hasMeaningfulContent = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return true;
  if (typeof value === "number") return true;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.some((item) => hasMeaningfulContent(item));
  if (typeof value === "object") return Object.values(value).some((v) => hasMeaningfulContent(v));
  return false;
};

// Printable-value checks for PDF export (stricter than generic persistence checks)
const hasPrintableValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "" && value.trim() !== "-";
  if (typeof value === "number") return true;
  if (typeof value === "boolean") return value === true;
  if (Array.isArray(value)) return value.some((v) => hasPrintableValue(v));
  if (typeof value === "object") return Object.values(value).some((v) => hasPrintableValue(v));
  return false;
};

const rowHasPrintableValues = (rowObj) => {
  if (!rowObj || typeof rowObj !== "object") return false;
  return Object.values(rowObj).some((v) => hasPrintableValue(v));
};

const isQuestionDataAnswered = (data) => {
  if (!data) return false;

  // Legacy: Handle arrays (backward compatibility)
  if (Array.isArray(data)) return data.some((v) => (v || "").toString().trim() !== "");

  // Handle plain strings/numbers
  if (typeof data !== "object") return (data || "").toString().trim() !== "";

  // Handle new object structure with answerType - ONLY check the selected answer type
  if (data.answerType) {
    if (data.answerType === "common") {
      // Only check commonValue for common answer type
      return (data.commonValue || "").toString().trim() !== "";
    }
    if (data.answerType === "yesno") {
      // Check yesnoValues array for Yes/No values
      return Array.isArray(data.yesnoValues) && data.yesnoValues.some((v) => v === "Yes" || v === "No");
    }
    if (data.answerType === "perprinciple") {
      // Check perPrincipleValues array for text values
      return (
        Array.isArray(data.perPrincipleValues) &&
        data.perPrincipleValues.some((v) => {
          const val = (v || "").toString().trim();
          return val !== "" && val !== "Yes" && val !== "No";
        })
      );
    }
  }

  // Fallback for objects without answerType (backward compatibility)
  if (Array.isArray(data.values)) return data.values.some((v) => (v || "").toString().trim() !== "");
  return false;
};

const renderAnswerByType = (data, pColumns) => {
  if (!data) return "";

  // Handle object with answerType (new structure)
  if (typeof data === "object" && !Array.isArray(data) && data.answerType) {
    if (data.answerType === "common") {
      // Common answer: return plain text (will be merged across P1-P9 in table)
      return data.commonValue || "-";
    }

    if (data.answerType === "yesno") {
      // Yes/No answers: use yesnoValues array
      const values = Array.isArray(data.yesnoValues) ? data.yesnoValues : pColumns.map(() => "");
      const yesNoOnly = pColumns.map((_, idx) => {
        const v = values[idx];
        return v === "Yes" || v === "No" ? v : "-";
      });
      return { type: "yesno", values: yesNoOnly };
    }

    if (data.answerType === "perprinciple") {
      // Per principle: use perPrincipleValues array
      const values = Array.isArray(data.perPrincipleValues) ? data.perPrincipleValues : pColumns.map(() => "");
      const textOnly = pColumns.map((_, idx) => {
        const v = values[idx];
        return v && v !== "Yes" && v !== "No" ? v : "-";
      });
      return { type: "perprinciple", values: textOnly };
    }
  }

  // Legacy: Handle arrays (backward compatibility)
  if (Array.isArray(data)) {
    const isYesNoArray = data.every((v) => !v || v === "Yes" || v === "No");
    const normalized = pColumns.map((_, idx) => data[idx] || "");
    if (isYesNoArray) {
      const yesNoOnly = normalized.map((v) => (v === "Yes" || v === "No" ? v : "-"));
      return { type: "yesno", values: yesNoOnly };
    }
    const textOnly = normalized.map((v) => (v && v !== "Yes" && v !== "No" ? v : "-"));
    return { type: "perprinciple", values: textOnly };
  }

  // Legacy: Handle plain strings
  if (typeof data !== "object") {
    return data || "-";
  }

  return "-";
};

/**
 * Q10 stores the selected mode on the row (`row1.answerType` / `row2.answerType`). Nested `review` and `frequency`
 * objects may still carry a stale `answerType` (e.g. "perprinciple") after switching the row to common — merge for PDF/HTML.
 */
const mergeQ10FieldWithRowAnswerType = (rowData, fieldName) => {
  if (!rowData || typeof rowData !== "object") return null;
  const fieldData = rowData[fieldName];
  if (!fieldData || typeof fieldData !== "object") return null;
  const mode = rowData.answerType || fieldData.answerType;
  return { ...fieldData, answerType: mode };
};

/** Escape text embedded in HTML so `&`, `<`, etc. do not break DOMParser (fixes PDF garbling like &P&1). */
const escapeHtmlForPdfCell = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Left accent bar on P1–P9 labels (PDF + on-screen Section B tables). */
const PRINCIPLE_LABEL_ACCENT_COLORS = ["#16a34a", "#ef4444", "#2563eb", "#f59e0b", "#a16207", "#06b6d4", "#15803d", "#db2777", "#1e3a8a"];

// Per-principle blocks in PDF: two columns (P# | answer) with clear spacing. Inner table sits inside one outer <td>;
// outer tbody splitting uses :scope > tr only, so these rows are not treated as main disclosure rows.
const renderPerPrincipleTwoColumnHtml = (pColumns, values) => {
  let inner = '<table class="brsr-per-principle-pdf-table" style="width:100%;border-collapse:collapse;margin:2px 0;font-size:10px;table-layout:fixed;"><colgroup><col style="width:1%" /><col /></colgroup><tbody>';
  const last = pColumns.length - 1;
  pColumns.forEach((pCol, idx) => {
    const v = values[idx] ?? "-";
    const accent = PRINCIPLE_LABEL_ACCENT_COLORS[idx] || "#64748b";
    const rowBorder = idx === last ? "border-bottom:1px solid #E5E7EB" : "border-bottom:none";
    inner += "<tr>";
    inner += `<td style="box-sizing:border-box;vertical-align:top;padding:3px 8px 3px 0;${rowBorder};white-space:nowrap;"><span style="display:inline-block;min-width:1.75em;padding:2px 6px;border:1px solid #BBF7D0;border-left:4px solid ${accent};border-radius:4px;background:#DCFCE7;font-weight:700;color:#15803D;text-align:center;line-height:1.2;">${pCol}</span></td>`;
    inner += `<td style="vertical-align:top;padding:3px 8px 3px 10px;${rowBorder};color:#1F2937;word-wrap:break-word;overflow-wrap:break-word;">${escapeHtmlForPdfCell(v)}</td>`;
    inner += "</tr>";
  });
  inner += "</tbody></table>";
  return inner;
};

// Convert Section B Tab 1 data to HTML - Updated for new answerType structure
export const convertSectionBTab1ToHtml = (tab1Data) => {
  if (!tab1Data) return "";

  const pColumns = ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8", "P9"];
  const questions = [
    { key: "q1a", label: "1.a. Whether your entity's policy/policies cover each principle and its core elements of the NGRBCs. (Yes/No)" },
    { key: "q1b", label: "1.b. Has the policy been approved by the Board? (Yes/No)" },
    { key: "q1c", label: "1.c. Web Link of the Policies, if available" },
    { key: "q2", label: "2. Whether the entity has translated the policy into procedures. (Yes/No)" },
    { key: "q3", label: "3. Do the enlisted policies extend to your value chain partners? (Yes/No)" },
    { key: "q4", label: "4. Name of the national and international codes/certifications/labels/standards adopted" },
    { key: "q5", label: "5. Specific commitments, goals and targets set by the entity" },
    { key: "q6", label: "6. Performance of the entity against the specific commitments, goals and targets" },
  ];

  // Build table with Disclosure Questions column and P1-P9 columns (NO Response column)
  let html = "<table><thead><tr>";
  html += "<th>Disclosure Questions</th>";
  pColumns.forEach((pCol) => {
    html += `<th>${pCol}</th>`;
  });
  html += "</tr></thead><tbody>";

  let includedRows = 0;
  questions.forEach((q) => {
    const data = tab1Data[q.key];
    if (!isQuestionDataAnswered(data)) return;

    const answer = renderAnswerByType(data, pColumns);

    // Skip if no valid answer
    if (!answer || answer === "-") return;

    includedRows += 1;

    // Check answer type and render accordingly
    if (typeof answer === "object" && answer.type === "yesno") {
      // Yes/No: show horizontally across P1-P9
      html += "<tr>";
      html += `<td>${q.label}</td>`;
      answer.values.forEach((val) => {
        html += `<td>${val}</td>`;
      });
      html += "</tr>";
    } else if (typeof answer === "object" && answer.type === "perprinciple") {
      html += "<tr>";
      html += `<td>${q.label}</td>`;
      html += `<td colspan="${pColumns.length}">${renderPerPrincipleTwoColumnHtml(pColumns, answer.values)}</td>`;
      html += "</tr>";
    } else {
      // Common: show merged across all P1-P9 columns
      html += "<tr>";
      html += `<td>${q.label}</td>`;
      html += `<td colspan="${pColumns.length}">${answer}</td>`;
      html += "</tr>";
    }
  });

  html += "</tbody></table>";
  return html;
};

// Convert Section B Tab 2 data to HTML
export const convertSectionBTab2ToHtml = (tab2Data) => {
  if (!tab2Data) return "";

  const pColumns = ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8", "P9"];

  let html = "";

  const strTrim = (v) => (typeof v === "string" ? v : v == null ? "" : String(v)).trim();

  // Questions 7-9: Show with Disclosure Questions column and P1-P9 columns (merged across)
  const hasQ7Q8Q9 = strTrim(tab2Data.q7) !== "" || strTrim(tab2Data.q8) !== "" || strTrim(tab2Data.q9) !== "";

  if (hasQ7Q8Q9) {
    html += "<table><thead><tr>";
    html += "<th>Disclosure Questions</th>";
    pColumns.forEach((pCol) => {
      html += `<th>${pCol}</th>`;
    });
    html += "</tr></thead><tbody>";

    // Question 7
    if (strTrim(tab2Data.q7) !== "") {
      html += "<tr>";
      html += "<td>7. Statement by director responsible for the business responsibility report</td>";
      html += `<td colspan="${pColumns.length}">${tab2Data.q7}</td>`;
      html += "</tr>";
    }

    // Question 8
    if (strTrim(tab2Data.q8) !== "") {
      html += "<tr>";
      html += "<td>8. Details of the highest authority responsible for implementation and oversight</td>";
      html += `<td colspan="${pColumns.length}">${tab2Data.q8}</td>`;
      html += "</tr>";
    }

    // Question 9
    if (strTrim(tab2Data.q9) !== "") {
      html += "<tr>";
      html += "<td>9. Does the entity have a specified Committee of the Board responsible for sustainability issues?</td>";
      html += `<td colspan="${pColumns.length}">${tab2Data.q9}</td>`;
      html += "</tr>";
    }

    html += "</tbody></table>";
  }

  // Question 10 - Subject for Review | Committee (P1-P9) | Frequency (P1-P9)
  const hasQ10Data = isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row1, "review")) || isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row1, "frequency")) || isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row2, "review")) || isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row2, "frequency"));

  if (hasQ10Data) {
    const totalCols = pColumns.length * 2 + 1; // Subject column + Committee(P1-P9) + Frequency(P1-P9)
    html += "<table><thead>";
    // Q10 label line above the "Subject for Review" headers
    html += `<tr><th colspan='${totalCols}' style="text-align:left;">10. Details of Review of NGRBCs by the Company:</th></tr>`;
    html += "<tr>";
    html += "<th rowspan='2'>Subject for Review</th>";
    html += `<th colspan='${pColumns.length}'>Committee</th>`;
    html += `<th colspan='${pColumns.length}'>Frequency</th>`;
    html += "</tr><tr>";
    // Committee P1-P9 headers
    pColumns.forEach((pCol) => {
      html += `<th>${pCol}</th>`;
    });
    // Frequency P1-P9 headers
    pColumns.forEach((pCol) => {
      html += `<th>${pCol}</th>`;
    });
    html += "</tr></thead><tbody>";

    // Helper function to render Q10 cells
    const renderQ10Cells = (answer, pColumns) => {
      let cellsHtml = "";
      if (typeof answer === "object" && answer.type === "yesno") {
        // Yes/No: show horizontally
        answer.values.forEach((val) => {
          cellsHtml += `<td>${val}</td>`;
        });
      } else if (typeof answer === "object" && answer.type === "perprinciple") {
        cellsHtml += `<td colspan="${pColumns.length}">${renderPerPrincipleTwoColumnHtml(pColumns, answer.values)}</td>`;
      } else {
        // Common: merge across all P columns
        cellsHtml += `<td colspan="${pColumns.length}">${answer || "-"}</td>`;
      }
      return cellsHtml;
    };

    // Row 1
    const includeQ10Row1 = isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row1, "review")) || isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row1, "frequency"));
    if (includeQ10Row1) {
      html += "<tr>";
      html += "<td>Performance against above policies and follow up action</td>";

      const reviewAnswer = renderAnswerByType(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row1, "review"), pColumns);
      html += renderQ10Cells(reviewAnswer, pColumns);

      const freqAnswer = renderAnswerByType(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row1, "frequency"), pColumns);
      html += renderQ10Cells(freqAnswer, pColumns);

      html += "</tr>";
    }

    // Row 2
    const includeQ10Row2 = isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row2, "review")) || isQuestionDataAnswered(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row2, "frequency"));
    if (includeQ10Row2) {
      html += "<tr>";
      html += "<td>Compliance with statutory requirements</td>";

      const reviewAnswer = renderAnswerByType(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row2, "review"), pColumns);
      html += renderQ10Cells(reviewAnswer, pColumns);

      const freqAnswer = renderAnswerByType(mergeQ10FieldWithRowAnswerType(tab2Data.q10?.row2, "frequency"), pColumns);
      html += renderQ10Cells(freqAnswer, pColumns);

      html += "</tr>";
    }

    html += "</tbody></table>";
  }

  // Question 11: Disclosure Questions | P1-P9
  const includeQ11 = isQuestionDataAnswered(tab2Data.q11);
  if (includeQ11) {
    html += "<table><thead><tr>";
    html += "<th>Disclosure Questions</th>";
    pColumns.forEach((pCol) => {
      html += `<th>${pCol}</th>`;
    });
    html += "</tr></thead><tbody>";

    const answer = renderAnswerByType(tab2Data.q11, pColumns);
    html += "<tr>";
    html += "<td>11. Has the entity carried out independent assessment/evaluation?</td>";

    if (typeof answer === "object" && answer.type === "yesno") {
      // Yes/No: show horizontally
      answer.values.forEach((val) => {
        html += `<td>${val}</td>`;
      });
    } else if (typeof answer === "object" && answer.type === "perprinciple") {
      html += `<td colspan="${pColumns.length}">${renderPerPrincipleTwoColumnHtml(pColumns, answer.values)}</td>`;
    } else {
      // Common: merge across all P columns
      html += `<td colspan="${pColumns.length}">${answer}</td>`;
    }

    html += "</tr>";
    html += "</tbody></table>";
  }

  // Question 12: Disclosure Questions | P1-P9
  const q12Rows = [
    { key: "row1", label: "The entity does not consider the Principles material to its business" },
    { key: "row2", label: "The entity is not at a stage where it is in a position to formulate and implement the policies" },
    { key: "row3", label: "The entity does not have the financial or/human and technical resources available" },
    { key: "row4", label: "It is planned to be done in the next financial year" },
    { key: "row5", label: "Any other reason (please specify)" },
  ];

  const hasQ12Data = q12Rows.some((row) => isQuestionDataAnswered(tab2Data.q12?.[row.key]));

  if (hasQ12Data) {
    html += "<table><thead><tr>";
    html += '<th>12. If answer to Q1 is "No" - reasons:</th>';
    pColumns.forEach((pCol) => {
      html += `<th>${pCol}</th>`;
    });
    html += "</tr></thead><tbody>";

    q12Rows.forEach((row) => {
      const data = tab2Data.q12?.[row.key];
      if (!isQuestionDataAnswered(data)) return;

      const answer = renderAnswerByType(data, pColumns);
      html += "<tr>";
      html += `<td>${row.label}</td>`;

      if (typeof answer === "object" && answer.type === "yesno") {
        // Yes/No: show horizontally
        answer.values.forEach((val) => {
          html += `<td>${val}</td>`;
        });
      } else if (typeof answer === "object" && answer.type === "perprinciple") {
        html += `<td colspan="${pColumns.length}">${renderPerPrincipleTwoColumnHtml(pColumns, answer.values)}</td>`;
      } else {
        // Common: merge across all P columns
        html += `<td colspan="${pColumns.length}">${answer}</td>`;
      }

      html += "</tr>";
    });

    html += "</tbody></table>";
  }

  return html;
};

/** Extra space after the leading number (e.g. 1.a. / 2.) so PDF question text aligns cleanly. */
const formatSectionBQuestionLabelPlain = (label) => {
  const s = String(label);
  const m = s.match(/^(\d+(?:\.\d+)?[a-z]?\.\s*)([\s\S]*)$/i);
  if (!m) return s;
  const num = m[1].replace(/\s+$/, "");
  const rest = m[2].replace(/^\s+/, "");
  return `${num}\u2003\u2003\u2003\u2003${rest}`;
};

/** Always returns a renderable answer for PDF rows (never skips the row). */
const ensurePdfAnswer = (data, pColumns) => {
  const a = renderAnswerByType(data, pColumns);
  if (a === "" || a == null) return "-";
  return a;
};

/** PDF-only: P1–P9 labels in first column, answers in second (merged P1–P9 area). */
const renderSectionBPdfPerPrincipleBlock = (pColumns, values) => {
  let inner = '<table class="brsr-per-principle-pdf-table" style="width:100%;border-collapse:collapse;font-size:9px;table-layout:fixed;"><colgroup><col style="width:1%" /><col /></colgroup><tbody>';
  const last = pColumns.length - 1;
  pColumns.forEach((pCol, idx) => {
    const v = values[idx] ?? "-";
    const accent = PRINCIPLE_LABEL_ACCENT_COLORS[idx] || "#64748b";
    const rowBorder = idx === last ? "border-bottom:1px solid #E5E7EB" : "border-bottom:none";
    inner += "<tr>";
    inner += `<td style="box-sizing:border-box;vertical-align:top;padding:3px 8px 3px 0;${rowBorder};white-space:nowrap;"><span style="display:inline-block;min-width:1.75em;padding:2px 6px;border:1px solid #BBF7D0;border-left:4px solid ${accent};border-radius:4px;background:#DCFCE7;font-weight:700;color:#15803D;text-align:center;line-height:1.2;">${pCol}</span></td>`;
    inner += `<td style="padding:3px 8px 3px 10px;${rowBorder};vertical-align:top;color:#1f2937;word-wrap:break-word;overflow-wrap:break-word;">${escapeHtmlForPdfCell(v)}</td>`;
    inner += "</tr>";
  });
  inner += "</tbody></table>";
  return inner;
};

/** One answer block for Q10 Committee or Frequency (9 cells or one merged cell). */
const renderSectionBQ10PdfAnswerBlock = (answer, pColumns, tdStyle) => {
  if (typeof answer === "object" && answer.type === "yesno") {
    let s = "";
    answer.values.forEach((val) => {
      s += `<td style="${tdStyle};text-align:center;">${escapeHtmlForPdfCell(val)}</td>`;
    });
    return s;
  }
  if (typeof answer === "object" && answer.type === "perprinciple") {
    return `<td colspan="9" style="${tdStyle}">${renderSectionBPdfPerPrincipleBlock(pColumns, answer.values)}</td>`;
  }
  return `<td colspan="9" style="${tdStyle}">${escapeHtmlForPdfCell(answer == null || answer === "" ? "-" : answer)}</td>`;
};

/**
 * Section B PDF: wrapper with (1) main 10-column table, (2) Q10 as its own 19-column table for correct jsPDF layout,
 * (3) tail 10-column table for Q11–Q12. All disclosure rows print even when empty.
 */
export const convertSectionBUnifiedTableHtml = (tab1Data, tab2Data) => {
  const t1 = tab1Data || {};
  const t2 = tab2Data || {};
  const pColumns = ["P1", "P2", "P3", "P4", "P5", "P6", "P7", "P8", "P9"];
  const tdQ = "padding:8px 12px;border-bottom:1px solid #E5E7EB;vertical-align:top;font-size:9px;color:#1f2937;";
  /** First column disclosure questions only (Section B PDF + preview) — heavier weight than answer cells. */
  const tdQFirst = `${tdQ}font-weight:600;`;
  const tdP = "padding:8px 6px;border-bottom:1px solid #E5E7EB;vertical-align:middle;font-size:9px;text-align:center;";
  const thBase = "padding:8px 6px;border-bottom:2px solid #D6E0DD;background:#f0f7f7;font-weight:600;font-size:9px;text-align:center;color:#2d3f50;";
  const thQ = `${thBase}text-align:left;min-width:220px;`;

  const thead10 = `<thead><tr><th style="${thQ}">Disclosure Questions</th>${pColumns.map((p) => `<th colspan="2" style="${thBase}">${p}</th>`).join("")}</tr></thead>`;
  const sectionRow = (title) => `<tr><td colspan="19" style="background:#f0f7f7;font-weight:700;padding:10px 12px;border-bottom:1px solid #D6E0DD;color:#2d3f50;font-size:10px;">${title}</td></tr>`;

  const appendAnswerRow = (htmlRef, labelPlain, answer) => {
    const label = formatSectionBQuestionLabelPlain(labelPlain);
    const a = answer === "" || answer == null ? "-" : answer;
    if (typeof a === "object" && a.type === "yesno") {
      htmlRef.html += `<tr><td style="${tdQFirst}">${label}</td>`;
      a.values.forEach((val) => {
        htmlRef.html += `<td colspan="2" style="${tdP}">${escapeHtmlForPdfCell(val)}</td>`;
      });
      htmlRef.html += `</tr>`;
      return;
    }
    if (typeof a === "object" && a.type === "perprinciple") {
      htmlRef.html += `<tr><td style="${tdQFirst}">${label}</td><td colspan="18" style="${tdQ}">${renderSectionBPdfPerPrincipleBlock(pColumns, a.values)}</td></tr>`;
      return;
    }
    htmlRef.html += `<tr><td style="${tdQFirst}">${label}</td><td colspan="18" style="${tdQ}">${escapeHtmlForPdfCell(a)}</td></tr>`;
  };

  const strTrim = (v) => (typeof v === "string" ? v : v == null ? "" : String(v)).trim();

  const innerTh = "padding:6px 4px;border-bottom:1px solid #D6E0DD;background:#eff6f5;font-size:8px;text-align:center;font-weight:600;color:#374151;";
  const innerTd = "padding:6px 4px;border-bottom:1px solid #E5E7EB;font-size:9px;vertical-align:top;";
  /** Match main “Disclosure Questions” column width in PDF (same as thQ min-width). */
  const q10SubjectCol = `${innerTh};text-align:left;vertical-align:middle;min-width:220px;`;

  const buildQ10StandaloneTable = () => {
    let qt = `<table style="width:100%;border-collapse:collapse;font-size:9px;">`;
    qt += `<thead>`;
    qt += `<tr><th colspan="19" style="${innerTh};text-align:left;font-size:10px;color:#2d3f50;padding:10px 12px;border-bottom:1px solid #E5E7EB;">${formatSectionBQuestionLabelPlain("10. Details of Review of NGRBCs by the Company:")}</th></tr>`;
    qt += `<tr><th rowspan="2" style="${q10SubjectCol}">Subject for Review</th>`;
    qt += `<th colspan="9" style="${innerTh}">Indicate whether review was undertaken by Director / Committee of the Board / Any other Committee</th>`;
    qt += `<th colspan="9" style="${innerTh}">Frequency (Annually / Half yearly / Quarterly / Any other – please specify)</th></tr><tr>`;
    pColumns.forEach((pCol) => {
      qt += `<th style="${innerTh}">${pCol}</th>`;
    });
    pColumns.forEach((pCol) => {
      qt += `<th style="${innerTh}">${pCol}</th>`;
    });
    qt += `</tr></thead><tbody>`;

    const q10Subjects = [
      {
        subject: "Performance against above policies and follow up action",
        rowKey: "row1",
      },
      {
        subject: "Compliance with statutory requirements of relevance to the principles, and, rectification of any non-compliances",
        rowKey: "row2",
      },
    ];

    q10Subjects.forEach(({ subject, rowKey }) => {
      const rowData = t2.q10?.[rowKey];
      const rev = ensurePdfAnswer(mergeQ10FieldWithRowAnswerType(rowData, "review"), pColumns);
      const fr = ensurePdfAnswer(mergeQ10FieldWithRowAnswerType(rowData, "frequency"), pColumns);
      qt += "<tr>";
      qt += `<td style="${innerTd}">${subject}</td>`;
      qt += renderSectionBQ10PdfAnswerBlock(rev, pColumns, innerTd);
      qt += renderSectionBQ10PdfAnswerBlock(fr, pColumns, innerTd);
      qt += "</tr>";
    });

    qt += "</tbody></table>";
    return qt;
  };

  const main = { html: `<table style="width:100%;border-collapse:collapse;font-size:9px;">${thead10}<tbody>` };

  main.html += sectionRow("Policy and management processes");
  const tab1Questions = [
    { key: "q1a", label: "1.a. Whether your entity's policy/policies cover each principle and its core elements of the NGRBCs. (Yes/No)" },
    { key: "q1b", label: "1.b. Has the policy been approved by the Board? (Yes/No)" },
    { key: "q1c", label: "1.c. Web Link of the Policies, if available" },
    { key: "q2", label: "2. Whether the entity has translated the policy into procedures. (Yes/No)" },
    { key: "q3", label: "3. Do the enlisted policies extend to your value chain partners? (Yes/No)" },
    { key: "q4", label: "4. Name of the national and international codes/certifications/labels/standards adopted" },
    { key: "q5", label: "5. Specific commitments, goals and targets set by the entity" },
    { key: "q6", label: "6. Performance of the entity against the specific commitments, goals and targets" },
  ];
  tab1Questions.forEach((q) => {
    appendAnswerRow(main, q.label, ensurePdfAnswer(t1[q.key], pColumns));
  });

  main.html += sectionRow("Governance, leadership and oversight");

  const q7Ans = strTrim(t2.q7) !== "" ? t2.q7 : "-";
  main.html += `<tr><td style="${tdQFirst}">${formatSectionBQuestionLabelPlain("7. Statement by director responsible for the business responsibility report")}</td><td colspan="18" style="${tdQ}">${escapeHtmlForPdfCell(q7Ans)}</td></tr>`;

  const q8Ans = strTrim(t2.q8) !== "" ? t2.q8 : "-";
  main.html += `<tr><td style="${tdQFirst}">${formatSectionBQuestionLabelPlain("8. Details of the highest authority responsible for implementation and oversight")}</td><td colspan="18" style="${tdQ}">${escapeHtmlForPdfCell(q8Ans)}</td></tr>`;

  const q9Ans = strTrim(t2.q9) !== "" ? t2.q9 : "-";
  main.html += `<tr><td style="${tdQFirst}">${formatSectionBQuestionLabelPlain("9. Does the entity have a specified Committee of the Board responsible for sustainability issues?")}</td><td colspan="18" style="${tdQ}">${escapeHtmlForPdfCell(q9Ans)}</td></tr>`;

  // Q10 — inline inside the same table, with its own 3-row sub-header then data rows (class marks thead-like rows for PDF + CSS)
  main.html += `<tr class="brsr-sb-q10-header-row"><td colspan="19" style="${innerTh};text-align:left;font-size:10px;color:#2d3f50;padding:10px 12px;border-bottom:1px solid #E5E7EB;">${formatSectionBQuestionLabelPlain("10. Details of Review of NGRBCs by the Company:")}</td></tr>`;
  main.html += `<tr class="brsr-sb-q10-header-row">`;
  main.html += `<th style="${q10SubjectCol}">Subject for Review</th>`;
  main.html += `<th colspan="9" style="${innerTh}">Indicate whether review was undertaken by Director / Committee of the Board / Any other Committee</th>`;
  main.html += `<th colspan="9" style="${innerTh}">Frequency (Annually / Half yearly / Quarterly / Any other \u2013 please specify)</th>`;
  main.html += `</tr>`;
  main.html += `<tr class="brsr-sb-q10-header-row">`;
  main.html += `<th style="${q10SubjectCol}"></th>`;
  pColumns.forEach((pCol) => {
    main.html += `<th style="${innerTh}">${pCol}</th>`;
  });
  pColumns.forEach((pCol) => {
    main.html += `<th style="${innerTh}">${pCol}</th>`;
  });
  main.html += `</tr>`;

  const q10Subjects = [
    { subject: "Performance against above policies and follow up action", rowKey: "row1" },
    { subject: "Compliance with statutory requirements of relevance to the principles, and, rectification of any non-compliances", rowKey: "row2" },
  ];
  q10Subjects.forEach(({ subject, rowKey }) => {
    const rowData = t2.q10?.[rowKey];
    const rev = ensurePdfAnswer(mergeQ10FieldWithRowAnswerType(rowData, "review"), pColumns);
    const fr = ensurePdfAnswer(mergeQ10FieldWithRowAnswerType(rowData, "frequency"), pColumns);
    main.html += `<tr><td style="${innerTd}">${subject}</td>`;
    main.html += renderSectionBQ10PdfAnswerBlock(rev, pColumns, innerTd);
    main.html += renderSectionBQ10PdfAnswerBlock(fr, pColumns, innerTd);
    main.html += `</tr>`;
  });

  main.html += "</tbody></table>";

  const thead10tail = `<thead><tr><th style="${thQ}">Disclosure Questions</th>${pColumns.map((p) => `<th colspan="2" style="${thBase}">${p}</th>`).join("")}</tr></thead>`;

  const tail = { html: `<table style="width:100%;border-collapse:collapse;font-size:9px;">${thead10tail}<tbody>` };

  appendAnswerRow(tail, "11. Has the entity carried out independent assessment/evaluation?", ensurePdfAnswer(t2.q11, pColumns));

  tail.html += `<tr class="brsr-sb-q10-header-row"><td colspan="19" style="padding:10px 12px;border-bottom:1px solid #E5E7EB;font-size:10px;color:#2d3f50;background:#eff6f5;font-weight:600;">${formatSectionBQuestionLabelPlain('12. If answer to question (1) above is "No" i.e. not all Principles are covered by a policy, reasons to be stated:')}</td></tr>`;
  tail.html += `<tr><th style="${thQ}">Questions</th>`;
  pColumns.forEach((p) => {
    tail.html += `<th colspan="2" style="${thBase}">${p}</th>`;
  });
  tail.html += `</tr>`;

  const q12Rows = [
    { key: "row1", label: "The entity does not consider the Principles material to its business" },
    { key: "row2", label: "The entity is not at a stage where it is in a position to formulate and implement the policies" },
    { key: "row3", label: "The entity does not have the financial or/human and technical resources available" },
    { key: "row4", label: "It is planned to be done in the next financial year" },
    { key: "row5", label: "Any other reason (please specify)" },
  ];
  q12Rows.forEach((row) => {
    appendAnswerRow(tail, row.label, ensurePdfAnswer(t2.q12?.[row.key], pColumns));
  });

  tail.html += "</tbody></table>";

  return `<div class="brsr-section-b-pdf-wrap">${main.html}${tail.html}</div>`;
};

// Update step4 with Section B data for PDF generation
export const updateStep4WithSectionB = (reportId, sectionData) => {
  try {
    const existingStep4 = JSON.parse(localStorage.getItem("step4") || "[]");

    // Remove old Section B entries
    const filtered = Array.isArray(existingStep4) ? existingStep4.filter((item) => item && item.module !== "Section B") : [];

    const newEntries = [];

    if (sectionData.tab1Data || sectionData.tab2Data) {
      const combinedHtml = convertSectionBUnifiedTableHtml(sectionData.tab1Data ?? {}, sectionData.tab2Data ?? {});

      if (combinedHtml.trim() !== "") {
        newEntries.push({
          id: "SectionB-Combined",
          module: "Section B",
          title: "Disclosure Questions",
          question: "",
          answer: combinedHtml,
        });
      }
    }

    // Save updated step4
    localStorage.setItem("step4", JSON.stringify([...filtered, ...newEntries]));

    return newEntries;
  } catch (e) {
    console.error("Failed to update step4 with Section B:", e);
    return [];
  }
};

// Convert Principle 1 Essential data to HTML
export const convertPrinciple1EssentialToHtml = (data, financialYears = {}) => {
  if (!data) return "";
  const fyH = buildFyHeaderLabels(financialYears);
  let html = "";
  html += "<h3 style='font-size:13px;font-weight:500;color:#2D3F50;margin:12px 0 8px 0;'>Essential Indicators</h3>";

  // Q1: Training and awareness programmes
  if (data.q1_training && data.q1_training.length > 0) {
    const rows = data.q1_training.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>1. Percentage coverage by training and awareness programmes on any of the Principles during the financial year:</p>";
      html += "<table><thead><tr>";
      html += "<th>Segment</th>";
      html += "<th>Total Number of training and awareness programmes held</th>";
      html += "<th>Topics / principles covered under the training and its impact</th>";
      html += "<th>%age of persons in respective category covered by the awareness programmes</th>";
      html += "</tr></thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.segment || "-"}</td>`;
        html += `<td>${row.programmes || "-"}</td>`;
        html += `<td>${row.topics || "-"}</td>`;
        html += `<td>${hasPrintableValue(row.percentage) ? `${row.percentage}%` : "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  // Q2: Fines/Penalties
  if (data.q2_fines && hasPrintableValue(data.q2_fines)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>2. Details of fines / penalties /  punishment/ award/ compounding fees/ settlement amount paid in proceedings (by the entity or by directors / KMPs) with regulators/ law enforcement agencies/ judicial institutions, in the financial year:</p>";

    // Combined table with Monetary and Non-Monetary sections
    html += "<table><tbody>";

    // Monetary Section
    html += "<tr><td colspan='6' style='background-color:#f5f5f5;font-weight:600;padding:12px 16px;font-size:15px;'>Monetary</td></tr>";
    html += "<tr>";
    html += "<th>Type</th>";
    html += "<th>NGRBC Principle</th>";
    html += "<th>Name of the regulatory/enforcement agencies/judicial institutions</th>";
    html += "<th>Amount (INR)</th>";
    html += "<th>Brief of the Case</th>";
    html += "<th>Has an appeal been preferred? (Yes/No)</th>";
    html += "</tr>";

    if (data.q2_fines.monetary) {
      const monetaryRows = data.q2_fines.monetary.filter((row) => rowHasPrintableValues(row));
      if (monetaryRows.length > 0) {
        monetaryRows.forEach((row) => {
          html += "<tr>";
          html += `<td>${row.type || "-"}</td>`;
          html += `<td>${row.principle || "-"}</td>`;
          html += `<td>${row.agency || "-"}</td>`;
          html += `<td>${row.amount || "-"}</td>`;
          html += `<td>${row.brief || "-"}</td>`;
          html += `<td>${row.appeal || "-"}</td>`;
          html += "</tr>";
        });
      } else {
        // Empty row for monetary if no data
        html += "<tr><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td></tr>";
      }
    }

    // Non-Monetary Section
    html += "<tr><td colspan='6' style='background-color:#f5f5f5;font-weight:600;padding:12px 16px;font-size:15px;'>Non-Monetary</td></tr>";
    html += "<tr>";
    html += "<th>Type</th>";
    html += "<th>NGRBC Principle</th>";
    html += "<th>Name of the regulatory/enforcement agencies/judicial institutions</th>";
    html += "<th>Amount (INR)</th>";
    html += "<th>Brief of the Case</th>";
    html += "<th>Has an appeal been preferred? (Yes/No)</th>";
    html += "</tr>";

    if (data.q2_fines.nonMonetary) {
      const nonMonetaryRows = data.q2_fines.nonMonetary.filter((row) => rowHasPrintableValues(row));
      if (nonMonetaryRows.length > 0) {
        nonMonetaryRows.forEach((row) => {
          html += "<tr>";
          html += `<td>${row.type || "-"}</td>`;
          html += `<td>${row.principle || "-"}</td>`;
          html += `<td>${row.agency || "-"}</td>`;
          html += `<td>-</td>`; // Non-monetary doesn't have amount
          html += `<td>${row.brief || "-"}</td>`;
          html += `<td>${row.appeal || "-"}</td>`;
          html += "</tr>";
        });
      } else {
        // Empty row for non-monetary if no data
        html += "<tr><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td></tr>";
      }
    }

    html += "</tbody></table>";
  }

  // Q3: Appeals
  if (data.q3_appeals && data.q3_appeals.length > 0) {
    const rows = data.q3_appeals.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>3. Of the instances disclosed in Question 2 above, details of the Appeal/ Revision preferred in cases where monetary or non-monetary action has been appealed:</p>";
      html += "<table><thead><tr>";
      html += "<th>Case Details</th>";
      html += "<th>Name of the regulatory/enforcement agencies/judicial institutions</th>";
      html += "</tr></thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.caseDetails || "-"}</td>`;
        html += `<td>${row.agency || "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  // Q4: Anti-corruption policy
  if (hasPrintableValue(data.q4_antiCorruption)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>4. Does the entity have an anti-corruption or anti-bribery policy? If yes, provide details in brief and if available, provide a web-link to the policy:</p>";
    html += "<table><tbody><tr>";
    html += "<td><strong>Policy Details:</strong></td>";
    html += `<td>${data.q4_antiCorruption}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q5: Disciplinary action
  if (data.q5_disciplinary && hasPrintableValue(data.q5_disciplinary)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>5. Number of Directors/KMPs/employees/workers against whom disciplinary action was taken by any law enforcement agency for the charges of bribery/ corruption:</p>";
    html += "<table><thead><tr>";
    html += `<th>Category</th><th>${fyH.colCurrent}</th><th>${fyH.colPrevious}</th>`;
    html += "</tr></thead><tbody>";
    const categories = [
      { key: "directors", label: "Directors" },
      { key: "kmps", label: "KMPs" },
      { key: "employees", label: "Employees" },
      { key: "workers", label: "Workers" },
    ];
    categories.forEach((cat) => {
      if (data.q5_disciplinary[cat.key] && hasPrintableValue(data.q5_disciplinary[cat.key])) {
        html += "<tr>";
        html += `<td>${cat.label}</td>`;
        html += `<td>${data.q5_disciplinary[cat.key].current || "-"}</td>`;
        html += `<td>${data.q5_disciplinary[cat.key].previous || "-"}</td>`;
        html += "</tr>";
      }
    });
    html += "</tbody></table>";
  }

  // Q6: Conflict of interest
  if (data.q6_conflict && hasPrintableValue(data.q6_conflict)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>6. Details of complaints with regard to conflict of interest:</p>";
    html += "<table><thead><tr>";
    html += '<th rowspan="2">Category</th>';
    html += `<th colspan="2">${fyH.colCurrent}</th>`;
    html += `<th colspan="2">${fyH.colPrevious}</th>`;
    html += "</tr><tr>";
    html += "<th>Number</th><th>Remarks</th><th>Number</th><th>Remarks</th>";
    html += "</tr></thead><tbody>";
    if (data.q6_conflict.directors && hasPrintableValue(data.q6_conflict.directors)) {
      html += "<tr>";
      html += "<td>Directors</td>";
      html += `<td>${data.q6_conflict.directors.currentNumber || "-"}</td>`;
      html += `<td>${data.q6_conflict.directors.currentRemarks || "-"}</td>`;
      html += `<td>${data.q6_conflict.directors.previousNumber || "-"}</td>`;
      html += `<td>${data.q6_conflict.directors.previousRemarks || "-"}</td>`;
      html += "</tr>";
    }
    if (data.q6_conflict.kmps && hasPrintableValue(data.q6_conflict.kmps)) {
      html += "<tr>";
      html += "<td>KMPs</td>";
      html += `<td>${data.q6_conflict.kmps.currentNumber || "-"}</td>`;
      html += `<td>${data.q6_conflict.kmps.currentRemarks || "-"}</td>`;
      html += `<td>${data.q6_conflict.kmps.previousNumber || "-"}</td>`;
      html += `<td>${data.q6_conflict.kmps.previousRemarks || "-"}</td>`;
      html += "</tr>";
    }
    html += "</tbody></table>";
  }

  // Q7: Corrective action
  if (hasPrintableValue(data.q7_corrective)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>7. Provide details of any corrective action taken or underway on issues related to fines / penalties / action taken by regulators/ law enforcement agencies/ judicial institutions, on cases of corruption and conflicts of interest:</p>";
    html += "<table><tbody><tr>";
    html += "<td><strong>Corrective Action:</strong></td>";
    html += `<td>${data.q7_corrective}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q8: Accounts payable
  if (data.q8_accountsPayable && hasPrintableValue(data.q8_accountsPayable)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>8. Number of days of accounts payables ((Accounts payable *365) / Cost of goods/services procured) in the following format:</p>";
    html += "<table><thead><tr>";
    html += `<th></th><th>${fyH.colCurrent}</th><th>${fyH.colPrevious}</th>`;
    html += "</tr></thead><tbody><tr>";
    html += "<td>Number of days of accounts payable</td>";
    html += `<td>${data.q8_accountsPayable.current || "-"}</td>`;
    html += `<td>${data.q8_accountsPayable.previous || "-"}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q9: Openness
  if (data.q9_openness && hasPrintableValue(data.q9_openness)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>9. Open-ness of business:</p>";
    html += "<table><thead><tr>";
    html += `<th>Metric</th><th>${fyH.colCurrent}</th><th>${fyH.colPrevious}</th>`;
    html += "</tr></thead><tbody>";

    if (data.q9_openness.concentrationPurchases && hasPrintableValue(data.q9_openness.concentrationPurchases)) {
      html += '<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;padding:8px;">Concentration of Purchases</td></tr>';
      html += `<tr><td>Purchases from trading houses as % of total purchases</td><td>${data.q9_openness.concentrationPurchases.a?.current || "-"}</td><td>${data.q9_openness.concentrationPurchases.a?.previous || "-"}</td></tr>`;
      html += `<tr><td>Number of trading houses</td><td>${data.q9_openness.concentrationPurchases.b?.current || "-"}</td><td>${data.q9_openness.concentrationPurchases.b?.previous || "-"}</td></tr>`;
      html += `<tr><td>Purchases from top 10 trading houses as %</td><td>${data.q9_openness.concentrationPurchases.c?.current || "-"}</td><td>${data.q9_openness.concentrationPurchases.c?.previous || "-"}</td></tr>`;
    }
    if (data.q9_openness.concentrationSales && hasPrintableValue(data.q9_openness.concentrationSales)) {
      html += '<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;padding:8px;">Concentration of Sales</td></tr>';
      html += `<tr><td>Sales to dealers/distributors as % of total sales</td><td>${data.q9_openness.concentrationSales.a?.current || "-"}</td><td>${data.q9_openness.concentrationSales.a?.previous || "-"}</td></tr>`;
      html += `<tr><td>Number of dealers/distributors</td><td>${data.q9_openness.concentrationSales.b?.current || "-"}</td><td>${data.q9_openness.concentrationSales.b?.previous || "-"}</td></tr>`;
      html += `<tr><td>Sales to top 10 dealers/distributors as %</td><td>${data.q9_openness.concentrationSales.c?.current || "-"}</td><td>${data.q9_openness.concentrationSales.c?.previous || "-"}</td></tr>`;
    }
    if (data.q9_openness.shareRPTs && hasPrintableValue(data.q9_openness.shareRPTs)) {
      html += '<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;padding:8px;">Share of RPTs in</td></tr>';
      html += `<tr><td>Purchases</td><td>${data.q9_openness.shareRPTs.a?.current || "-"}</td><td>${data.q9_openness.shareRPTs.a?.previous || "-"}</td></tr>`;
      html += `<tr><td>Sales</td><td>${data.q9_openness.shareRPTs.b?.current || "-"}</td><td>${data.q9_openness.shareRPTs.b?.previous || "-"}</td></tr>`;
      html += `<tr><td>Loans & advances</td><td>${data.q9_openness.shareRPTs.c?.current || "-"}</td><td>${data.q9_openness.shareRPTs.c?.previous || "-"}</td></tr>`;
      html += `<tr><td>Investments</td><td>${data.q9_openness.shareRPTs.d?.current || "-"}</td><td>${data.q9_openness.shareRPTs.d?.previous || "-"}</td></tr>`;
    }
    html += "</tbody></table>";
  }

  return html || "<p>No data available</p>";
};

// Convert Principle 1 Leadership data to HTML
export const convertPrinciple1LeadershipToHtml = (data) => {
  if (!data) return "";
  let html = "";
  html += "<h3 style='font-size:13px;font-weight:500;color:#2D3F50;margin:12px 0 8px 0;'>Leadership Indicators</h3>";

  // Q1: Value chain awareness programmes
  if (data.q1_valueChainAwareness && data.q1_valueChainAwareness.length > 0) {
    const rows = data.q1_valueChainAwareness.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>1. Awareness programmes conducted for value chain partners on any of the Principles during the financial year:</p>";
      html += "<table><thead><tr>";
      html += "<th>Total number of awareness programmes held</th>";
      html += "<th>Topics / principles covered under the training</th>";
      html += "<th>%age of value chain partners covered by the awareness programmes</th>";
      html += "</tr></thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.programmes || "-"}</td>`;
        html += `<td>${row.topics || "-"}</td>`;
        html += `<td>${hasPrintableValue(row.percentage) ? `${row.percentage}%` : "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  // Q2: Conflict of interest processes
  if (data.q2_conflictProcesses && hasPrintableValue(data.q2_conflictProcesses)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>2. Does the entity have processes in place to avoid/ manage conflict of interests involving members of the Board? (Yes/No) If Yes, provide details:</p>";
    html += "<table><tbody>";
    html += "<tr><td><strong>Has processes in place:</strong></td>";
    html += `<td>${data.q2_conflictProcesses.hasProcesses || "-"}</td></tr>`;
    html += "<tr><td><strong>Details:</strong></td>";
    html += `<td>${data.q2_conflictProcesses.details || "-"}</td></tr>`;
    html += "</tbody></table>";
  }

  return html || "<p>No data available</p>";
};

// Convert Principle 7 Essential data to HTML (no section <h3>; caller adds "Essential Indicators" once)
export const convertPrinciple7EssentialToHtml = (data) => {
  if (!data) return "";
  let html = "";

  // Q1a: Number of affiliations
  if (hasPrintableValue(data.q1a_affiliations)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>1a. Number of affiliations with trade and industry chambers/ associations:</p>";
    html += "<table><tbody><tr>";
    html += `<td>${data.q1a_affiliations}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q1b: Trade chambers list with S.No.
  if (data.q1b_tradeChambers && data.q1b_tradeChambers.length > 0) {
    const rows = data.q1b_tradeChambers.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>1b. List the top 10 trade and industry chambers/ associations (determined based on the total members of such body) the entity is a member of/ affiliated to:</p>";
      html += "<table><thead><tr>";
      html += "<th style='width:80px;'>S. No.</th>";
      html += "<th>Name of the trade and industry chambers/associations</th>";
      html += "<th>Reach of trade and industry chambers/ associations (State/National)</th>";
      html += "</tr></thead><tbody>";
      rows.forEach((row, idx) => {
        html += "<tr>";
        html += `<td>${idx + 1}</td>`;
        html += `<td>${row.name || "-"}</td>`;
        html += `<td>${row.reach || "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  // Q2: Anti-competitive conduct
  if (data.q2_antiCompetitive && data.q2_antiCompetitive.length > 0) {
    const rows = data.q2_antiCompetitive.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>2. Provide details of corrective action taken or underway on any issues related to anti-competitive conduct by the entity, based on adverse orders from regulatory authorities:</p>";
      html += "<table><thead><tr>";
      html += "<th>Name of authority</th>";
      html += "<th>Brief of the case</th>";
      html += "<th>Corrective action taken</th>";
      html += "</tr></thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.authority || "-"}</td>`;
        html += `<td>${row.brief || "-"}</td>`;
        html += `<td>${row.action || "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  return html || "<p>No data available</p>";
};

// Convert Principle 9 Essential data to HTML (no section <h3>; caller adds it once)
export const convertPrinciple9EssentialToHtml = (data, financialYears = {}) => {
  if (!data) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "";

  // Q1: Consumer complaints mechanisms
  if (hasPrintableValue(data.q1_complaintsMechanism)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>1. Describe the mechanisms in place to receive and respond to consumer complaints and feedback:</p>";
    html += "<table><tbody><tr>";
    html += `<td>${data.q1_complaintsMechanism}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q2: Turnover of products/services
  if (data.q2_productTurnover && data.q2_productTurnover.length > 0) {
    const rows = data.q2_productTurnover.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>2. Turnover of products and/ services as a percentage of turnover from all products/service that carry information about:</p>";
      html += "<table><thead><tr>";
      html += "<th>As a percentage to total turnover</th>";
      html += "<th>Percentage (%)</th>";
      html += "</tr></thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.description || "-"}</td>`;
        html += `<td>${hasPrintableValue(row.percentage) ? `${row.percentage}%` : "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  // Q3: Consumer complaints
  if (data.q3_complaints && data.q3_complaints.length > 0) {
    const rows = data.q3_complaints.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>3. Number of consumer complaints in respect of the following:</p>";
      html += "<table><thead>";
      html += "<tr>";
      html += "<th rowspan='2' style='vertical-align:middle;'></th>";
      html += `<th colspan='3' style='text-align:center;'>${L.colCurrentLong}</th>`;
      html += `<th colspan='3' style='text-align:center;'>${L.colPreviousLong}</th>`;
      html += "</tr>";
      html += "<tr>";
      html += "<th>Received during the year</th>";
      html += "<th>Pending resolution at end of year</th>";
      html += "<th>Remarks</th>";
      html += "<th>Received during the year</th>";
      html += "<th>Pending resolution at end of year</th>";
      html += "<th>Remarks</th>";
      html += "</tr>";
      html += "</thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.category || "-"}</td>`;
        html += `<td>${row.currentReceived || "-"}</td>`;
        html += `<td>${row.currentPending || "-"}</td>`;
        html += `<td>${row.currentRemarks || "-"}</td>`;
        html += `<td>${row.previousReceived || "-"}</td>`;
        html += `<td>${row.previousPending || "-"}</td>`;
        html += `<td>${row.previousRemarks || "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  // Q4: Product recalls
  if (data.q4_productRecalls && data.q4_productRecalls.length > 0) {
    const rows = data.q4_productRecalls.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>4. Details of instances of product recalls on account of safety issues:</p>";
      html += "<table><thead><tr>";
      html += "<th>Type</th>";
      html += "<th>Number</th>";
      html += "<th>Reasons for recall</th>";
      html += "</tr></thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.type || "-"}</td>`;
        html += `<td>${row.number || "-"}</td>`;
        html += `<td>${row.reasons || "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  // Q5: Cyber security policy
  if (hasPrintableValue(data.q5_cyberSecurity)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>5. Does the entity have a framework/ policy on cyber security and risks related to data privacy? (Yes/No) If available, provide a web-link of the policy:</p>";
    html += "<table><tbody><tr>";
    html += `<td>${data.q5_cyberSecurity}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q6: Corrective actions
  if (hasPrintableValue(data.q6_correctiveActions)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>6. Provide details of any corrective actions taken or underway on issues relating to advertising, and delivery of essential services; cyber security and data privacy of customers; re-occurrence of instances of product recalls; penalty / action taken by regulatory authorities on safety of products / services:</p>";
    html += "<table><tbody><tr>";
    html += `<td>${data.q6_correctiveActions}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q7a: Number of instances of data breaches
  if (hasPrintableValue(data.q7a_dataBreaches)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>7a. Number of instances of data breaches along-with impact:</p>";
    html += "<table><tbody><tr>";
    html += `<td>${data.q7a_dataBreaches}</td>`;
    html += "</tr></tbody></table>";
  }

  // Q7b: Percentage of data breaches involving PII
  if (hasPrintableValue(data.q7b_piiBreaches)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>7b. Percentage of data breaches involving personally identifiable information of customers:</p>";
    html += "<table><tbody><tr>";
    html += `<td>${data.q7b_piiBreaches}%</td>`;
    html += "</tr></tbody></table>";
  }

  // Q7c: Impact of data breaches
  if (hasPrintableValue(data.q7c_breachImpact)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>7c. Impact, if any, of the data breaches:</p>";
    html += "<table><tbody><tr>";
    html += `<td>${data.q7c_breachImpact}</td>`;
    html += "</tr></tbody></table>";
  }

  return html || "<p>No data available</p>";
};

// Specialized converters for principles with complex table structures

// Principle 3 Essential — Q13 complaints table only (exact headers). Section title added by convertPrincipleDataToHtml.
const convertPrinciple3EssentialQ13Html = (data, financialYears = {}) => {
  let html = "";
  const L = buildFyHeaderLabels(financialYears);

  // Q13: Complaints table
  if (data.q13_complaints && hasPrintableValue(data.q13_complaints)) {
    html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>13. Number of Complaints on the following made by employees and workers:</p>";
    html += "<table><thead>";
    html += "<tr>";
    html += "<th rowspan='2' style='vertical-align:middle;'></th>";
    html += `<th colspan='3' style='text-align:center;'>${L.colCurrentLong}</th>`;
    html += `<th colspan='3' style='text-align:center;'>${L.colPreviousLong}</th>`;
    html += "</tr>";
    html += "<tr>";
    html += "<th>Filed during the Year</th>";
    html += "<th>Pending resolution at the end of year</th>";
    html += "<th>Remarks</th>";
    html += "<th>Filed during the Year</th>";
    html += "<th>Pending resolution at the end of year</th>";
    html += "<th>Remarks</th>";
    html += "</tr>";
    html += "</thead><tbody>";

    // Working Conditions
    if (data.q13_complaints.workingConditions && hasPrintableValue(data.q13_complaints.workingConditions)) {
      html += "<tr>";
      html += "<td>Working Conditions</td>";
      html += `<td>${data.q13_complaints.workingConditions.currentFiled || "-"}</td>`;
      html += `<td>${data.q13_complaints.workingConditions.currentPending || "-"}</td>`;
      html += `<td>${data.q13_complaints.workingConditions.currentRemarks || "-"}</td>`;
      html += `<td>${data.q13_complaints.workingConditions.previousFiled || "-"}</td>`;
      html += `<td>${data.q13_complaints.workingConditions.previousPending || "-"}</td>`;
      html += `<td>${data.q13_complaints.workingConditions.previousRemarks || "-"}</td>`;
      html += "</tr>";
    }

    // Health & Safety
    if (data.q13_complaints.healthSafety && hasPrintableValue(data.q13_complaints.healthSafety)) {
      html += "<tr>";
      html += "<td>Health & Safety</td>";
      html += `<td>${data.q13_complaints.healthSafety.currentFiled || "-"}</td>`;
      html += `<td>${data.q13_complaints.healthSafety.currentPending || "-"}</td>`;
      html += `<td>${data.q13_complaints.healthSafety.currentRemarks || "-"}</td>`;
      html += `<td>${data.q13_complaints.healthSafety.previousFiled || "-"}</td>`;
      html += `<td>${data.q13_complaints.healthSafety.previousPending || "-"}</td>`;
      html += `<td>${data.q13_complaints.healthSafety.previousRemarks || "-"}</td>`;
      html += "</tr>";
    }

    html += "</tbody></table>";
  }

  return html;
};

// Principle 6 Essential — Q6 air emissions table only (matches Section C column labels). Section title added by caller.
const convertPrinciple6EssentialQ6Html = (data, financialYears = {}) => {
  let html = "";
  const L = buildFyHeaderLabels(financialYears);

  // Q6: Air emissions
  if (data.q6_airEmissions && data.q6_airEmissions.length > 0) {
    const rows = data.q6_airEmissions.filter((row) => rowHasPrintableValues(row));
    if (rows.length > 0) {
      html += "<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>6. Please provide details of air emissions (other than GHG emissions) by the entity, in the following format:</p>";
      html += "<table><thead><tr>";
      html += "<th>Parameter</th>";
      html += "<th>Please specify unit</th>";
      html += `<th>${L.parenCurrent}</th>`;
      html += `<th>${L.parenPrevious}</th>`;
      html += "</tr></thead><tbody>";
      rows.forEach((row) => {
        html += "<tr>";
        // For "Others - please specify", combine parameter with the specify field
        const parameterText = row.parameter === "Others - please specify" && row.specify ? `${row.parameter} - ${row.specify}` : row.parameter || "-";
        html += `<td>${parameterText}</td>`;
        html += `<td>${row.unit || "-"}</td>`;
        html += `<td>${row.current || "-"}</td>`;
        html += `<td>${row.previous || "-"}</td>`;
        html += "</tr>";
      });
      html += "</tbody></table>";
    }
  }

  return html;
};

// Drop section <h3> and trim; if nothing left, avoid PDF text path showing only raw tags
const finalizePrincipleSectionHtml = (html) => {
  if (!html || !html.trim()) return "<p>No data available</p>";
  const stripped = html.replace(/<h3[^>]*>[\s\S]*?<\/h3>\s*/i, "").trim();
  if (!stripped) return "<p>No data available</p>";
  return html;
};

// Generic converter for other principles (simplified but structured)
export const convertPrincipleDataToHtml = (principleData, principleNum, type, financialYears = {}) => {
  if (!principleData || typeof principleData !== "object") return "";

  let html = "";
  html += `<h3 style='font-size:13px;font-weight:500;color:#2D3F50;margin:12px 0 8px 0;'>${type} Indicators</h3>`;

  const skipKeys = new Set();

  if (principleNum === 3 && type === "Essential") {
    html += convertPrinciple3EssentialQ13Html(principleData, financialYears);
    skipKeys.add("q13_complaints");
  } else if (principleNum === 6 && type === "Essential") {
    html += convertPrinciple6EssentialQ6Html(principleData, financialYears);
    skipKeys.add("q6_airEmissions");
  } else if (principleNum === 7 && type === "Essential") {
    html += convertPrinciple7EssentialToHtml(principleData);
    return finalizePrincipleSectionHtml(html);
  } else if (principleNum === 9 && type === "Essential") {
    html += convertPrinciple9EssentialToHtml(principleData, financialYears);
    return finalizePrincipleSectionHtml(html);
  }

  Object.entries(principleData).forEach(([key, value]) => {
    if (skipKeys.has(key)) return;
    // Extract question number and name from key
    const questionMatch = key.match(/^q(\d+[a-z]?)_(.+)$/);
    const questionNum = questionMatch ? questionMatch[1] : "";
    const questionName = questionMatch ? questionMatch[2] : key;

    // Format question name for display
    const displayName = questionName
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();

    // Handle different value types
    if (!hasPrintableValue(value)) return;

    if (Array.isArray(value) && value.length > 0) {
      // Array of objects - create a table
      if (typeof value[0] === "object") {
        const rows = value.filter((row) => rowHasPrintableValues(row));
        if (rows.length === 0) return;
        const keys = Object.keys(rows[0]);

        // Add question caption
        html += `<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>Q${questionNum}: ${displayName}</p>`;

        // Check if this table should have S.No. column (for tradeChambers type tables)
        const needsSerialNo = key.includes("tradeChambers") || key.includes("tradeAndIndustry");

        html += "<table><thead><tr>";
        if (needsSerialNo) {
          html += "<th style='width:80px;'>S. No.</th>";
        }
        keys.forEach((k) => {
          const headerName = k.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
          html += `<th>${headerName}</th>`;
        });
        html += "</tr></thead><tbody>";
        rows.forEach((row, idx) => {
          html += "<tr>";
          if (needsSerialNo) {
            html += `<td>${idx + 1}</td>`;
          }
          keys.forEach((k) => {
            html += `<td>${row[k] || "-"}</td>`;
          });
          html += "</tr>";
        });
        html += "</tbody></table>";
      } else {
        html += `<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>Q${questionNum}: ${displayName}</p>`;
        html += "<table><thead><tr><th>Value</th></tr></thead><tbody>";
        value.forEach((item) => {
          html += `<tr><td>${item === false ? "No" : item === true ? "Yes" : item || "-"}</td></tr>`;
        });
        html += "</tbody></table>";
      }
    } else if (typeof value === "object" && value !== null) {
      // Nested object - create a structured table
      html += `<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>Q${questionNum}: ${displayName}</p>`;
      html += "<table><tbody>";

      const renderNestedObject = (obj, prefix = "") => {
        let rows = "";
        Object.entries(obj).forEach(([k, v]) => {
          const label = k.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
          if (typeof v === "object" && v !== null && !Array.isArray(v)) {
            // Check if it's a simple current/previous structure
            if (v.current !== undefined || v.previous !== undefined) {
              rows += `<tr><td>${prefix}${label}</td><td>Current: ${v.current || "-"}</td><td>Previous: ${v.previous || "-"}</td></tr>`;
            } else {
              rows += renderNestedObject(v, `${prefix}${label} - `);
            }
          } else if (Array.isArray(v)) {
            rows += `<tr><td>${prefix}${label}</td><td colspan="2">${v.map((item) => (typeof item === "object" ? JSON.stringify(item) : item)).join(", ") || "-"}</td></tr>`;
          } else {
            const normalized = v === false ? "No" : v === true ? "Yes" : v || "-";
            rows += `<tr><td>${prefix}${label}</td><td colspan="2">${normalized}</td></tr>`;
          }
        });
        return rows;
      };

      html += renderNestedObject(value);
      html += "</tbody></table>";
    } else if (value !== "" && value !== null && value !== undefined) {
      // Simple string/number value
      html += `<p style='font-weight:500;color:#407775;margin:8px 0 6px 0;font-size:11px;'>Q${questionNum}: ${displayName}</p>`;
      const normalized = value === false ? "No" : value === true ? "Yes" : value;
      html += `<table><tbody><tr><td>${normalized}</td></tr></tbody></table>`;
    }
  });

  return finalizePrincipleSectionHtml(html);
};

// Section C UI stores FY as { currentFY, previousFY }; step1 only has financialYear — derive previous like SectionC.js
const getSectionCFinancialYearsForPdf = (sectionData) => {
  const fromSaved = sectionData?.financialYears;
  if (fromSaved && (String(fromSaved.currentFY || "").trim() || String(fromSaved.previousFY || "").trim())) {
    return fromSaved;
  }
  try {
    const step1 = localStorage.getItem("step1");
    if (!step1) return {};
    const dataStep1 = JSON.parse(step1);
    const currentFY = dataStep1.financialYear;
    if (!currentFY) return {};
    const parts = String(currentFY).split("-");
    if (parts.length !== 2) return { currentFY: String(currentFY), previousFY: "" };
    const startYear = parseInt(parts[0], 10);
    if (Number.isNaN(startYear)) return { currentFY: String(currentFY), previousFY: "" };
    const prevStartYear = startYear - 1;
    const prevEndYear = startYear;
    const previousFY = `${prevStartYear}-${String(prevEndYear).slice(-2)}`;
    return { currentFY: String(currentFY), previousFY };
  } catch {
    return {};
  }
};

/**
 * Same derivation as Section A `getFinancialYears()` — full-span labels for PDF (e.g. 2025-2026).
 * step1.financialYear is typically "YYYY-YY" (e.g. 2025-26).
 */
export const deriveExpandedFinancialYearLabelsFromStep1 = () => {
  try {
    const step1 = localStorage.getItem("step1");
    if (!step1) return { currentYear: "", previousYear: "", priorYear: "" };
    const dataStep1 = JSON.parse(step1);
    const currentFY = dataStep1.financialYear;
    if (!currentFY) return { currentYear: "", previousYear: "", priorYear: "" };
    const parts = String(currentFY).split("-");
    if (parts.length !== 2) return { currentYear: String(currentFY), previousYear: "", priorYear: "" };
    const startYear = parseInt(parts[0], 10);
    if (Number.isNaN(startYear)) return { currentYear: String(currentFY), previousYear: "", priorYear: "" };
    return {
      currentYear: `${startYear}-${startYear + 1}`,
      previousYear: `${startYear - 1}-${startYear}`,
      priorYear: `${startYear - 2}-${startYear - 1}`,
    };
  } catch {
    return { currentYear: "", previousYear: "", priorYear: "" };
  }
};

/** Unified FY strings for PDF table headers (Section A + Section C shapes). */
const buildFyHeaderLabels = (fyIn = {}) => {
  const exp = deriveExpandedFinancialYearLabelsFromStep1();
  // Prefer Section C shape (currentFY) when present so labels match the module; else Section A expanded years.
  const cur = String(fyIn.currentFY || "").trim() || String(fyIn.currentYear || "").trim() || exp.currentYear || "";
  const prev = String(fyIn.previousFY || "").trim() || String(fyIn.previousYear || "").trim() || exp.previousYear || "";
  const prior = String(fyIn.priorYear || "").trim() || exp.priorYear || "";
  return {
    current: cur,
    previous: prev,
    prior: prior,
    colCurrent: cur ? `FY ${cur}` : "Current FY",
    colPrevious: prev ? `FY ${prev}` : "Previous FY",
    colPrior: prior ? `FY ${prior}` : "Prior FY",
    colCurrentLong: cur ? `FY ${cur}` : "Current FY",
    colPreviousLong: prev ? `FY ${prev}` : "Previous FY",
    colPriorLong: prior ? `FY ${prior}` : "Prior FY",
    /** PDF/table headers: short FY labels only (no Current/Previous Financial Year suffix). */
    parenCurrent: cur ? `FY ${cur}` : "Current FY",
    parenPrevious: prev ? `FY ${prev}` : "Previous FY",
  };
};

// ============================================================================
// Per-Question Converter Functions for Section C
// ============================================================================

// Convert a single question's data to HTML table (without question text - just the answer)
const convertSingleQuestionToHtml = (principleNum, type, questionKey, questionData, financialYears = {}) => {
  if (!questionData || !hasMeaningfulContent(questionData)) return "";

  const L = buildFyHeaderLabels(financialYears);

  // Handle simple text/string answers
  if (typeof questionData === "string" && questionData.trim() !== "") {
    // Section C: plain text answers should not be wrapped in <table>,
    // otherwise the PDF layer treats them as tables and converts to images.
    return questionData;
  }

  // Handle simple Yes/No object answers
  if (typeof questionData === "object" && !Array.isArray(questionData)) {
    // Check if it's a simple hasValue/details pattern
    if (questionData.hasProcesses !== undefined || questionData.hasProcedures !== undefined || questionData.hasSystem !== undefined) {
      const lines = [];
      Object.entries(questionData).forEach(([key, value]) => {
        if (hasPrintableValue(value)) {
          const label = key
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
          const normalized = value === false ? "No" : value === true ? "Yes" : value || "-";
          lines.push(`<p><strong>${label}:</strong> ${normalized}</p>`);
        }
      });
      return lines.length ? `<div class="brsr-pdf-rich-lines">${lines.join("")}</div>` : "";
    }

    // Check for current/previous pattern
    if (questionData.current !== undefined || questionData.previous !== undefined) {
      let html = `<table><thead><tr><th>${L.parenCurrent}</th><th>${L.parenPrevious}</th></tr></thead><tbody>`;
      html += `<tr><td>${questionData.current || "-"}</td><td>${questionData.previous || "-"}</td></tr>`;
      html += "</tbody></table>";
      return html;
    }
  }

  // Handle array of objects (tables)
  if (Array.isArray(questionData) && questionData.length > 0 && typeof questionData[0] === "object") {
    const rows = questionData.filter((row) => rowHasPrintableValues(row));
    if (rows.length === 0) return "";

    const keys = Object.keys(rows[0]);
    let html = "<table><thead><tr>";
    keys.forEach((k) => {
      let headerName = k
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
      // Special handling for current/previous columns with FY
      if (k === "current") headerName = L.parenCurrent;
      if (k === "previous") headerName = L.parenPrevious;
      html += `<th>${headerName}</th>`;
    });
    html += "</tr></thead><tbody>";
    rows.forEach((row) => {
      html += "<tr>";
      keys.forEach((k) => {
        const val = row[k];
        const normalized = val === false ? "No" : val === true ? "Yes" : val || "-";
        html += `<td>${normalized}</td>`;
      });
      html += "</tr>";
    });
    html += "</tbody></table>";
    return html;
  }

  // Handle complex nested objects
  if (typeof questionData === "object" && !Array.isArray(questionData)) {
    let html = "<table><tbody>";

    const renderNestedObject = (obj, prefix = "") => {
      let rows = "";
      Object.entries(obj).forEach(([k, v]) => {
        const label = k
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, (str) => str.toUpperCase())
          .trim();
        if (typeof v === "object" && v !== null && !Array.isArray(v)) {
          // Check for current/previous structure
          if (v.current !== undefined || v.previous !== undefined) {
            rows += `<tr><td>${prefix}${label}</td><td>${L.colCurrent}: ${v.current || "-"}</td><td>${L.colPrevious}: ${v.previous || "-"}</td></tr>`;
          } else {
            rows += renderNestedObject(v, `${prefix}${label} - `);
          }
        } else if (Array.isArray(v)) {
          rows += `<tr><td>${prefix}${label}</td><td colspan="2">${v.map((item) => (typeof item === "object" ? JSON.stringify(item) : item)).join(", ") || "-"}</td></tr>`;
        } else {
          const normalized = v === false ? "No" : v === true ? "Yes" : v || "-";
          rows += `<tr><td>${prefix}${label}</td><td colspan="2">${normalized}</td></tr>`;
        }
      });
      return rows;
    };

    html += renderNestedObject(questionData);
    html += "</tbody></table>";
    return html;
  }

  return "";
};

// Specialized converters for specific question types that need custom formatting

// Principle 1 Q1: Training table
const convertP1Q1TrainingToHtml = (data) => {
  if (!data.q1_training || !data.q1_training.length) return "";
  const rows = data.q1_training.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Segment</th>";
  html += "<th>Total Number of training and awareness programmes held</th>";
  html += "<th>Topics / principles covered under the training and its impact</th>";
  html += "<th>%age of persons in respective category covered by the awareness programmes</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.segment || "-"}</td>`;
    html += `<td>${row.programmes || "-"}</td>`;
    html += `<td>${row.topics || "-"}</td>`;
    html += `<td>${hasPrintableValue(row.percentage) ? `${row.percentage}%` : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

/**
 * Principle 1 Essential Q2 — matches SectionC.js: Monetary / Non-Monetary band rows, then column headers,
 * then data (same header row repeated after Non-Monetary label).
 */
const convertP1Q2FinesToHtml = (data) => {
  if (!data.q2_fines || !hasPrintableValue(data.q2_fines)) return "";

  const band = "background-color:#f5f5f5;font-weight:600;padding:12px 16px;font-size:15px;";
  const thStyle = "padding:8px 6px;border-bottom:1px solid #ddd;font-weight:600;";
  const headerRow = () => `<tr><th style="${thStyle}">Type</th><th style="${thStyle}">NGRBC Principle</th><th style="${thStyle}">Name of the regulatory/enforcement agencies/judicial institutions</th><th style="${thStyle}">Amount (INR)</th><th style="${thStyle}">Brief of the Case</th><th style="${thStyle}">Has an appeal been preferred? (Yes/No)</th></tr>`;

  let html = "<table><tbody>";
  html += `<tr><td colspan="6" style="${band}">Monetary</td></tr>`;
  html += headerRow();

  if (data.q2_fines.monetary) {
    const monetaryRows = data.q2_fines.monetary.filter((row) => rowHasPrintableValues(row));
    if (monetaryRows.length > 0) {
      monetaryRows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.type || "-"}</td>`;
        html += `<td>${row.principle || "-"}</td>`;
        html += `<td>${row.agency || "-"}</td>`;
        html += `<td>${row.amount || "-"}</td>`;
        html += `<td>${row.brief || "-"}</td>`;
        html += `<td>${row.appeal || "-"}</td>`;
        html += "</tr>";
      });
    } else {
      html += "<tr><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td></tr>";
    }
  }

  html += `<tr><td colspan="6" style="${band}">Non-Monetary</td></tr>`;
  html += headerRow();

  if (data.q2_fines.nonMonetary) {
    const nonMonetaryRows = data.q2_fines.nonMonetary.filter((row) => rowHasPrintableValues(row));
    if (nonMonetaryRows.length > 0) {
      nonMonetaryRows.forEach((row) => {
        html += "<tr>";
        html += `<td>${row.type || "-"}</td>`;
        html += `<td>${row.principle || "-"}</td>`;
        html += `<td>${row.agency || "-"}</td>`;
        html += `<td>-</td>`;
        html += `<td>${row.brief || "-"}</td>`;
        html += `<td>${row.appeal || "-"}</td>`;
        html += "</tr>";
      });
    } else {
      html += "<tr><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td><td>-</td></tr>";
    }
  }

  html += "</tbody></table>";
  return html;
};

/** Principle 1 Essential Q3 — exact column titles as SectionC.js (no Actions column in PDF). */
const convertP1Q3AppealsToHtml = (data) => {
  if (!data.q3_appeals || !data.q3_appeals.length) return "";
  const rows = data.q3_appeals.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Case Details</th>";
  html += "<th>Name of the regulatory/enforcement agencies/judicial institutions</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.caseDetails || "-"}</td>`;
    html += `<td>${row.agency || "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 1 Essential — Q5 disciplinary: matches SectionC.js (blank top-left header cell)
const convertP1Q5DisciplinaryToHtml = (data, financialYears = {}) => {
  if (!data.q5_disciplinary || !hasPrintableValue(data.q5_disciplinary)) return "";

  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th></th><th>${L.parenCurrent}</th><th>${L.parenPrevious}</th>`;
  html += "</tr></thead><tbody>";

  const categories = [
    { key: "directors", label: "Directors" },
    { key: "kmps", label: "KMPs" },
    { key: "employees", label: "Employees" },
    { key: "workers", label: "Workers" },
  ];

  categories.forEach((cat) => {
    const row = data.q5_disciplinary[cat.key];
    html += "<tr>";
    html += `<td>${cat.label}</td>`;
    html += `<td>${row && hasPrintableValue(row.current) ? row.current : "-"}</td>`;
    html += `<td>${row && hasPrintableValue(row.previous) ? row.previous : "-"}</td>`;
    html += "</tr>";
  });

  html += "</tbody></table>";
  return html;
};

// Principle 1 Essential — Q6: first column = full complaint labels (SectionC.js), not short category names
const convertP1Q6ConflictToHtml = (data, financialYears = {}) => {
  if (!data.q6_conflict || !hasPrintableValue(data.q6_conflict)) return "";

  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead>";
  html += "<tr>";
  html += '<th rowspan="2"></th>';
  html += `<th colspan="2">${L.parenCurrent}</th>`;
  html += `<th colspan="2">${L.parenPrevious}</th>`;
  html += "</tr><tr>";
  html += "<th>Number</th><th>Remarks</th><th>Number</th><th>Remarks</th>";
  html += "</tr></thead><tbody>";

  const rows = [
    {
      key: "directors",
      label: "Number of complaints received in relation to issues of Conflict of Interest of the Directors",
    },
    {
      key: "kmps",
      label: "Number of complaints received in relation to issues of Conflict of Interest of the KMPs",
    },
  ];

  rows.forEach(({ key, label }) => {
    const row = data.q6_conflict[key];
    html += "<tr>";
    html += `<td>${label}</td>`;
    html += `<td>${row && hasPrintableValue(row.currentNumber) ? row.currentNumber : "-"}</td>`;
    html += `<td>${row && hasPrintableValue(row.currentRemarks) ? row.currentRemarks : "-"}</td>`;
    html += `<td>${row && hasPrintableValue(row.previousNumber) ? row.previousNumber : "-"}</td>`;
    html += `<td>${row && hasPrintableValue(row.previousRemarks) ? row.previousRemarks : "-"}</td>`;
    html += "</tr>";
  });

  html += "</tbody></table>";
  return html;
};

// Principle 1 Essential — Q8: matches SectionC.js (blank header cell + metric label in first column)
const convertP1Q8AccountsPayableToHtml = (data, financialYears = {}) => {
  if (!data.q8_accountsPayable || !hasPrintableValue(data.q8_accountsPayable)) return "";

  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th></th><th>${L.parenCurrent}</th><th>${L.parenPrevious}</th>`;
  html += "</tr></thead><tbody><tr>";
  html += "<td>Number of days of accounts payables</td>";
  html += `<td>${data.q8_accountsPayable.current || "-"}</td>`;
  html += `<td>${data.q8_accountsPayable.previous || "-"}</td>`;
  html += "</tr></tbody></table>";
  return html;
};

const formatP1Q9PercentCell = (v) => {
  if (!hasPrintableValue(v)) return "-";
  const s = String(v).trim();
  if (s === "") return "-";
  return /%$/.test(s) ? s : `${s}%`;
};

const formatP1Q9NumberCell = (v) => (hasPrintableValue(v) ? String(v).trim() : "-");

// Principle 1 Essential Q9 — SectionC.js: Parameter | Metrics | FY columns + rowspans
const convertP1Q9OpennessToHtml = (data, financialYears = {}) => {
  if (!data.q9_openness || !hasPrintableValue(data.q9_openness)) return "";

  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += "<th>Parameter</th><th>Metrics</th>";
  html += `<th>${L.parenCurrent}</th>`;
  html += `<th>${L.parenPrevious}</th>`;
  html += "</tr></thead><tbody>";

  const op = data.q9_openness;
  const cp = op.concentrationPurchases || {};
  html += '<tr><td rowspan="3">Concentration of Purchases</td>';
  html += "<td>(a) Purchases from trading houses as % of total purchases</td>";
  html += `<td>${formatP1Q9PercentCell(cp.a?.current)}</td><td>${formatP1Q9PercentCell(cp.a?.previous)}</td></tr>`;
  html += "<tr><td>(b) Number of trading houses</td>";
  html += `<td>${formatP1Q9NumberCell(cp.b?.current)}</td><td>${formatP1Q9NumberCell(cp.b?.previous)}</td></tr>`;
  html += "<tr><td>(c) Purchases from top 10 trading houses as %</td>";
  html += `<td>${formatP1Q9PercentCell(cp.c?.current)}</td><td>${formatP1Q9PercentCell(cp.c?.previous)}</td></tr>`;

  const cs = op.concentrationSales || {};
  html += '<tr><td rowspan="3">Concentration of Sales</td>';
  html += "<td>(a) Sales to dealers/distributors as % of total sales</td>";
  html += `<td>${formatP1Q9PercentCell(cs.a?.current)}</td><td>${formatP1Q9PercentCell(cs.a?.previous)}</td></tr>`;
  html += "<tr><td>(b) Number of dealers/distributors</td>";
  html += `<td>${formatP1Q9NumberCell(cs.b?.current)}</td><td>${formatP1Q9NumberCell(cs.b?.previous)}</td></tr>`;
  html += "<tr><td>(c) Sales to top 10 dealers/distributors as %</td>";
  html += `<td>${formatP1Q9PercentCell(cs.c?.current)}</td><td>${formatP1Q9PercentCell(cs.c?.previous)}</td></tr>`;

  const sr = op.shareRPTs || {};
  const rptTitle = "Share of related party transactions (RPTs) in:";
  html += `<tr><td rowspan="4">${rptTitle}</td>`;
  html += "<td>(a) Purchases (Purchases with related parties / Total Purchases)</td>";
  html += `<td>${formatP1Q9PercentCell(sr.a?.current)}</td><td>${formatP1Q9PercentCell(sr.a?.previous)}</td></tr>`;
  html += "<tr><td>(b) Sales (Sales to related parties / Total Sales)</td>";
  html += `<td>${formatP1Q9PercentCell(sr.b?.current)}</td><td>${formatP1Q9PercentCell(sr.b?.previous)}</td></tr>`;
  html += "<tr><td>(c) Loans & advances (Loans & advances given to related parties / Total loans & advances)</td>";
  html += `<td>${formatP1Q9PercentCell(sr.c?.current)}</td><td>${formatP1Q9PercentCell(sr.c?.previous)}</td></tr>`;
  html += "<tr><td>(d) Investments (Investments in related parties / Total Investments made)</td>";
  html += `<td>${formatP1Q9PercentCell(sr.d?.current)}</td><td>${formatP1Q9PercentCell(sr.d?.previous)}</td></tr>`;

  html += "</tbody></table>";
  return html;
};

// Principle 1 Leadership Q1 — exact headers as SectionC.js (no Actions column in PDF)
const convertP1LeadershipQ1ValueChainToHtml = (data) => {
  if (!Array.isArray(data) || data.length === 0) return "";
  const rows = data.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Total no of awareness programmes held</th>";
  html += "<th>Topics/principles covered under the training</th>";
  html += "<th>%age of value chain partners covered (by value of business done with such partners) under the awareness programmes</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.programmes || "-"}</td>`;
    html += `<td>${row.topics || "-"}</td>`;
    html += `<td>${hasPrintableValue(row.percentage) ? `${String(row.percentage).trim()}%` : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const formatP2PercentageCell = (v) => {
  if (!hasPrintableValue(v)) return "-";
  const s = String(v).trim();
  if (s === "-") return "-";
  return /%$/.test(s) ? s : `${s}%`;
};

// Principle 2 Essential — Q1 rd/capex: headered table
const convertP2Q1RdCapexToHtml = (data, financialYears = {}) => {
  if (!data.q1_rdCapex) return "";
  const rd = data.q1_rdCapex.rd || {};
  const capex = data.q1_rdCapex.capex || {};

  const hasAny = hasPrintableValue(rd.current) || hasPrintableValue(rd.previous) || hasPrintableValue(rd.details) || hasPrintableValue(capex.current) || hasPrintableValue(capex.previous) || hasPrintableValue(capex.details);
  if (!hasAny) return "";

  const L = buildFyHeaderLabels(financialYears);
  // Matches Section C UI table columns (and ensures <thead> exists for PDF header rendering)
  let html = "<table><thead><tr>";
  html += "<th></th>";
  html += `<th>${L.parenCurrent}</th>`;
  html += `<th>${L.parenPrevious}</th>`;
  html += "<th>Details of improvements in environmental and social impacts</th>";
  html += "</tr></thead><tbody>";

  html += "<tr>";
  html += "<td>R&D</td>";
  html += `<td>${formatP2PercentageCell(rd.current)}</td>`;
  html += `<td>${formatP2PercentageCell(rd.previous)}</td>`;
  html += `<td>${rd.details || "-"}</td>`;
  html += "</tr>";

  html += "<tr>";
  html += "<td>Capex</td>";
  html += `<td>${formatP2PercentageCell(capex.current)}</td>`;
  html += `<td>${formatP2PercentageCell(capex.previous)}</td>`;
  html += `<td>${capex.details || "-"}</td>`;
  html += "</tr>";

  html += "</tbody></table>";
  return html;
};

// Principle 2 Essential — Q3: bold label + details per line (no table in PDF)
const convertP2Q3ReclaimProcessesToHtml = (data) => {
  if (!data.q3_reclaimProcesses) return "";
  const q = data.q3_reclaimProcesses || {};
  const hasAny = hasPrintableValue(q.plastics) || hasPrintableValue(q.ewaste) || hasPrintableValue(q.hazardous) || hasPrintableValue(q.other);
  if (!hasAny) return "";

  const blocks = [
    ["(a) Plastics (including packaging)", q.plastics],
    ["(b) E-waste", q.ewaste],
    ["(c) Hazardous waste", q.hazardous],
    ["(d) Other waste", q.other],
  ];
  let html = '<div class="brsr-pdf-rich-lines">';
  blocks.forEach(([label, val]) => {
    const detail = hasPrintableValue(val) ? String(val).trim() : "-";
    html += `<p><strong>${label}</strong> — ${detail}</p>`;
  });
  html += "</div>";
  return html;
};

const p3CalcPct = (num, den) => {
  if (!hasPrintableValue(num) || !hasPrintableValue(den) || parseFloat(String(den).replace(/,/g, "")) === 0) return "";
  const p = (parseFloat(String(num).replace(/,/g, "")) / parseFloat(String(den).replace(/,/g, ""))) * 100;
  return p.toFixed(2);
};

const p3Sum = (a, b) => String((parseFloat(a) || 0) + (parseFloat(b) || 0));

const p3WellbeingRowHtml = (genderLabel, row) => {
  const total = row.total || "";
  const hi = row.healthInsurance || "";
  const ai = row.accidentInsurance || "";
  const mat = row.maternity || "";
  const pat = row.paternity || "";
  const dc = row.dayCare || "";
  const hp = p3CalcPct(hi, total);
  const ap = p3CalcPct(ai, total);
  const mp = p3CalcPct(mat, total);
  const pp = p3CalcPct(pat, total);
  const dp = p3CalcPct(dc, total);
  return `<tr><td>${genderLabel}</td><td>${total || "-"}</td><td>${hi || "-"}</td><td>${hp ? `${hp}%` : ""}</td><td>${ai || "-"}</td><td>${ap ? `${ap}%` : ""}</td><td>${mat || "-"}</td><td>${mp ? `${mp}%` : ""}</td><td>${pat || "-"}</td><td>${pp ? `${pp}%` : ""}</td><td>${dc || "-"}</td><td>${dp ? `${dp}%` : ""}</td></tr>`;
};

const p3WellbeingTotalRowHtml = (cat) => {
  const m = cat.male;
  const f = cat.female;
  const totalA = p3Sum(m.total, f.total);
  const sumHi = p3Sum(m.healthInsurance, f.healthInsurance);
  const sumAi = p3Sum(m.accidentInsurance, f.accidentInsurance);
  const sumMat = p3Sum(m.maternity, f.maternity);
  const sumPat = p3Sum(m.paternity, f.paternity);
  const sumDc = p3Sum(m.dayCare, f.dayCare);
  const hp = p3CalcPct(sumHi, totalA);
  const ap = p3CalcPct(sumAi, totalA);
  const mp = p3CalcPct(sumMat, totalA);
  const pp = p3CalcPct(sumPat, totalA);
  const dp = p3CalcPct(sumDc, totalA);
  return `<tr><td>Total</td><td>${totalA || "-"}</td><td>${sumHi || "-"}</td><td>${hp ? `${hp}%` : ""}</td><td>${sumAi || "-"}</td><td>${ap ? `${ap}%` : ""}</td><td>${sumMat || "-"}</td><td>${mp ? `${mp}%` : ""}</td><td>${sumPat || "-"}</td><td>${pp ? `${pp}%` : ""}</td><td>${sumDc || "-"}</td><td>${dp ? `${dp}%` : ""}</td></tr>`;
};

const buildP3Q1WellbeingTableHtml = (wellbeingRoot, coveredLabel, permanentSectionTitle, otherSectionTitle) => {
  const perm = wellbeingRoot.permanent;
  const otp = wellbeingRoot.otherThanPermanent;
  let html = "<table><thead>";
  html += `<tr><th rowspan="3">Category</th><th rowspan="3">Total (A)</th><th colspan="10">${coveredLabel}</th></tr>`;
  html += '<tr><th colspan="2">Health insurance</th><th colspan="2">Accident insurance</th><th colspan="2">Maternity benefits</th><th colspan="2">Paternity Benefits</th><th colspan="2">Day Care facilities</th></tr>';
  html += "<tr><th>Number (B)</th><th>% (B / A)</th><th>Number (C)</th><th>% (C / A)</th><th>Number (D)</th><th>% (D / A)</th><th>Number (E)</th><th>% (E / A)</th><th>Number (F)</th><th>% (F / A)</th></tr>";
  html += "</thead><tbody>";
  html += `<tr><td colspan="12" style="background-color:#f0f0f0;font-weight:600;text-align:center;">${permanentSectionTitle}</td></tr>`;
  html += p3WellbeingRowHtml("Male", perm.male);
  html += p3WellbeingRowHtml("Female", perm.female);
  html += p3WellbeingTotalRowHtml(perm);
  html += `<tr><td colspan="12" style="background-color:#f0f0f0;font-weight:600;text-align:center;">${otherSectionTitle}</td></tr>`;
  html += p3WellbeingRowHtml("Male", otp.male);
  html += p3WellbeingRowHtml("Female", otp.female);
  html += p3WellbeingTotalRowHtml(otp);
  html += "</tbody></table>";
  return html;
};

const convertP3Q1aEmployeeWellbeingToHtml = (data) => {
  if (!data.q1a_employeeWellbeing) return "";
  if (!hasPrintableValue(data.q1a_employeeWellbeing)) return "";
  return buildP3Q1WellbeingTableHtml(data.q1a_employeeWellbeing, "% of employees covered by", "Permanent employees", "Other than Permanent employees");
};

const convertP3Q1bWorkerWellbeingToHtml = (data) => {
  if (!data.q1b_workerWellbeing) return "";
  if (!hasPrintableValue(data.q1b_workerWellbeing)) return "";
  return buildP3Q1WellbeingTableHtml(data.q1b_workerWellbeing, "% of workers covered by", "Permanent workers", "Other than Permanent workers");
};

const p3FyHdr2 = (L, cur) => {
  const y = cur ? L.current : L.previous;

  return y ? `FY ${y}` : cur ? "Current FY" : "Previous FY";
};

const convertP3EssentialQ2RetirementToHtml = (principleData, financialYears = {}) => {
  const rb = principleData.q2_retirementBenefits;
  if (!rb || !hasPrintableValue(rb)) return "";
  const L = buildFyHeaderLabels(financialYears);
  const rows = [
    { key: "pf", label: "PF" },
    { key: "gratuity", label: "Gratuity" },
    { key: "esi", label: "ESI" },
    { key: "others", label: "Others – please specify" },
  ];
  let html = "<table><thead><tr>";
  html += `<th rowspan="2">Benefits</th><th colspan="3">${p3FyHdr2(L, true)}</th><th colspan="3">${p3FyHdr2(L, false)}</th></tr><tr>`;
  html += "<th>No. of employees covered as a % of total employees</th><th>No. of workers covered as a % of total workers</th><th>Deducted and deposited with the authority (Y/N/N.A.)</th>";
  html += "<th>No. of employees covered as a % of total employees</th><th>No. of workers covered as a % of total workers</th><th>Deducted and deposited with the authority (Y/N/N.A.)</th>";
  html += "</tr></thead><tbody>";
  rows.forEach(({ key, label }) => {
    const r = rb[key] || {};
    let firstCol = label;
    if (key === "others" && hasPrintableValue(rb.others?.specify)) firstCol += `<br/>${String(rb.others.specify).trim()}`;
    html += "<tr>";
    html += `<td>${firstCol}</td>`;
    html += `<td>${formatP2PercentageCell(r.currentEmployees)}</td>`;
    html += `<td>${formatP2PercentageCell(r.currentWorkers)}</td>`;
    html += `<td>${hasPrintableValue(r.currentDeposited) ? r.currentDeposited : "-"}</td>`;
    html += `<td>${formatP2PercentageCell(r.previousEmployees)}</td>`;
    html += `<td>${formatP2PercentageCell(r.previousWorkers)}</td>`;
    html += `<td>${hasPrintableValue(r.previousDeposited) ? r.previousDeposited : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP3EssentialQ5ReturnRetentionToHtml = (principleData) => {
  const q = principleData.q5_returnRetention;
  if (!q || !hasPrintableValue(q)) return "";
  const em = q.employees || {};
  const wo = q.workers || {};
  const avg = (a, b) => {
    const t = (parseFloat(a) || 0) + (parseFloat(b) || 0);
    return t > 0 ? (t / 2).toFixed(2) : "";
  };
  let html = "<table><thead><tr>";
  html += `<th rowspan="2">Gender</th><th colspan="2">Permanent employees</th><th colspan="2">Permanent workers</th></tr><tr>`;
  html += "<th>Return to work rate</th><th>Retention rate</th><th>Return to work rate</th><th>Retention rate</th>";
  html += "</tr></thead><tbody>";
  ["male", "female"].forEach((g) => {
    const gl = g === "male" ? "Male" : "Female";
    html += "<tr>";
    html += `<td>${gl}</td>`;
    html += `<td>${formatP2PercentageCell(em[`${g}Return`])}</td>`;
    html += `<td>${formatP2PercentageCell(em[`${g}Retention`])}</td>`;
    html += `<td>${formatP2PercentageCell(wo[`${g}Return`])}</td>`;
    html += `<td>${formatP2PercentageCell(wo[`${g}Retention`])}</td>`;
    html += "</tr>";
  });
  const tr = avg(em.maleReturn, em.femaleReturn);
  const ter = avg(em.maleRetention, em.femaleRetention);
  const twr = avg(wo.maleReturn, wo.femaleReturn);
  const twer = avg(wo.maleRetention, wo.femaleRetention);
  html += `<tr style="font-weight:600;background-color:#f5f5f5;"><td>Total</td>`;
  html += `<td>${tr ? `${tr}%` : ""}</td><td>${ter ? `${ter}%` : ""}</td>`;
  html += `<td>${twr ? `${twr}%` : ""}</td><td>${twer ? `${twer}%` : ""}</td></tr>`;
  html += "</tbody></table>";
  return html;
};

const convertP3EssentialQ6GrievanceToHtml = (principleData) => {
  const g = principleData.q6_grievance;
  if (!g || !hasPrintableValue(g)) return "";
  const cats = [
    { key: "permanentWorkers", label: "Permanent Workers" },
    { key: "otherThanPermanentWorkers", label: "Other than Permanent Workers" },
    { key: "permanentEmployees", label: "Permanent Employees" },
    { key: "otherThanPermanentEmployees", label: "Other than Permanent Employees" },
  ];
  let html = "<table><thead><tr><th></th><th>Yes/No (If Yes, then give details of the mechanism in brief)</th></tr></thead><tbody>";
  cats.forEach(({ key, label }) => {
    const row = g[key] || {};
    const yn = hasPrintableValue(row.hasMechanism) ? row.hasMechanism : "-";
    const det = row.hasMechanism === "Yes" && hasPrintableValue(row.details) ? String(row.details).trim() : "";
    const cell = det ? `${yn}<br/><br/>${det}` : yn;
    html += `<tr><td>${label}</td><td>${cell}</td></tr>`;
  });
  html += "</tbody></table>";
  return html;
};

const convertP3EssentialQ7MembershipToHtml = (principleData, financialYears = {}) => {
  const m = principleData.q7_membership;
  if (!m || !hasPrintableValue(m)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th rowspan="2">Category</th><th colspan="3">${p3FyHdr2(L, true)}</th><th colspan="3">${p3FyHdr2(L, false)}</th></tr><tr>`;
  html += "<th>Total employees / workers in respective category (A)</th>";
  html += "<th>No. of employees / workers in respective category, who are part of association(s) or Union (B)</th>";
  html += "<th>% (B / A)</th>";
  html += "<th>Total employees / workers in respective category (C)</th>";
  html += "<th>No. of employees / workers in respective category, who are part of association(s) or Union (D)</th>";
  html += "<th>% (D / C)</th>";
  html += "</tr></thead><tbody>";
  const pct = (mem, tot) => {
    const p = p3CalcPct(mem, tot);
    return p ? `${p}%` : "-";
  };
  const membershipRow = (label, cAgg, pAgg) => {
    html += `<tr><td style="font-weight:600;">${label}</td>`;
    html += `<td>${hasPrintableValue(cAgg?.total) ? cAgg.total : "-"}</td><td>${hasPrintableValue(cAgg?.members) ? cAgg.members : "-"}</td><td>${pct(cAgg?.members, cAgg?.total)}</td>`;
    html += `<td>${hasPrintableValue(pAgg?.total) ? pAgg.total : "-"}</td><td>${hasPrintableValue(pAgg?.members) ? pAgg.members : "-"}</td><td>${pct(pAgg?.members, pAgg?.total)}</td></tr>`;
  };
  membershipRow("Total Permanent Employees", m.currentFY?.permanentEmployees?.aggregate, m.previousFY?.permanentEmployees?.aggregate);
  ["male", "female", "others"].forEach((g) => {
    const gl = g === "male" ? "Male" : g === "female" ? "Female" : "Others";
    const c = m.currentFY?.permanentEmployees?.[g] || {};
    const p = m.previousFY?.permanentEmployees?.[g] || {};
    html += `<tr><td style="font-weight:500;padding-left:12px;">${gl}</td>`;
    html += `<td>${hasPrintableValue(c.total) ? c.total : "-"}</td><td>${hasPrintableValue(c.members) ? c.members : "-"}</td><td>${pct(c.members, c.total)}</td>`;
    html += `<td>${hasPrintableValue(p.total) ? p.total : "-"}</td><td>${hasPrintableValue(p.members) ? p.members : "-"}</td><td>${pct(p.members, p.total)}</td></tr>`;
  });
  membershipRow("Total Permanent Workers", m.currentFY?.permanentWorkers?.aggregate, m.previousFY?.permanentWorkers?.aggregate);
  ["male", "female", "others"].forEach((g) => {
    const gl = g === "male" ? "Male" : g === "female" ? "Female" : "Others";
    const c = m.currentFY?.permanentWorkers?.[g] || {};
    const p = m.previousFY?.permanentWorkers?.[g] || {};
    html += `<tr><td style="font-weight:500;padding-left:12px;">${gl}</td>`;
    html += `<td>${hasPrintableValue(c.total) ? c.total : "-"}</td><td>${hasPrintableValue(c.members) ? c.members : "-"}</td><td>${pct(c.members, c.total)}</td>`;
    html += `<td>${hasPrintableValue(p.total) ? p.total : "-"}</td><td>${hasPrintableValue(p.members) ? p.members : "-"}</td><td>${pct(p.members, p.total)}</td></tr>`;
  });
  html += "</tbody></table>";
  return html;
};

const convertP3Q1cWellbeingSpendingToHtml = (data, financialYears = {}) => {
  if (!data.q1c_wellbeingSpending) return "";
  const w = data.q1c_wellbeingSpending;
  if (!hasPrintableValue(w.current) && !hasPrintableValue(w.previous)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th></th><th>${p3FyHdr2(L, true)}</th><th>${p3FyHdr2(L, false)}</th></tr></thead><tbody><tr>`;
  html += "<td>Cost incurred on well- being measures as a % of total revenue of the company</td>";
  html += `<td>${formatP2PercentageCell(w.current)}</td>`;
  html += `<td>${formatP2PercentageCell(w.previous)}</td>`;
  html += "</tr></tbody></table>";
  return html;
};

const p3TrainingTableHead = (L) => {
  let html = "<table><thead>";
  html += `<tr><th rowspan="3">Category</th><th colspan="5">${p3FyHdr2(L, true)}</th><th colspan="5">${p3FyHdr2(L, false)}</th></tr>`;
  html += '<tr><th rowspan="2">Total (A)</th><th colspan="2">On Health and safety measures</th><th colspan="2">On Skill upgradation</th>' + '<th rowspan="2">Total (D)</th><th colspan="2">On Health and safety measures</th><th colspan="2">On Skill upgradation</th></tr>';
  html += "<tr><th>No. (B)</th><th>% (B / A)</th><th>No. (C)</th><th>% (C / A)</th><th>No. (E)</th><th>% (E / D)</th><th>No. (F)</th><th>% (F / D)</th></tr>";
  html += "</thead><tbody>";
  return html;
};

const p3PctOrDash = (num, den) => {
  const p = p3CalcPct(num, den);
  return p ? `${p}%` : "-";
};

const convertP3EssentialQ8TrainingToHtml = (principleData, financialYears = {}) => {
  const t = principleData.q8_training;
  if (!t || !hasPrintableValue(t)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = p3TrainingTableHead(L);
  const seg = (label, curE, prevE) => {
    let s = "";
    s += `<tr><td colspan="11" style="background-color:#f0f0f0;font-weight:600;text-align:center;">${label}</td></tr>`;
    ["male", "female"].forEach((g) => {
      const gl = g === "male" ? "Male" : "Female";
      const c = curE[g] || {};
      const p = prevE[g] || {};
      s += `<tr><td style="font-weight:500;">${gl}</td>`;
      s += `<td>${hasPrintableValue(c.total) ? c.total : "-"}</td><td>${hasPrintableValue(c.healthSafety) ? c.healthSafety : "-"}</td><td>${p3PctOrDash(c.healthSafety, c.total)}</td>`;
      s += `<td>${hasPrintableValue(c.skillUpgrade) ? c.skillUpgrade : "-"}</td><td>${p3PctOrDash(c.skillUpgrade, c.total)}</td>`;
      s += `<td>${hasPrintableValue(p.total) ? p.total : "-"}</td><td>${hasPrintableValue(p.healthSafety) ? p.healthSafety : "-"}</td><td>${p3PctOrDash(p.healthSafety, p.total)}</td>`;
      s += `<td>${hasPrintableValue(p.skillUpgrade) ? p.skillUpgrade : "-"}</td><td>${p3PctOrDash(p.skillUpgrade, p.total)}</td></tr>`;
    });
    const m = curE.male || {};
    const f = curE.female || {};
    const pm = prevE.male || {};
    const pf = prevE.female || {};
    const tA = p3Sum(m.total, f.total);
    const tB = p3Sum(m.healthSafety, f.healthSafety);
    const tC = p3Sum(m.skillUpgrade, f.skillUpgrade);
    const tD = p3Sum(pm.total, pf.total);
    const tE = p3Sum(pm.healthSafety, pf.healthSafety);
    const tF = p3Sum(pm.skillUpgrade, pf.skillUpgrade);
    s += `<tr style="font-weight:600;background-color:#f5f5f5;"><td>Total</td>`;
    s += `<td>${tA || "-"}</td><td>${tB || "-"}</td><td>${p3PctOrDash(tB, tA)}</td><td>${tC || "-"}</td><td>${p3PctOrDash(tC, tA)}</td>`;
    s += `<td>${tD || "-"}</td><td>${tE || "-"}</td><td>${p3PctOrDash(tE, tD)}</td><td>${tF || "-"}</td><td>${p3PctOrDash(tF, tD)}</td></tr>`;
    return s;
  };
  html += seg("Employees", t.currentFY?.employees || {}, t.previousFY?.employees || {});
  html += seg("Workers", t.currentFY?.workers || {}, t.previousFY?.workers || {});
  html += "</tbody></table>";
  return html;
};

const convertP3EssentialQ9PerformanceToHtml = (principleData, financialYears = {}) => {
  const t = principleData.q9_performanceReviews;
  if (!t || !hasPrintableValue(t)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th rowspan="2">Category</th><th colspan="3">${p3FyHdr2(L, true)}</th><th colspan="3">${p3FyHdr2(L, false)}</th></tr><tr>`;
  html += "<th>Total (A)</th><th>No. (B)</th><th>% (B / A)</th>";
  html += "<th>Total (C)</th><th>No. (D)</th><th>% (D / C)</th>";
  html += "</tr></thead><tbody>";
  const block = (title, curE, prevE) => {
    let s = `<tr><td colspan="7" style="background-color:#f0f0f0;font-weight:600;text-align:center;">${title}</td></tr>`;
    ["male", "female"].forEach((g) => {
      const gl = g === "male" ? "Male" : "Female";
      const c = curE[g] || {};
      const p = prevE[g] || {};
      s += `<tr><td style="font-weight:500;">${gl}</td>`;
      s += `<td>${hasPrintableValue(c.total) ? c.total : "-"}</td><td>${hasPrintableValue(c.reviewed) ? c.reviewed : "-"}</td><td>${p3PctOrDash(c.reviewed, c.total)}</td>`;
      s += `<td>${hasPrintableValue(p.total) ? p.total : "-"}</td><td>${hasPrintableValue(p.reviewed) ? p.reviewed : "-"}</td><td>${p3PctOrDash(p.reviewed, p.total)}</td></tr>`;
    });
    const m = curE.male || {};
    const f = curE.female || {};
    const pm = prevE.male || {};
    const pf = prevE.female || {};
    const tA = p3Sum(m.total, f.total);
    const tB = p3Sum(m.reviewed, f.reviewed);
    const tC = p3Sum(pm.total, pf.total);
    const tD = p3Sum(pm.reviewed, pf.reviewed);
    s += `<tr style="font-weight:600;background-color:#f5f5f5;"><td>Total</td>`;
    s += `<td>${tA || "-"}</td><td>${tB || "-"}</td><td>${p3PctOrDash(tB, tA)}</td>`;
    s += `<td>${tC || "-"}</td><td>${tD || "-"}</td><td>${p3PctOrDash(tD, tC)}</td></tr>`;
    return s;
  };
  html += block("Employees", t.currentFY?.employees || {}, t.previousFY?.employees || {});
  html += block("Workers", t.currentFY?.workers || {}, t.previousFY?.workers || {});
  html += "</tbody></table>";
  return html;
};

const convertP3EssentialQ11SafetyIncidentsToHtml = (principleData, financialYears = {}) => {
  const si = principleData.q11_safetyIncidents;
  if (!si || !hasPrintableValue(si)) return "";
  const L = buildFyHeaderLabels(financialYears);
  const incidents = [
    { key: "ltifr", label: "Lost Time Injury Frequency Rate (LTIFR) (per one million-person hours worked)" },
    { key: "recordableInjuries", label: "Total recordable work-related injuries" },
    { key: "fatalities", label: "No. of fatalities" },
    { key: "highConsequence", label: "High consequence work-related injury or ill-health (excluding fatalities)" },
  ];
  let html = "<table><thead><tr>";
  html += `<th>Safety Incident / Number</th><th>Category*</th><th>${p3FyHdr2(L, true)}</th><th>${p3FyHdr2(L, false)}</th></tr></thead><tbody>`;
  incidents.forEach(({ key, label }) => {
    const r = si[key] || {};
    html += `<tr><td rowspan="2">${label}</td><td>Employees</td>`;
    html += `<td>${hasPrintableValue(r.employeesCurrent) ? r.employeesCurrent : "-"}</td>`;
    html += `<td>${hasPrintableValue(r.employeesPrevious) ? r.employeesPrevious : "-"}</td></tr>`;
    html += `<tr><td>Workers</td>`;
    html += `<td>${hasPrintableValue(r.workersCurrent) ? r.workersCurrent : "-"}</td>`;
    html += `<td>${hasPrintableValue(r.workersPrevious) ? r.workersPrevious : "-"}</td></tr>`;
  });
  html += "</tbody></table>";
  html += "<p style='font-size:10px;color:#666;margin:4px 0 0 0;'>*Including in the contract workforce</p>";
  return html;
};

const convertP3EssentialQ14AssessmentsToHtml = (principleData) => {
  const a = principleData.q14_assessments;
  if (!a || !hasPrintableValue(a)) return "";
  let html = "<table><thead><tr>";
  html += "<th></th><th>% of your plants and offices that were assessed (by entity or statutory authorities or third parties)</th>";
  html += "</tr></thead><tbody>";
  html += `<tr><td>Health and safety practices</td><td>${formatP2PercentageCell(a.healthSafety)}</td></tr>`;
  html += `<tr><td>Working Conditions</td><td>${formatP2PercentageCell(a.workingConditions)}</td></tr>`;
  html += "</tbody></table>";
  return html;
};

const convertP3Q10HealthSafetyToHtml = (data) => {
  const h = data.q10_healthSafety;
  if (!h || typeof h !== "object") return "";
  const a = h.a_implemented || {};
  const cRaw = h.c_workerReporting;
  const cYes = cRaw === "Yes" || cRaw === "Y";
  const cNo = cRaw === "No" || cRaw === "N";
  const cLine = cYes ? "Yes." : cNo ? "No." : hasPrintableValue(cRaw) ? String(cRaw).trim() : "-";
  const cDetails = cYes && hasPrintableValue(h.c_workerReportingDetails) ? `<br/><br/>${String(h.c_workerReportingDetails).trim()}` : "";
  const dYes = h.d_medicalAccess === "Yes";
  const dLine = hasPrintableValue(h.d_medicalAccess) ? (dYes ? "Yes." : h.d_medicalAccess === "No" ? "No." : String(h.d_medicalAccess).trim()) : "-";
  const dDetails = dYes && hasPrintableValue(h.d_medicalAccessDetails) ? `<br/><br/>${String(h.d_medicalAccessDetails).trim()}` : "";
  const hasAny = hasPrintableValue(a.hasSystem) || hasPrintableValue(a.coverage) || hasPrintableValue(h.b_processes) || hasPrintableValue(h.c_workerReporting) || hasPrintableValue(h.c_workerReportingDetails) || hasPrintableValue(h.d_medicalAccess) || hasPrintableValue(h.d_medicalAccessDetails);
  if (!hasAny) return "";
  let html = '<div class="brsr-pdf-rich-lines">';
  html += `<p><strong>(a) Whether an occupational health and safety management system has been implemented by the entity? (Yes/ No). If yes, the coverage such system?</strong></p>`;
  const aLine = hasPrintableValue(a.hasSystem) || hasPrintableValue(a.coverage) ? `${a.hasSystem || "-"}${hasPrintableValue(a.coverage) ? ` ${String(a.coverage).trim()}` : ""}` : "-";
  html += `<p style="margin-left:0;padding-left:8px;">${aLine}</p>`;
  html += `<p><strong>(b) What are the processes used to identify work-related hazards and assess risks on a routine and non-routine basis by the entity?</strong></p>`;
  html += `<p style="margin-left:0;padding-left:8px;">${hasPrintableValue(h.b_processes) ? String(h.b_processes).trim() : "-"}</p>`;
  html += `<p><strong>(c) Whether you have processes for workers to report the work related hazards and to remove themselves from such risks. (Y/N)</strong></p>`;
  html += `<p style="margin-left:0;padding-left:8px;">${cLine === "-" ? "-" : `${cLine}${cDetails}`}</p>`;
  html += `<p><strong>(d) Do the employees/ worker of the entity have access to non-occupational medical and healthcare services? (Yes/ No)</strong></p>`;
  html += `<p style="margin-left:0;padding-left:8px;">${dLine === "-" ? "-" : `${dLine}${dDetails}`}</p>`;
  html += "</div>";
  return html;
};

const convertP3LeadershipQ1LifeInsuranceToHtml = (data) => {
  if (!data || typeof data !== "object") return "";
  const { employees, workers } = data;
  if (!hasPrintableValue(employees) && !hasPrintableValue(workers)) return "";
  const yn = (v) => (hasPrintableValue(v) ? String(v).trim() : "-");
  let html = '<div class="brsr-pdf-rich-lines">';
  html += `<p><strong>(A) Employees</strong> – ${yn(employees)}</p>`;
  html += `<p><strong>(B) Workers</strong> – ${yn(workers)}</p>`;
  html += "</div>";
  return html;
};

const convertP3LeadershipQ3RehabilitationToHtml = (data, financialYears = {}) => {
  if (!data || typeof data !== "object") return "";
  const { employees, workers } = data;
  const L = buildFyHeaderLabels(financialYears);
  const hasAny = (employees && rowHasPrintableValues(employees)) || (workers && rowHasPrintableValues(workers));
  if (!hasAny) return "";
  const fyC = p3FyHdr2(L, true);
  const fyP = p3FyHdr2(L, false);
  let html = "<table><thead>";
  html += `<tr><th rowspan="2"></th><th colspan="2">Total no of affected employees/workers</th><th colspan="2">No if employees/workers that are rehabilitated or whose family members have been placed in suitable employment</th></tr>`;
  html += `<tr><th>${fyC}</th><th>${fyP}</th><th>${fyC}</th><th>${fyP}</th></tr>`;
  html += "</thead><tbody>";
  html += "<tr>";
  html += "<td>Employees</td>";
  html += `<td>${employees?.currentAffected || "-"}</td><td>${employees?.previousAffected || "-"}</td>`;
  html += `<td>${employees?.currentRehabilitated || "-"}</td><td>${employees?.previousRehabilitated || "-"}</td>`;
  html += "</tr>";
  html += "<tr>";
  html += "<td>Workers</td>";
  html += `<td>${workers?.currentAffected || "-"}</td><td>${workers?.previousAffected || "-"}</td>`;
  html += `<td>${workers?.currentRehabilitated || "-"}</td><td>${workers?.previousRehabilitated || "-"}</td>`;
  html += "</tr>";
  html += "</tbody></table>";
  return html;
};

const convertP3LeadershipQ5ValueChainAssessmentToHtml = (data) => {
  if (!data || typeof data !== "object") return "";
  if (!hasPrintableValue(data.healthSafety) && !hasPrintableValue(data.workingConditions)) return "";
  let html = "<table><thead><tr><th></th>";
  html += "<th>% of value chain partners (by value of business done with such partners) that were assessed</th>";
  html += "</tr></thead><tbody>";
  html += `<tr><td>Health and Safety Practices</td><td>${formatP2PercentageCell(data.healthSafety)}</td></tr>`;
  html += `<tr><td>Working Conditions</td><td>${formatP2PercentageCell(data.workingConditions)}</td></tr>`;
  html += "</tbody></table>";
  return html;
};

const convertP4EssentialQ2StakeholderGroupsToHtml = (data) => {
  if (!Array.isArray(data) || data.length === 0) return "";
  const rows = data.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>Stakeholder group</th>";
  html += "<th>Whether identified as vulnerable & marginalised group (Yes/No)</th>";
  html += "<th>Channels of communication (Email, SMS, Newspaper, Pamphlets, Advertisement, Community Meetings, Notice Board, Website), other</th>";
  html += "<th>Frequency (Annually/ Half yearly/ Quarterly/ other - please specify)</th>";
  html += "<th>Purpose and scope of engagement including key topics and concerns raised during such engagement</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.stakeholderGroup || "-"}</td>`;
    html += `<td>${row.vulnerableMarginalised || "-"}</td>`;
    html += `<td>${row.channels || "-"}</td>`;
    html += `<td>${row.frequency || "-"}</td>`;
    html += `<td>${row.purposeScope || "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP4LeadershipQ2StakeholderConsultationToHtml = (data) => {
  if (!data || typeof data !== "object") return "";
  const { isUsed, details } = data;
  if (!hasPrintableValue(isUsed) && !hasPrintableValue(details)) return "";
  if (isUsed === "Yes") {
    const body = hasPrintableValue(details) ? String(details).trim() : "";
    return body ? `Yes.\n\n${body}` : "Yes.";
  }
  if (isUsed === "No") return "No.";
  if (hasPrintableValue(details)) return String(details).trim();
  return "-";
};

const convertP5Q4FocalPointToHtml = (essential) => {
  const y = essential?.q4_focalPoint;
  const d = essential?.q4_focalPointDetails;
  if (!hasPrintableValue(y) && !hasPrintableValue(d)) return "";
  const prefix = y === "Yes" ? "Yes." : y === "No" ? "No." : "-";
  if (y === "Yes" && hasPrintableValue(d)) return `${prefix}\n\n${String(d).trim()}`;
  return prefix;
};

const convertP5Q9BusinessAgreementsToHtml = (essential) => {
  const y = essential?.q9_businessAgreements;
  const d = essential?.q9_businessAgreementsDetails;
  if (!hasPrintableValue(y) && !hasPrintableValue(d)) return "";
  const prefix = y === "Yes" ? "Yes." : y === "No" ? "No." : "-";
  if (y === "Yes" && hasPrintableValue(d)) return `${prefix}\n\n${String(d).trim()}`;
  return prefix;
};

const p5FyHdrInline = (L, cur) => {
  const y = cur ? L.current : L.previous;
  return y ? `FY ${y}` : cur ? "Current FY" : "Previous FY";
};

const convertP5EssentialQ1HumanRightsTrainingToHtml = (principleData, financialYears = {}) => {
  const t = principleData.q1_humanRightsTraining;
  if (!t || !hasPrintableValue(t)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th rowspan="2">Category</th><th colspan="3">${p5FyHdrInline(L, true)}</th><th colspan="3">${p5FyHdrInline(L, false)}</th></tr><tr>`;
  html += "<th>Total (A)</th><th>No. of employees / workers covered (B)</th><th>% (B/A)</th>";
  html += "<th>Total (C)</th><th>No. of employees / workers covered (D)</th><th>% (D/C)</th>";
  html += "</tr></thead><tbody>";
  const pct = (c, tot) => {
    const p = p3CalcPct(c, tot);
    return p ? `${p}%` : "-";
  };
  const permOtherRows = (curE, prevE) => {
    const curP = curE.permanent || {};
    const curO = curE.otherThanPermanent || {};
    const prevP = prevE.permanent || {};
    const prevO = prevE.otherThanPermanent || {};
    html += `<tr><td>Permanent</td>`;
    html += `<td>${hasPrintableValue(curP.total) ? curP.total : "-"}</td><td>${hasPrintableValue(curP.covered) ? curP.covered : "-"}</td><td>${pct(curP.covered, curP.total)}</td>`;
    html += `<td>${hasPrintableValue(prevP.total) ? prevP.total : "-"}</td><td>${hasPrintableValue(prevP.covered) ? prevP.covered : "-"}</td><td>${pct(prevP.covered, prevP.total)}</td></tr>`;
    html += `<tr><td>Other than permanent</td>`;
    html += `<td>${hasPrintableValue(curO.total) ? curO.total : "-"}</td><td>${hasPrintableValue(curO.covered) ? curO.covered : "-"}</td><td>${pct(curO.covered, curO.total)}</td>`;
    html += `<td>${hasPrintableValue(prevO.total) ? prevO.total : "-"}</td><td>${hasPrintableValue(prevO.covered) ? prevO.covered : "-"}</td><td>${pct(prevO.covered, prevO.total)}</td></tr>`;
  };
  html += `<tr><td colspan="7" style="background-color:#f5f5f5;font-weight:600;">Employees</td></tr>`;
  permOtherRows(t.currentFY?.employees || {}, t.previousFY?.employees || {});
  const ecT = p3Sum(t.currentFY?.employees?.permanent?.total, t.currentFY?.employees?.otherThanPermanent?.total);
  const ecC = p3Sum(t.currentFY?.employees?.permanent?.covered, t.currentFY?.employees?.otherThanPermanent?.covered);
  const epT = p3Sum(t.previousFY?.employees?.permanent?.total, t.previousFY?.employees?.otherThanPermanent?.total);
  const epC = p3Sum(t.previousFY?.employees?.permanent?.covered, t.previousFY?.employees?.otherThanPermanent?.covered);
  html += `<tr style="font-weight:600;"><td>Total Employees</td><td>${ecT || "-"}</td><td>${ecC || "-"}</td><td>${pct(ecC, ecT)}</td><td>${epT || "-"}</td><td>${epC || "-"}</td><td>${pct(epC, epT)}</td></tr>`;
  html += `<tr><td colspan="7" style="background-color:#f5f5f5;font-weight:600;">Workers</td></tr>`;
  permOtherRows(t.currentFY?.workers || {}, t.previousFY?.workers || {});
  const wcT = p3Sum(t.currentFY?.workers?.permanent?.total, t.currentFY?.workers?.otherThanPermanent?.total);
  const wcC = p3Sum(t.currentFY?.workers?.permanent?.covered, t.currentFY?.workers?.otherThanPermanent?.covered);
  const wpT = p3Sum(t.previousFY?.workers?.permanent?.total, t.previousFY?.workers?.otherThanPermanent?.total);
  const wpC = p3Sum(t.previousFY?.workers?.permanent?.covered, t.previousFY?.workers?.otherThanPermanent?.covered);
  html += `<tr style="font-weight:600;"><td>Total Workers</td><td>${wcT || "-"}</td><td>${wcC || "-"}</td><td>${pct(wcC, wcT)}</td><td>${wpT || "-"}</td><td>${wpC || "-"}</td><td>${pct(wpC, wpT)}</td></tr>`;
  html += "</tbody></table>";
  return html;
};

const convertP5EssentialQ2MinimumWagesToHtml = (principleData, financialYears = {}) => {
  const w = principleData.q2_minimumWages;
  if (!w || !hasPrintableValue(w)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th rowspan="2">Category</th><th colspan="3">${p5FyHdrInline(L, true)}</th><th colspan="3">${p5FyHdrInline(L, false)}</th></tr><tr>`;
  html += "<th>Total (A)</th><th>No. (B)</th><th>% (B/A)</th><th>Total (D)</th><th>No. (E)</th><th>% (E/D)</th>";
  html += "</tr></thead><tbody>";
  const pct = p3PctOrDash;
  const block = (title, cat) => {
    const c = cat?.currentFY || {};
    const p = cat?.previousFY || {};
    html += `<tr><td colspan="7" style="background-color:#f5f5f5;font-weight:600;">${title}</td></tr>`;
    html += `<tr><td>Below minimum wages</td><td>${hasPrintableValue(c.total) ? c.total : "-"}</td><td>${hasPrintableValue(c.belowMin) ? c.belowMin : "-"}</td><td>${pct(c.belowMin, c.total)}</td>`;
    html += `<td>${hasPrintableValue(p.total) ? p.total : "-"}</td><td>${hasPrintableValue(p.belowMin) ? p.belowMin : "-"}</td><td>${pct(p.belowMin, p.total)}</td></tr>`;
    html += `<tr><td>At minimum wages</td><td>${hasPrintableValue(c.total) ? c.total : "-"}</td><td>${hasPrintableValue(c.atMin) ? c.atMin : "-"}</td><td>${pct(c.atMin, c.total)}</td>`;
    html += `<td>${hasPrintableValue(p.total) ? p.total : "-"}</td><td>${hasPrintableValue(p.atMin) ? p.atMin : "-"}</td><td>${pct(p.atMin, p.total)}</td></tr>`;
  };
  block("Employees", w.employees);
  block("Workers", w.workers);
  html += "</tbody></table>";
  return html;
};

const convertP5EssentialQ3aMedianRemunerationToHtml = (principleData) => {
  const m = principleData.q3a_medianRemuneration;
  if (!m || !hasPrintableValue(m)) return "";
  const rows = [
    { key: "bod", label: "Board of Directors (BoD)" },
    { key: "kmp", label: "Key Managerial Personnel" },
    { key: "employees", label: "Employees other than BoD and KMP" },
    { key: "workers", label: "Workers" },
  ];
  let html = "<table><thead><tr>";
  html += `<th rowspan="2"></th><th colspan="2">Male</th><th colspan="2">Female</th></tr><tr>`;
  html += "<th>Number</th><th>Median remuneration/ salary/ wages of respective category</th>";
  html += "<th>Number</th><th>Median remuneration/ salary/ wages of respective category</th>";
  html += "</tr></thead><tbody>";
  rows.forEach(({ key, label }) => {
    const r = m[key] || {};
    html += `<tr><td>${label}</td>`;
    html += `<td>${hasPrintableValue(r.maleNumber) ? r.maleNumber : "-"}</td><td>${hasPrintableValue(r.maleMedian) ? r.maleMedian : "-"}</td>`;
    html += `<td>${hasPrintableValue(r.femaleNumber) ? r.femaleNumber : "-"}</td><td>${hasPrintableValue(r.femaleMedian) ? r.femaleMedian : "-"}</td></tr>`;
  });
  html += "</tbody></table>";
  return html;
};

const convertP5EssentialQ3bGrossWagesToHtml = (principleData, financialYears = {}) => {
  const g = principleData.q3b_grossWagesFemales;
  if (!g || (!hasPrintableValue(g.current) && !hasPrintableValue(g.previous))) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th></th><th>${p5FyHdrInline(L, true)}</th><th>${p5FyHdrInline(L, false)}</th></tr></thead><tbody>`;
  html += `<tr><td>Gross wages paid to females as % of total wages</td><td>${formatP2PercentageCell(g.current)}</td><td>${formatP2PercentageCell(g.previous)}</td></tr>`;
  html += "</tbody></table>";
  return html;
};

const convertP5EssentialQ6ComplaintsToHtml = (principleData, financialYears = {}) => {
  const c = principleData.q6_complaints;
  if (!c || !hasPrintableValue(c)) return "";
  const L = buildFyHeaderLabels(financialYears);
  const rows = [
    { key: "sexualHarassment", label: "Sexual Harassment" },
    { key: "discrimination", label: "Discrimination at workplace" },
    { key: "childLabour", label: "Child Labour" },
    { key: "forcedLabour", label: "Forced Labour/Involuntary Labour" },
    { key: "wages", label: "Wages" },
    { key: "otherHumanRights", label: "Other human rights related issues" },
  ];
  let html = "<table><thead><tr>";
  html += `<th rowspan="2"></th><th colspan="3">${p5FyHdrInline(L, true)}</th><th colspan="3">${p5FyHdrInline(L, false)}</th></tr><tr>`;
  html += "<th>Filed during the year</th><th>Pending resolution at the end of year</th><th>Remarks</th>";
  html += "<th>Filed during the year</th><th>Pending resolution at the end of year</th><th>Remarks</th>";
  html += "</tr></thead><tbody>";
  rows.forEach(({ key, label }) => {
    const b = c[key] || {};
    html += `<tr><td>${label}</td>`;
    html += `<td>${hasPrintableValue(b.currentFiled) ? b.currentFiled : "-"}</td><td>${hasPrintableValue(b.currentPending) ? b.currentPending : "-"}</td><td>${hasPrintableValue(b.currentRemarks) ? b.currentRemarks : "-"}</td>`;
    html += `<td>${hasPrintableValue(b.previousFiled) ? b.previousFiled : "-"}</td><td>${hasPrintableValue(b.previousPending) ? b.previousPending : "-"}</td><td>${hasPrintableValue(b.previousRemarks) ? b.previousRemarks : "-"}</td></tr>`;
  });
  html += "</tbody></table>";
  return html;
};

const convertP5EssentialQ7PoshToHtml = (principleData, financialYears = {}) => {
  const p = principleData.q7_poshComplaints;
  if (!p || !hasPrintableValue(p)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th></th><th>${p5FyHdrInline(L, true)}</th><th>${p5FyHdrInline(L, false)}</th></tr></thead><tbody>`;
  html += `<tr><td>Total complaints reported under sexual harassment of women at workplace</td>`;
  html += `<td>${hasPrintableValue(p.totalComplaints?.current) ? p.totalComplaints.current : "-"}</td>`;
  html += `<td>${hasPrintableValue(p.totalComplaints?.previous) ? p.totalComplaints.previous : "-"}</td></tr>`;
  html += `<tr><td>Complaints on POSH as a % of female employees / workers</td>`;
  html += `<td>${formatP2PercentageCell(p.percentageOfFemales?.current)}</td>`;
  html += `<td>${formatP2PercentageCell(p.percentageOfFemales?.previous)}</td></tr>`;
  html += `<tr><td>Complaints on POSH upheld</td>`;
  html += `<td>${formatP2PercentageCell(p.upheld?.current)}</td>`;
  html += `<td>${formatP2PercentageCell(p.upheld?.previous)}</td></tr>`;
  html += "</tbody></table>";
  return html;
};

const convertP5EssentialQ10AssessmentsToHtml = (principleData) => {
  const a = principleData.q10_assessments;
  if (!a || !hasPrintableValue(a)) return "";
  const rows = [
    { key: "childLabour", label: "Child labour" },
    { key: "forcedLabour", label: "Forced/involuntary labour" },
    { key: "sexualHarassment", label: "Sexual harassment" },
    { key: "discrimination", label: "Discrimination at workplace" },
    { key: "wages", label: "Wages" },
    { key: "others", label: "Others – please specify" },
  ];
  let html = "<table><thead><tr>";
  html += "<th></th><th>% of your plants and offices that were assessed (by entity or statutory authorities or third parties)</th>";
  html += "</tr></thead><tbody>";
  rows.forEach(({ key, label }) => {
    html += `<tr><td>${label}</td><td>${formatP2PercentageCell(a[key])}</td></tr>`;
  });
  html += "</tbody></table>";
  return html;
};

const convertP5LeadershipQ4ValueChainToHtml = (leadership) => {
  const a = leadership?.q4_valueChainAssessment;
  if (!a || !hasPrintableValue(a)) return "";
  const rows = [
    { key: "sexualHarassment", label: "Sexual Harassment" },
    { key: "discrimination", label: "Discrimination at workplace" },
    { key: "childLabour", label: "Child Labour" },
    { key: "forcedLabour", label: "Forced Labour/Involuntary Labour" },
    { key: "wages", label: "Wages" },
    { key: "others", label: "Others - please specify" },
  ];
  let html = "<table><thead><tr>";
  html += "<th></th><th>% of value chain partners (by value of business done with such partners) that were assessed</th>";
  html += "</tr></thead><tbody>";
  rows.forEach(({ key, label }) => {
    html += `<tr><td>${label}</td><td>${formatP2PercentageCell(a[key])}</td></tr>`;
  });
  html += "</tbody></table>";
  return html;
};

// Principle 2 Leadership Q1 — exact headers as SectionC.js (no Actions column)
const convertP2LeadershipQ1LcaToHtml = (data) => {
  if (!Array.isArray(data) || data.length === 0) return "";
  const rows = data.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>NIC Code</th>";
  html += "<th>Name of Product/Service</th>";
  html += "<th>% of total turnover contributed</th>";
  html += "<th>Boundary for which the Life Cycle Perspective / Assessment was conducted</th>";
  html += "<th>Whether conducted by independent external agency (Yes/No)</th>";
  html += "<th>Results communicated in public domain (Yes/No). If yes, provide the web-link.</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.nicCode || "-"}</td>`;
    html += `<td>${row.name || "-"}</td>`;
    html += `<td>${formatP2PercentageCell(row.turnoverPercent)}</td>`;
    html += `<td>${row.boundary || "-"}</td>`;
    html += `<td>${row.independentAgency || "-"}</td>`;
    const comm = row.resultsCommunicated || "-";
    const link = hasPrintableValue(row.webLink) ? String(row.webLink).trim() : "";
    html += `<td>${comm}${link ? ` — ${link}` : ""}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 2 Leadership Q2 — SectionC.js: Name / Description / Action taken (no Actions column)
const convertP2LeadershipQ2ConcernsToHtml = (data) => {
  if (!Array.isArray(data) || data.length === 0) return "";
  const rows = data.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Name of Product / Service</th>";
  html += "<th>Description of the risk/ concern</th>";
  html += "<th>Action Taken</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.name || "-"}</td>`;
    html += `<td>${row.description || "-"}</td>`;
    html += `<td>${row.actionTaken || "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 2 Leadership Q3 — rowspan/colspan headers as SectionC.js (no Actions column)
const convertP2LeadershipQ3RecycledToHtml = (data, financialYears = {}) => {
  if (!Array.isArray(data) || data.length === 0) return "";
  const rows = data.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead>";
  html += `<tr><th rowspan="2">Indicate input material</th><th colspan="2">Recycled or re-used input material to total material</th></tr>`;
  html += `<tr><th>${L.parenCurrent}</th><th>${L.parenPrevious}</th></tr>`;
  html += "</thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.material || "-"}</td>`;
    html += `<td>${formatP2PercentageCell(row.current)}</td>`;
    html += `<td>${formatP2PercentageCell(row.previous)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const P2_Q4_RECLAIMED_CATEGORIES = [
  { key: "plastics", label: "Plastics (including packaging)" },
  { key: "ewaste", label: "E-waste" },
  { key: "hazardous", label: "Hazardous waste" },
  { key: "other", label: "Other waste" },
];

// Principle 2 Leadership Q4 — FY group headers + Re-Used / Recycled / Safely Disposed (SectionC.js, no Actions)
const convertP2LeadershipQ4ReclaimedToHtml = (data, financialYears = {}) => {
  if (!data || typeof data !== "object") return "";

  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead>";
  html += `<tr><th rowspan="2"></th><th colspan="3">${L.colCurrentLong}</th><th colspan="3">${L.colPreviousLong}</th></tr>`;
  html += "<tr><th>Re-Used</th><th>Recycled</th><th>Safely Disposed</th><th>Re-Used</th><th>Recycled</th><th>Safely Disposed</th></tr>";
  html += "</thead><tbody>";
  P2_Q4_RECLAIMED_CATEGORIES.forEach(({ key, label }) => {
    const row = data[key] || {};
    html += "<tr>";
    html += `<td>${label}</td>`;
    html += `<td>${row.currentReused || "-"}</td><td>${row.currentRecycled || "-"}</td><td>${row.currentDisposed || "-"}</td>`;
    html += `<td>${row.previousReused || "-"}</td><td>${row.previousRecycled || "-"}</td><td>${row.previousDisposed || "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 2 Leadership Q5 — exact headers as SectionC.js (no Actions column)
const convertP2LeadershipQ5ReclaimedPercentageToHtml = (data) => {
  if (!Array.isArray(data) || data.length === 0) return "";
  const rows = data.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Indicate product category</th>";
  html += "<th>Reclaimed products and their packaging materials as % of total products sold in respective category</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${row.category || "-"}</td>`;
    html += `<td>${formatP2PercentageCell(row.percentage)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 3 Q13: Complaints table (matches SectionC.js — two fixed rows)
const convertP3Q13ComplaintsToHtml = (data, financialYears = {}) => {
  if (!data.q13_complaints || !hasPrintableValue(data.q13_complaints)) return "";

  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead>";
  html += `<tr><th rowspan="2"></th><th colspan="3">${p3FyHdr2(L, true)}</th><th colspan="3">${p3FyHdr2(L, false)}</th></tr>`;
  html += "<tr><th>Filed during the Year</th><th>Pending resolution at the end of year</th><th>Remarks</th>";
  html += "<th>Filed during the Year</th><th>Pending resolution at the end of year</th><th>Remarks</th></tr>";
  html += "</thead><tbody>";

  const row = (label, block) => {
    const b = block || {};
    html += `<tr><td>${label}</td>`;
    html += `<td>${hasPrintableValue(b.currentFiled) ? b.currentFiled : "-"}</td>`;
    html += `<td>${hasPrintableValue(b.currentPending) ? b.currentPending : "-"}</td>`;
    html += `<td>${hasPrintableValue(b.currentRemarks) ? b.currentRemarks : "-"}</td>`;
    html += `<td>${hasPrintableValue(b.previousFiled) ? b.previousFiled : "-"}</td>`;
    html += `<td>${hasPrintableValue(b.previousPending) ? b.previousPending : "-"}</td>`;
    html += `<td>${hasPrintableValue(b.previousRemarks) ? b.previousRemarks : "-"}</td></tr>`;
  };
  row("Working Conditions", data.q13_complaints.workingConditions);
  row("Health & Safety", data.q13_complaints.healthSafety);

  html += "</tbody></table>";
  return html;
};

// Principle 6 Q6: Air emissions table
// Shared helpers for Principle 6 conversions
const p6FyCell = (L, cur) => {
  const y = cur ? L.current : L.previous;
  return y ? `FY ${y}` : cur ? "Current FY" : "Previous FY";
};
const p6Cell = (v) => (hasPrintableValue(v) ? String(v).trim() : "-");
const p6Total = (...vals) => {
  const any = vals.some((v) => hasPrintableValue(v));
  if (!any) return "-";
  const sum = vals.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
  return sum > 0 ? sum.toFixed(2) : "-";
};

const p6ExternalAssessmentBlock = (hasAssessment, agencyName) => {
  if (!hasPrintableValue(hasAssessment)) return "";
  const yn = hasAssessment === "Y" ? "Y" : hasAssessment === "N" ? "N" : String(hasAssessment).trim();
  const agency = hasPrintableValue(agencyName) ? String(agencyName).trim() : "-";
  // Important: Do NOT use `brsr-pdf-rich-lines` here.
  // The PDF renderer prioritizes `brsr-pdf-rich-lines` over `<table>` rendering;
  // when a table+note are combined into one HTML string, it would otherwise
  // skip the table entirely.
  let html = "<div>";
  html += `<p><strong>Note: Indicate if any independent assessment/ evaluation/assurance has been carried out by an external agency? (Y/N)</strong> ${yn}</p>`;
  if (yn === "Y") {
    html += `<p><strong>If yes, name of the external agency:</strong> ${agency}</p>`;
  }
  html += "</div>";
  return html;
};

const convertP6Q1EnergyConsumptionToHtml = (data, financialYears = {}) => {
  const q1 = data.q1_energyConsumption;
  if (!q1 || typeof q1 !== "object") return "";
  const L = buildFyHeaderLabels(financialYears);

  const renewable = q1.renewable || {};
  const nonRenewable = q1.nonRenewable || {};

  const totalRenewableCurrent = p6Total(renewable?.electricity?.current, renewable?.fuel?.current, renewable?.other?.current);
  const totalRenewablePrevious = p6Total(renewable?.electricity?.previous, renewable?.fuel?.previous, renewable?.other?.previous);

  const totalNonRenewableCurrent = p6Total(nonRenewable?.electricity?.current, nonRenewable?.fuel?.current, nonRenewable?.other?.current);
  const totalNonRenewablePrevious = p6Total(nonRenewable?.electricity?.previous, nonRenewable?.fuel?.previous, nonRenewable?.other?.previous);

  const totalEnergyCurrent = p6Total(renewable?.electricity?.current, renewable?.fuel?.current, renewable?.other?.current, nonRenewable?.electricity?.current, nonRenewable?.fuel?.current, nonRenewable?.other?.current);
  const totalEnergyPrevious = p6Total(renewable?.electricity?.previous, renewable?.fuel?.previous, renewable?.other?.previous, nonRenewable?.electricity?.previous, nonRenewable?.fuel?.previous, nonRenewable?.other?.previous);

  const hasExt = hasPrintableValue(q1?.externalAssessment?.hasAssessment);

  // Render the table if there are any inputs; otherwise render only the note if assessment is selected.
  const hasAnyTable = hasPrintableValue(renewable?.electricity?.current) || hasPrintableValue(renewable?.electricity?.previous) || hasPrintableValue(renewable?.fuel?.current) || hasPrintableValue(nonRenewable?.electricity?.current) || hasPrintableValue(q1?.intensity?.perRupeeTurnover?.current) || hasPrintableValue(q1?.intensity?.physicalOutput?.current) || hasPrintableValue(q1?.intensity?.perRupeeTurnoverPPP?.current);

  if (!hasAnyTable && !hasExt) return "";

  let html = "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";

  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">From renewable sources</td></tr>`;
  html += `<tr><td>Total electricity consumption (A)</td><td>${p6Cell(renewable?.electricity?.current)}</td><td>${p6Cell(renewable?.electricity?.previous)}</td></tr>`;
  html += `<tr><td>Total fuel consumption (B)</td><td>${p6Cell(renewable?.fuel?.current)}</td><td>${p6Cell(renewable?.fuel?.previous)}</td></tr>`;
  html += `<tr><td>Energy consumption through other sources (C)</td><td>${p6Cell(renewable?.other?.current)}</td><td>${p6Cell(renewable?.other?.previous)}</td></tr>`;
  html += `<tr><td style="font-weight:600;">Total energy consumed from renewable sources (A+B+C)</td><td style="font-weight:500;">${totalRenewableCurrent}</td><td style="font-weight:500;">${totalRenewablePrevious}</td></tr>`;

  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">From non-renewable sources</td></tr>`;
  html += `<tr><td>Total electricity consumption (D)</td><td>${p6Cell(nonRenewable?.electricity?.current)}</td><td>${p6Cell(nonRenewable?.electricity?.previous)}</td></tr>`;
  html += `<tr><td>Total fuel consumption (E)</td><td>${p6Cell(nonRenewable?.fuel?.current)}</td><td>${p6Cell(nonRenewable?.fuel?.previous)}</td></tr>`;
  html += `<tr><td>Energy consumption through other sources (F)</td><td>${p6Cell(nonRenewable?.other?.current)}</td><td>${p6Cell(nonRenewable?.other?.previous)}</td></tr>`;
  html += `<tr><td style="font-weight:600;">Total energy consumed from non-renewable sources (D+E+F)</td><td style="font-weight:500;">${totalNonRenewableCurrent}</td><td style="font-weight:500;">${totalNonRenewablePrevious}</td></tr>`;
  html += `<tr><td style="font-weight:600;">Total energy consumed (A+B+C+D+E+F)</td><td style="font-weight:500;">${totalEnergyCurrent}</td><td style="font-weight:500;">${totalEnergyPrevious}</td></tr>`;

  // Energy intensity
  html += `<tr><td>Energy intensity per rupee of turnover<br/> (Total energy consumed / Revenue from operations)</td><td>${p6Cell(q1?.intensity?.perRupeeTurnover?.current)}</td><td>${p6Cell(q1?.intensity?.perRupeeTurnover?.previous)}</td></tr>`;
  html += `<tr><td>Energy intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)<br/> (Total energy consumed / Revenue from operations adjusted for PPP)</td><td>${p6Cell(q1?.intensity?.perRupeeTurnoverPPP?.current)}</td><td>${p6Cell(q1?.intensity?.perRupeeTurnoverPPP?.previous)}</td></tr>`;
  html += `<tr><td>Energy intensity in terms of physical output<br/> (Energy intensity optional – the relevant metric may be selected by the entity)</td><td>${p6Cell(q1?.intensity?.physicalOutput?.current)}</td><td>${p6Cell(q1?.intensity?.physicalOutput?.previous)}</td></tr>`;

  html += "</tbody></table>";

  html += p6ExternalAssessmentBlock(q1?.externalAssessment?.hasAssessment, q1?.externalAssessment?.agencyName);
  return html;
};

const convertP6Q3WaterDetailsToHtml = (data, financialYears = {}) => {
  const q3 = data.q3_waterDetails;
  if (!q3 || typeof q3 !== "object") return "";
  const L = buildFyHeaderLabels(financialYears);
  const hasExt = hasPrintableValue(q3?.externalAssessment?.hasAssessment);

  const w = q3.withdrawal || {};
  const i = q3.intensity || {};
  const c = q3.consumption || {};

  const hasAnyTable = hasPrintableValue(c?.current) || hasPrintableValue(c?.previous) || Object.values(w || {}).some((x) => hasPrintableValue(x?.current) || hasPrintableValue(x?.previous)) || hasPrintableValue(i?.perRupeeTurnover?.current) || hasPrintableValue(i?.perRupeeTurnoverPPP?.current) || hasPrintableValue(i?.physicalOutput?.current);

  if (!hasAnyTable && !hasExt) return "";

  let html = "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";

  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">Water withdrawal by source (in kilolitres)</td></tr>`;
  const withdrawalRows = [
    { key: "surfaceWater", label: "(i) Surface water" },
    { key: "groundwater", label: "(ii) Groundwater" },
    { key: "thirdParty", label: "(iii) Third party water" },
    { key: "seawater", label: "(iv) Seawater / desalinated water" },
    { key: "others", label: "(v) Others" },
  ];
  withdrawalRows.forEach((r) => {
    const row = w?.[r.key] || {};
    html += `<tr><td>${r.label}</td><td>${p6Cell(row.current)}</td><td>${p6Cell(row.previous)}</td></tr>`;
  });
  html += `<tr><td style="font-weight:600;">Total volume of water withdrawal (in kilolitres) (i + ii + iii + iv + v)</td><td style="font-weight:500;">${p6Total(w?.surfaceWater?.current, w?.groundwater?.current, w?.thirdParty?.current, w?.seawater?.current, w?.others?.current)}</td><td style="font-weight:500;">${p6Total(w?.surfaceWater?.previous, w?.groundwater?.previous, w?.thirdParty?.previous, w?.seawater?.previous, w?.others?.previous)}</td></tr>`;
  html += `<tr><td style="font-weight:600;">Total volume of water consumption (in kilolitres)</td><td>${p6Cell(c?.current)}</td><td>${p6Cell(c?.previous)}</td></tr>`;

  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">Water intensity</td></tr>`;
  html += `<tr><td>Water intensity per rupee of turnover<br/> (Total water consumption / Revenue from operations)</td><td>${p6Cell(i?.perRupeeTurnover?.current)}</td><td>${p6Cell(i?.perRupeeTurnover?.previous)}</td></tr>`;
  html += `<tr><td>Water intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)<br/> (Total water consumption / Revenue from operations adjusted for PPP)</td><td>${p6Cell(i?.perRupeeTurnoverPPP?.current)}</td><td>${p6Cell(i?.perRupeeTurnoverPPP?.previous)}</td></tr>`;
  html += `<tr><td>Water intensity in terms of physical output<br/> (Water intensity (optional) – the relevant metric may be selected by the entity)</td><td>${p6Cell(i?.physicalOutput?.current)}</td><td>${p6Cell(i?.physicalOutput?.previous)}</td></tr>`;

  html += "</tbody></table>";
  html += p6ExternalAssessmentBlock(q3?.externalAssessment?.hasAssessment, q3?.externalAssessment?.agencyName);
  return html;
};

const convertP6Q4WaterDischargeToHtml = (data, financialYears = {}) => {
  const q4 = data.q4_waterDischarge;
  if (!q4 || typeof q4 !== "object") return "";
  const L = buildFyHeaderLabels(financialYears);
  const hasExt = hasPrintableValue(q4?.externalAssessment?.hasAssessment);

  const hasAnyTable = hasPrintableValue(q4?.surfaceWater?.noTreatment?.current) || hasPrintableValue(q4?.groundwater?.withTreatment?.current) || hasExt;
  if (!hasAnyTable) return "";

  let html = "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";

  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">Water discharge by destination and level of treatment (in kilolitres)</td></tr>`;
  const destinations = [
    { key: "surfaceWater", label: "(i) To Surface water" },
    { key: "groundwater", label: "(ii) To Groundwater" },
    { key: "seawater", label: "(iii) To Seawater" },
    { key: "thirdParties", label: "(iv) Sent to third-parties" },
    { key: "others", label: "(v) Others" },
  ];

  destinations.forEach((d) => {
    const group = q4?.[d.key] || {};
    html += `<tr><td style="font-weight:600;">${d.label}</td><td></td><td></td></tr>`;
    const noT = group?.noTreatment || {};
    const wT = group?.withTreatment || {};

    html += `<tr><td style="padding-left:10px;">- No treatment</td><td>${p6Cell(noT.current)}</td><td>${p6Cell(noT.previous)}</td></tr>`;
    const level = hasPrintableValue(wT?.treatmentLevel) ? String(wT.treatmentLevel).trim() : "";
    const levelTxt = level ? ` (${level})` : "";
    html += `<tr><td style="padding-left:10px;">- With treatment – please specify level of treatment${levelTxt}</td><td>${p6Cell(wT.current)}</td><td>${p6Cell(wT.previous)}</td></tr>`;
  });

  // Total water discharged row
  html += `<tr><td style="font-weight:600;">Total water discharged (in kilolitres)</td><td style="font-weight:500;">${p6Total(
    q4?.surfaceWater?.noTreatment?.current,
    q4?.surfaceWater?.withTreatment?.current,
    q4?.groundwater?.noTreatment?.current,
    q4?.groundwater?.withTreatment?.current,
    q4?.seawater?.noTreatment?.current,
    q4?.seawater?.withTreatment?.current,
    q4?.thirdParties?.noTreatment?.current,
    q4?.thirdParties?.withTreatment?.current,
    q4?.others?.noTreatment?.current,
    q4?.others?.withTreatment?.current,
  )}</td><td style="font-weight:500;">${p6Total(q4?.surfaceWater?.noTreatment?.previous, q4?.surfaceWater?.withTreatment?.previous, q4?.groundwater?.noTreatment?.previous, q4?.groundwater?.withTreatment?.previous, q4?.seawater?.noTreatment?.previous, q4?.seawater?.withTreatment?.previous, q4?.thirdParties?.noTreatment?.previous, q4?.thirdParties?.withTreatment?.previous, q4?.others?.noTreatment?.previous, q4?.others?.withTreatment?.previous)}</td></tr>`;

  html += "</tbody></table>";
  html += p6ExternalAssessmentBlock(q4?.externalAssessment?.hasAssessment, q4?.externalAssessment?.agencyName);
  return html;
};

const convertP6Q7GhgEmissionsToHtml = (data, financialYears = {}) => {
  const q7 = data.q7_ghgEmissions;
  if (!q7 || typeof q7 !== "object") return "";
  const L = buildFyHeaderLabels(financialYears);
  const hasExt = hasPrintableValue(q7?.externalAssessment?.hasAssessment);
  const hasAnyTable = hasPrintableValue(q7?.scope1?.current) || hasPrintableValue(q7?.scope2?.current) || hasPrintableValue(q7?.intensity?.perRupeeTurnover?.current) || hasExt;
  if (!hasAnyTable) return "";

  let html = "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += "<th>Unit</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";

  html += `<tr><td>Total Scope 1 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)</td><td>${p6Cell(q7?.scope1?.unit)}</td><td>${p6Cell(q7?.scope1?.current)}</td><td>${p6Cell(q7?.scope1?.previous)}</td></tr>`;
  html += `<tr><td>Total Scope 2 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)</td><td>${p6Cell(q7?.scope2?.unit)}</td><td>${p6Cell(q7?.scope2?.current)}</td><td>${p6Cell(q7?.scope2?.previous)}</td></tr>`;

  html += `<tr><td colspan="4" style="background-color:#f5f5f5;font-weight:600;text-align:center;">Total Scope 1 and Scope 2 emission intensity</td></tr>`;

  html += `<tr><td colspan="2">Total Scope 1 and Scope 2 emission intensity per rupee of turnover<br/> (Total Scope 1 and Scope 2 GHG emissions / Revenue from operations)</td><td>${p6Cell(q7?.intensity?.perRupeeTurnover?.current)}</td><td>${p6Cell(q7?.intensity?.perRupeeTurnover?.previous)}</td></tr>`;
  html += `<tr><td colspan="2">Total Scope 1 and Scope 2 emission intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)<br/> (Total Scope 1 and Scope 2 GHG emissions / Revenue from operations adjusted for PPP)</td><td>${p6Cell(q7?.intensity?.perRupeeTurnoverPPP?.current)}</td><td>${p6Cell(q7?.intensity?.perRupeeTurnoverPPP?.previous)}</td></tr>`;
  html += `<tr><td colspan="2">Total Scope 1 and Scope 2 emission intensity (optional) – the relevant metric may be selected by the entity</td><td>${p6Cell(q7?.intensity?.physicalOutput?.current)}</td><td>${p6Cell(q7?.intensity?.physicalOutput?.previous)}</td></tr>`;

  html += "</tbody></table>";
  html += p6ExternalAssessmentBlock(q7?.externalAssessment?.hasAssessment, q7?.externalAssessment?.agencyName);
  return html;
};

const convertP6Q9WasteManagementToHtml = (data, financialYears = {}) => {
  const q9 = data.q9_wasteManagement;
  if (!q9 || typeof q9 !== "object") return "";
  const L = buildFyHeaderLabels(financialYears);
  const hasExt = hasPrintableValue(q9?.externalAssessment?.hasAssessment);

  const hasAnyTable = hasPrintableValue(q9?.totalWaste?.plastic?.current) || hasPrintableValue(q9?.recovery?.recycled?.current) || hasPrintableValue(q9?.disposal?.incineration?.current) || hasExt;
  if (!hasAnyTable) return "";

  let html = "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";

  // Total Waste generated
  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">Total Waste generated (in metric tonnes)</td></tr>`;
  const wasteCats = [
    { key: "plastic", label: "Plastic waste (A)" },
    { key: "ewaste", label: "E-waste (B)" },
    { key: "biomedical", label: "Bio-medical waste (C)" },
    { key: "construction", label: "Construction and demolition waste (D)" },
    { key: "battery", label: "Battery waste (E)" },
    { key: "radioactive", label: "Radioactive waste (F)" },
  ];
  wasteCats.forEach((w) => {
    html += `<tr><td>${w.label}</td><td>${p6Cell(q9?.totalWaste?.[w.key]?.current)}</td><td>${p6Cell(q9?.totalWaste?.[w.key]?.previous)}</td></tr>`;
  });
  const otherHaz = q9?.totalWaste?.otherHazardous || {};
  const otherHazSpec = hasPrintableValue(otherHaz.specify) ? ` (Specify: ${String(otherHaz.specify).trim()})` : "";
  html += `<tr><td>Other Hazardous waste. Please specify, if any. (G)${otherHazSpec}</td><td>${p6Cell(otherHaz.current)}</td><td>${p6Cell(otherHaz.previous)}</td></tr>`;
  const otherNonHaz = q9?.totalWaste?.otherNonHazardous || {};
  const otherNonSpec = hasPrintableValue(otherNonHaz.specify) ? ` (Specify: ${String(otherNonHaz.specify).trim()})` : "";
  html += `<tr><td>Other Non-hazardous waste generated (H). Please specify, if any.${otherNonSpec}</td><td>${p6Cell(otherNonHaz.current)}</td><td>${p6Cell(otherNonHaz.previous)}</td></tr>`;

  html += `<tr><td style="font-weight:600;">Total (A+B + C + D + E + F + G + H)</td><td style="font-weight:500;">${p6Total(q9?.totalWaste?.plastic?.current, q9?.totalWaste?.ewaste?.current, q9?.totalWaste?.biomedical?.current, q9?.totalWaste?.construction?.current, q9?.totalWaste?.battery?.current, q9?.totalWaste?.radioactive?.current, q9?.totalWaste?.otherHazardous?.current, q9?.totalWaste?.otherNonHazardous?.current)}</td><td style="font-weight:500;">${p6Total(
    q9?.totalWaste?.plastic?.previous,
    q9?.totalWaste?.ewaste?.previous,
    q9?.totalWaste?.biomedical?.previous,
    q9?.totalWaste?.construction?.previous,
    q9?.totalWaste?.battery?.previous,
    q9?.totalWaste?.radioactive?.previous,
    q9?.totalWaste?.otherHazardous?.previous,
    q9?.totalWaste?.otherNonHazardous?.previous,
  )}</td></tr>`;

  // Waste intensity
  html += `<tr><td>Waste intensity per rupee of turnover<br/> (Total waste generated / Revenue from operations)</td><td>${p6Cell(q9?.intensity?.perRupeeTurnover?.current)}</td><td>${p6Cell(q9?.intensity?.perRupeeTurnover?.previous)}</td></tr>`;
  html += `<tr><td>Waste intensity per rupee of turnover adjusted for Purchasing Power Parity (PPP)<br/> (Total waste generated / Revenue from operations adjusted for PPP)</td><td>${p6Cell(q9?.intensity?.perRupeeTurnoverPPP?.current)}</td><td>${p6Cell(q9?.intensity?.perRupeeTurnoverPPP?.previous)}</td></tr>`;
  html += `<tr><td>Waste intensity in terms of physical output<br/> (Waste intensity (optional) – the relevant metric may be selected by the entity)</td><td>${p6Cell(q9?.intensity?.physicalOutput?.current)}</td><td>${p6Cell(q9?.intensity?.physicalOutput?.previous)}</td></tr>`;

  // Recovery section
  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">For each category of waste generated, total waste recovered through recycling, re-using or other recovery operations (in metric tonnes)</td></tr>`;
  html += `<tr><td>Recycled</td><td>${p6Cell(q9?.recovery?.recycled?.current)}</td><td>${p6Cell(q9?.recovery?.recycled?.previous)}</td></tr>`;
  html += `<tr><td>Re-used</td><td>${p6Cell(q9?.recovery?.reused?.current)}</td><td>${p6Cell(q9?.recovery?.reused?.previous)}</td></tr>`;
  html += `<tr><td>Other recovery operations</td><td>${p6Cell(q9?.recovery?.otherRecovery?.current)}</td><td>${p6Cell(q9?.recovery?.otherRecovery?.previous)}</td></tr>`;
  html += `<tr><td style="font-weight:600;">Total</td><td style="font-weight:500;">${p6Total(q9?.recovery?.recycled?.current, q9?.recovery?.reused?.current, q9?.recovery?.otherRecovery?.current)}</td><td style="font-weight:500;">${p6Total(q9?.recovery?.recycled?.previous, q9?.recovery?.reused?.previous, q9?.recovery?.otherRecovery?.previous)}</td></tr>`;

  // Disposal section
  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">For each category of waste generated, total waste disposed by nature of disposal method (in metric tonnes)</td></tr>`;
  html += `<tr><td>Incineration</td><td>${p6Cell(q9?.disposal?.incineration?.current)}</td><td>${p6Cell(q9?.disposal?.incineration?.previous)}</td></tr>`;
  html += `<tr><td>Landfilling</td><td>${p6Cell(q9?.disposal?.landfilling?.current)}</td><td>${p6Cell(q9?.disposal?.landfilling?.previous)}</td></tr>`;
  html += `<tr><td>Other disposal operations</td><td>${p6Cell(q9?.disposal?.otherDisposal?.current)}</td><td>${p6Cell(q9?.disposal?.otherDisposal?.previous)}</td></tr>`;
  html += `<tr><td style="font-weight:600;">Total</td><td style="font-weight:500;">${p6Total(q9?.disposal?.incineration?.current, q9?.disposal?.landfilling?.current, q9?.disposal?.otherDisposal?.current)}</td><td style="font-weight:500;">${p6Total(q9?.disposal?.incineration?.previous, q9?.disposal?.landfilling?.previous, q9?.disposal?.otherDisposal?.previous)}</td></tr>`;

  html += "</tbody></table>";
  html += p6ExternalAssessmentBlock(q9?.externalAssessment?.hasAssessment, q9?.externalAssessment?.agencyName);
  return html;
};

const convertP6Q11EcoSensitiveToHtml = (data) => {
  const q11 = data.q11_ecologicallySensitive;
  if (!Array.isArray(q11) || q11.length === 0) return "";
  const rows = q11.filter((r) => Object.values(r || {}).some((v) => hasPrintableValue(v)));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Location of operations/offices</th>";
  html += "<th>Type of operations</th>";
  html += "<th>Whether the conditions of environmental approval / clearance are being complied with? (Y/N) If no, the reasons thereof and corrective action taken, if any.</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((r, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${p6Cell(r.location)}</td>`;
    html += `<td>${p6Cell(r.typeOfOperations)}</td>`;
    html += `<td>${p6Cell(r.compliance)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP6Q12EiaToHtml = (data) => {
  const q12 = data.q12_eia;
  if (!Array.isArray(q12) || q12.length === 0) return "";
  const rows = q12.filter((r) => Object.values(r || {}).some((v) => hasPrintableValue(v)));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Name and brief details of project</th>";
  html += "<th>EIA Notification No.</th>";
  html += "<th>Date</th>";
  html += "<th>Whether conducted by independent external agency (Yes / No)</th>";
  html += "<th>Results communicated in public domain (Yes / No)</th>";
  html += "<th>Relevant Web link</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((r) => {
    html += "<tr>";
    html += `<td>${p6Cell(r.projectName)}</td>`;
    html += `<td>${p6Cell(r.eiaNotificationNo)}</td>`;
    html += `<td>${p6Cell(r.date)}</td>`;
    html += `<td>${p6Cell(r.independentAgency)}</td>`;
    html += `<td>${p6Cell(r.publicDomain)}</td>`;
    html += `<td>${p6Cell(r.webLink)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP6Q13EnvironmentalComplianceToHtml = (data) => {
  const q13 = data.q13_environmentalCompliance;
  if (!Array.isArray(q13) || q13.length === 0) return "";
  const rows = q13.filter((r) => Object.values(r || {}).some((v) => hasPrintableValue(v)));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Specify the law / regulation / guidelines which was not complied with</th>";
  html += "<th>Provide details of the non-compliance</th>";
  html += "<th>Any fines / penalties / action taken by regulatory agencies such as pollution control boards or by courts</th>";
  html += "<th>Corrective action taken, if any</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((r, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${p6Cell(r.law)}</td>`;
    html += `<td>${p6Cell(r.nonComplianceDetails)}</td>`;
    html += `<td>${p6Cell(r.finesPenalties)}</td>`;
    html += `<td>${p6Cell(r.correctiveAction)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 6 Leadership Q1 — Water stress: (i) & (ii) text + (iii) tables
const convertP6LeadershipQ1WaterStressToHtml = (data, financialYears = {}) => {
  const q1 = data.q1_waterStress;
  if (!q1 || typeof q1 !== "object") return "";
  const L = buildFyHeaderLabels(financialYears);

  const withdrawal = q1.withdrawal || {};
  const consumption = q1.consumption || {};
  const discharge = q1.discharge || {};

  const hasTable =
    hasPrintableValue(consumption?.current) ||
    Object.values(withdrawal || {}).some((v) => hasPrintableValue(v?.current) || hasPrintableValue(v?.previous)) ||
    Object.values(discharge || {}).some((dest) => {
      const nd = dest?.noTreatment || {};
      const wt = dest?.withTreatment || {};
      return hasPrintableValue(nd?.current) || hasPrintableValue(nd?.previous) || hasPrintableValue(wt?.current) || hasPrintableValue(wt?.previous) || hasPrintableValue(wt?.treatmentLevel);
    });

  const hasText = hasPrintableValue(q1?.areaName) || hasPrintableValue(q1?.natureOfOperations);
  if (!hasText && !hasTable) return "";

  let html = '<div class="brsr-pdf-rich-lines">';
  html += `<p><strong>(i) Name of the area:</strong> ${p6Cell(q1?.areaName)}</p>`;
  html += `<p><strong>(ii) Nature of operations:</strong> ${p6Cell(q1?.natureOfOperations)}</p>`;
  html += "</div>";

  if (!hasTable) return html;

  html += "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";

  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">Water withdrawal by source (in kilolitres)</td></tr>`;
  const withdrawalRows = [
    { key: "surfaceWater", label: "(i) Surface water" },
    { key: "groundwater", label: "(ii) Groundwater" },
    { key: "thirdParty", label: "(iii) Third party water" },
    { key: "seawater", label: "(iv) Seawater / desalinated water" },
    { key: "others", label: "(v) Others" },
  ];
  withdrawalRows.forEach((r) => {
    const row = withdrawal?.[r.key] || {};
    html += `<tr><td>${r.label}</td><td>${p6Cell(row.current)}</td><td>${p6Cell(row.previous)}</td></tr>`;
  });
  html += `<tr><td style="font-weight:600;">Total volume of water withdrawal (in kilolitres)</td><td style="font-weight:500;">${p6Total(withdrawal?.surfaceWater?.current, withdrawal?.groundwater?.current, withdrawal?.thirdParty?.current, withdrawal?.seawater?.current, withdrawal?.others?.current)}</td><td style="font-weight:500;">${p6Total(
    withdrawal?.surfaceWater?.previous,
    withdrawal?.groundwater?.previous,
    withdrawal?.thirdParty?.previous,
    withdrawal?.seawater?.previous,
    withdrawal?.others?.previous,
  )}</td></tr>`;
  html += `<tr><td style="font-weight:600;">Total volume of water consumption (in kilolitres)</td><td>${p6Cell(consumption?.current)}</td><td>${p6Cell(consumption?.previous)}</td></tr>`;

  html += `<tr><td colspan="3" style="background-color:#f5f5f5;font-weight:600;text-align:center;">Water discharge by destination and level of treatment (in kilolitres)</td></tr>`;
  const destinations = [
    { key: "surfaceWater", label: "(i) Into Surface water" },
    { key: "groundwater", label: "(ii) Into Groundwater" },
    { key: "seawater", label: "(iii) Into Seawater" },
    { key: "thirdParties", label: "(iv) Sent to third-parties" },
    { key: "others", label: "(v) Others" },
  ];
  destinations.forEach((d) => {
    const group = discharge?.[d.key] || {};
    const noT = group?.noTreatment || {};
    const wt = group?.withTreatment || {};
    const level = hasPrintableValue(wt?.treatmentLevel) ? String(wt?.treatmentLevel).trim() : "";
    const levelTxt = level ? ` (${level})` : "";
    html += `<tr><td style="font-weight:600;">${d.label}</td><td></td><td></td></tr>`;
    html += `<tr><td style="padding-left:10px;">- No treatment</td><td>${p6Cell(noT?.current)}</td><td>${p6Cell(noT?.previous)}</td></tr>`;
    html += `<tr><td style="padding-left:10px;">- With treatment – please specify level of treatment${levelTxt}</td><td>${p6Cell(wt?.current)}</td><td>${p6Cell(wt?.previous)}</td></tr>`;
  });

  html += `<tr><td style="font-weight:600;">Total water discharged (in kilolitres)</td><td style="font-weight:500;">${p6Total(
    discharge?.surfaceWater?.noTreatment?.current,
    discharge?.surfaceWater?.withTreatment?.current,
    discharge?.groundwater?.noTreatment?.current,
    discharge?.groundwater?.withTreatment?.current,
    discharge?.seawater?.noTreatment?.current,
    discharge?.seawater?.withTreatment?.current,
    discharge?.thirdParties?.noTreatment?.current,
    discharge?.thirdParties?.withTreatment?.current,
    discharge?.others?.noTreatment?.current,
    discharge?.others?.withTreatment?.current,
  )}</td><td style="font-weight:500;">${p6Total(
    discharge?.surfaceWater?.noTreatment?.previous,
    discharge?.surfaceWater?.withTreatment?.previous,
    discharge?.groundwater?.noTreatment?.previous,
    discharge?.groundwater?.withTreatment?.previous,
    discharge?.seawater?.noTreatment?.previous,
    discharge?.seawater?.withTreatment?.previous,
    discharge?.thirdParties?.noTreatment?.previous,
    discharge?.thirdParties?.withTreatment?.previous,
    discharge?.others?.noTreatment?.previous,
    discharge?.others?.withTreatment?.previous,
  )}</td></tr>`;

  html += "</tbody></table>";
  return html;
};

// Principle 6 Leadership Q2 — Scope 3 emissions (+ external assessment note)
const convertP6LeadershipQ2Scope3EmissionsToHtml = (data, financialYears = {}) => {
  const q2 = data.q2_scope3Emissions;
  if (!q2 || typeof q2 !== "object") return "";
  const L = buildFyHeaderLabels(financialYears);

  const hasExt = hasPrintableValue(q2?.externalAssessment?.hasAssessment);
  const hasTable = hasPrintableValue(q2?.totalScope3?.unit) || hasPrintableValue(q2?.totalScope3?.current) || hasPrintableValue(q2?.totalScope3?.previous) || hasPrintableValue(q2?.intensity?.perRupeeTurnover?.current) || hasPrintableValue(q2?.intensity?.perRupeeTurnover?.previous) || hasPrintableValue(q2?.intensity?.optional?.current) || hasPrintableValue(q2?.intensity?.optional?.previous);

  if (!hasTable && !hasExt) return "";

  let html = "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += "<th>Unit</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";

  html += `<tr><td>Total Scope 3 emissions (Break-up of the GHG into CO2, CH4, N2O, HFCs, PFCs, SF6, NF3, if available)</td><td>${p6Cell(q2?.totalScope3?.unit)}</td><td>${p6Cell(q2?.totalScope3?.current)}</td><td>${p6Cell(q2?.totalScope3?.previous)}</td></tr>`;

  html += `<tr><td colspan="2">Total Scope 3 emissions per rupee of turnover</td><td>${p6Cell(q2?.intensity?.perRupeeTurnover?.current)}</td><td>${p6Cell(q2?.intensity?.perRupeeTurnover?.previous)}</td></tr>`;

  html += `<tr><td colspan="2">Total Scope 3 emission intensity (optional) – the relevant metric may be selected by the entity</td><td>${p6Cell(q2?.intensity?.optional?.current)}</td><td>${p6Cell(q2?.intensity?.optional?.previous)}</td></tr>`;

  html += "</tbody></table>";
  html += p6ExternalAssessmentBlock(q2?.externalAssessment?.hasAssessment, q2?.externalAssessment?.agencyName);
  return html;
};

// Principle 6 Leadership Q4 — Resource efficiency initiatives table
const convertP6LeadershipQ4ResourceEfficiencyToHtml = (data) => {
  const q4 = data.q4_resourceEfficiency;
  if (!Array.isArray(q4) || q4.length === 0) return "";
  const rows = q4.filter((r) => Object.values(r || {}).some((v) => hasPrintableValue(v)));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Sr. No</th>";
  html += "<th>Initiative undertaken</th>";
  html += "<th>Details of the initiative (Web-link, if any, may be provided along-with summary)</th>";
  html += "<th>Outcome of the initiative</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((r, idx) => {
    html += "<tr>";
    html += `<td style="text-align:center;">${idx + 1}</td>`;
    html += `<td>${p6Cell(r?.initiative)}</td>`;
    html += `<td>${p6Cell(r?.details)}</td>`;
    html += `<td>${p6Cell(r?.outcome)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 6 Q6: Air emissions table + external assessment note
const convertP6Q6AirEmissionsToHtml = (data, financialYears = {}) => {
  if (!data.q6_airEmissions || !data.q6_airEmissions.length) return "";
  const L = buildFyHeaderLabels(financialYears);

  const ext = data.q6_airEmissionsExternalAssessment || {};
  const hasExt = hasPrintableValue(ext.hasAssessment);

  // If the table values are empty but Y/N is selected, still show the table structure + note.
  const filteredRows = data.q6_airEmissions.filter((row) => rowHasPrintableValues(row));
  const rowsToRender = filteredRows.length > 0 ? filteredRows : hasExt ? data.q6_airEmissions : [];
  if (!rowsToRender.length) return "";

  let html = "<table><thead><tr>";
  html += "<th>Parameter</th>";
  html += "<th>Please specify unit</th>";
  html += `<th>${p6FyCell(L, true)}</th>`;
  html += `<th>${p6FyCell(L, false)}</th>`;
  html += "</tr></thead><tbody>";
  rowsToRender.forEach((row) => {
    const parameterText = row.parameter === "Others - please specify" && row.specify ? `${row.parameter} - ${row.specify}` : row.parameter || "-";
    html += "<tr>";
    html += `<td>${parameterText}</td>`;
    html += `<td>${p6Cell(row.unit)}</td>`;
    html += `<td>${p6Cell(row.current)}</td>`;
    html += `<td>${p6Cell(row.previous)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";

  html += p6ExternalAssessmentBlock(ext.hasAssessment, ext.agencyName);
  return html;
};

// Principle 7 Q1b: Trade chambers table
const convertP7Q1bTradChambersToHtml = (data) => {
  if (!data.q1b_tradeChambers || !data.q1b_tradeChambers.length) return "";
  const rows = data.q1b_tradeChambers.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th style='width:80px;'>S. No.</th>";
  html += "<th>Name of the trade and industry chambers/ associations</th>";
  html += "<th>Reach of trade and industry chambers/ associations (State/ National)</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${row.name || "-"}</td>`;
    html += `<td>${row.reach || "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 7 Essential Q2 — exact table
const convertP7Q2CorrectiveActionToHtml = (data) => {
  if (!Array.isArray(data.q2_correctiveAction) || data.q2_correctiveAction.length === 0) return "";
  const rows = data.q2_correctiveAction.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  let html = "<table><thead><tr>";
  html += "<th>Name of authority</th>";
  html += "<th>Brief of the case</th>";
  html += "<th>Corrective action taken</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${hasPrintableValue(row.authority) ? row.authority : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.brief) ? row.brief : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.correctiveAction) ? row.correctiveAction : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Principle 7 Leadership Q1 — exact table
const convertP7LeadershipQ1PublicPolicyToHtml = (data) => {
  if (!Array.isArray(data.q1_publicPolicy) || data.q1_publicPolicy.length === 0) return "";
  const rows = data.q1_publicPolicy.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";

  const freqText = (row) => {
    if (!hasPrintableValue(row?.frequency)) return "-";
    if (row.frequency === "Others") {
      return hasPrintableValue(row?.frequencyOther) ? `Others - ${row.frequencyOther}` : "Others";
    }
    return row.frequency;
  };

  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Public policy advocated</th>";
  html += "<th>Method resorted for such advocacy</th>";
  html += "<th>Whether information available in public domain? (Yes/No)</th>";
  html += "<th>Frequency of Review by Board (Annually/ Half yearly/ Quarterly / Others - please specify)</th>";
  html += "<th>Web Link, if available</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${hasPrintableValue(row.policy) ? row.policy : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.method) ? row.method : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.publicDomain) ? row.publicDomain : "-"}</td>`;
    html += `<td>${freqText(row)}</td>`;
    html += `<td>${hasPrintableValue(row.webLink) ? row.webLink : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8EssentialQ1SiaToHtml = (data) => {
  if (!Array.isArray(data.q1_sia) || data.q1_sia.length === 0) return "";
  const rows = data.q1_sia.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>Name and brief details of project</th>";
  html += "<th>SIA Notification Number</th>";
  html += "<th>Date of notification</th>";
  html += "<th>Whether conducted by independent external agency (Yes / No)</th>";
  html += "<th>Results communicated in public domain (Yes / No)</th>";
  html += "<th>Relevant Web link</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${hasPrintableValue(row.projectName) ? row.projectName : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.notificationNo) ? row.notificationNo : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.date) ? row.date : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.independentAgency) ? row.independentAgency : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.publicDomain) ? row.publicDomain : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.webLink) ? row.webLink : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8EssentialQ2RrProjectsToHtml = (data) => {
  if (!Array.isArray(data.q2_rrProjects) || data.q2_rrProjects.length === 0) return "";
  const rows = data.q2_rrProjects.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Name of Project for which R&R is ongoing</th>";
  html += "<th>State</th>";
  html += "<th>District</th>";
  html += "<th>No. of Project Affected Families (PAFs)</th>";
  html += "<th>% of PAFs covered by R&R</th>";
  html += "<th>Amounts paid to PAFs in the FY (In INR)</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${hasPrintableValue(row.projectName) ? row.projectName : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.state) ? row.state : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.district) ? row.district : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.pafNumber) ? row.pafNumber : "-"}</td>`;
    html += `<td>${formatP2PercentageCell(row.pafPercentage)}</td>`;
    html += `<td>${hasPrintableValue(row.amountPaid) ? row.amountPaid : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8EssentialQ4InputMaterialToHtml = (data, financialYears = {}) => {
  if (!data.q4_inputMaterial || !hasPrintableValue(data.q4_inputMaterial)) return "";
  const L = buildFyHeaderLabels(financialYears);
  let html = "<table><thead><tr>";
  html += `<th></th><th>FY ${L.current || "Current FY"}</th><th>FY ${L.previous || "Previous FY"}</th>`;
  html += "</tr></thead><tbody>";
  html += "<tr>";
  html += "<td>Directly sourced from MSMEs/ small producers</td>";
  html += `<td>${formatP2PercentageCell(data.q4_inputMaterial?.msme?.current)}</td>`;
  html += `<td>${formatP2PercentageCell(data.q4_inputMaterial?.msme?.previous)}</td>`;
  html += "</tr>";
  html += "<tr>";
  html += "<td>Directly from within India</td>";
  html += `<td>${formatP2PercentageCell(data.q4_inputMaterial?.withinIndia?.current)}</td>`;
  html += `<td>${formatP2PercentageCell(data.q4_inputMaterial?.withinIndia?.previous)}</td>`;
  html += "</tr>";
  html += "</tbody></table>";
  return html;
};

const convertP8EssentialQ5JobCreationToHtml = (data, financialYears = {}) => {
  if (!data.q5_jobCreation || !hasPrintableValue(data.q5_jobCreation)) return "";
  const L = buildFyHeaderLabels(financialYears);
  const locations = [
    { key: "rural", label: "Rural" },
    { key: "semiUrban", label: "Semi-urban" },
    { key: "urban", label: "Urban" },
    { key: "metropolitan", label: "Metropolitan" },
  ];
  let html = "<table><thead><tr>";
  html += `<th>Location</th><th>FY ${L.current || "Current FY"}</th><th>FY ${L.previous || "Previous FY"}</th>`;
  html += "</tr></thead><tbody>";
  locations.forEach(({ key, label }) => {
    html += "<tr>";
    html += `<td>${label}</td>`;
    html += `<td>${formatP2PercentageCell(data.q5_jobCreation?.[key]?.current)}</td>`;
    html += `<td>${formatP2PercentageCell(data.q5_jobCreation?.[key]?.previous)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8LeadershipQ1MitigateToHtml = (data) => {
  if (!Array.isArray(data.q1_mitigateImpacts) || data.q1_mitigateImpacts.length === 0) return "";
  const rows = data.q1_mitigateImpacts.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>Details of negative social impact identified</th>";
  html += "<th>Corrective action taken</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${hasPrintableValue(row.impact) ? row.impact : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.correctiveAction) ? row.correctiveAction : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8LeadershipQ2CsrAspirationsToHtml = (data) => {
  if (!Array.isArray(data.q2_csrAspirations) || data.q2_csrAspirations.length === 0) return "";
  const rows = data.q2_csrAspirations.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>State</th>";
  html += "<th>Aspirational district</th>";
  html += "<th>Amount Spent (In INR)</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${hasPrintableValue(row.state) ? row.state : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.district) ? row.district : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.amountSpent) ? row.amountSpent : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8LeadershipQ4IpBenefitsToHtml = (data) => {
  if (!Array.isArray(data.q4_ipBenefits) || data.q4_ipBenefits.length === 0) return "";
  const rows = data.q4_ipBenefits.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>Intellectual Property based on traditional knowledge</th>";
  html += "<th>Owned / Acquired (Yes/No)</th>";
  html += "<th>Benefit Shared (Yes/No)</th>";
  html += "<th>Basis of calculating benefit share</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${hasPrintableValue(row.ipName) ? row.ipName : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.owned) ? row.owned : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.benefitShared) ? row.benefitShared : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.basisCalculation) ? row.basisCalculation : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8LeadershipQ5IpDisputesToHtml = (data) => {
  if (!Array.isArray(data.q5_ipDisputes) || data.q5_ipDisputes.length === 0) return "";
  const rows = data.q5_ipDisputes.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>Name of authority</th>";
  html += "<th>Brief of the case</th>";
  html += "<th>Corrective action taken</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${hasPrintableValue(row.authority) ? row.authority : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.caseDetails) ? row.caseDetails : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.correctiveAction) ? row.correctiveAction : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP8LeadershipQ6CsrBeneficiariesToHtml = (data) => {
  if (!Array.isArray(data.q6_csrBeneficiaries) || data.q6_csrBeneficiaries.length === 0) return "";
  const rows = data.q6_csrBeneficiaries.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr>";
  html += "<th>S. No.</th>";
  html += "<th>CSR Project</th>";
  html += "<th>No of persons benefitted from CSR projects</th>";
  html += "<th>% of beneficiaries from vulnerable and marginalized groups</th>";
  html += "</tr></thead><tbody>";
  rows.forEach((row, idx) => {
    html += "<tr>";
    html += `<td>${idx + 1}</td>`;
    html += `<td>${hasPrintableValue(row.csrProject) ? row.csrProject : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.personsCount) ? row.personsCount : "-"}</td>`;
    html += `<td>${formatP2PercentageCell(row.vulnerablePercentage)}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP9EssentialQ2ProductInfoTurnoverToHtml = (data) => {
  const t = data.q2_productInfoTurnover;
  if (!t || !hasPrintableValue(t)) return "";
  const rows = [
    { key: "environmentalSocialParameters", label: "Environmental and social parameters relevant to the product" },
    { key: "safeResponsibleUsage", label: "Safe and responsible usage" },
    { key: "recyclingSafeDisposal", label: "Recycling and/ or safe disposal" },
  ];
  let html = "<table><thead><tr><th></th><th>As a percentage to total turnover</th></tr></thead><tbody>";
  rows.forEach(({ key, label }) => {
    html += `<tr><td>${label}</td><td>${formatP2PercentageCell(t[key])}</td></tr>`;
  });
  html += "</tbody></table>";
  return html;
};

const convertP9EssentialQ3ComplaintNumbersToHtml = (data, financialYears = {}) => {
  const q = data.q3_complaintNumbers;
  if (!q || !hasPrintableValue(q)) return "";
  const L = buildFyHeaderLabels(financialYears);
  const rows = [
    { key: "dataPrivacy", label: "Data privacy" },
    { key: "advertising", label: "Advertising" },
    { key: "cyberSecurity", label: "Cyber-security" },
    { key: "deliveryServices", label: "Delivery of essential services" },
    { key: "restrictiveTraces", label: "Restrictive Trade Practices" },
    { key: "unfairTrade", label: "Unfair Trade Practices" },
    { key: "other", label: "Other" },
  ];
  let html = "<table><thead>";
  html += `<tr><th></th><th colspan="3">${L.colCurrent}</th><th colspan="3">${L.colPrevious}</th></tr>`;
  html += "<tr><th></th><th>Received during the year</th><th>Pending resolution at end of year</th><th>Remarks</th><th>Received during the year</th><th>Pending resolution at end of year</th><th>Remarks</th></tr>";
  html += "</thead><tbody>";
  rows.forEach(({ key, label }) => {
    const c = q[key]?.current || {};
    const p = q[key]?.previous || {};
    html += "<tr>";
    html += `<td>${label}</td>`;
    html += `<td>${hasPrintableValue(c.received) ? c.received : "-"}</td>`;
    html += `<td>${hasPrintableValue(c.pending) ? c.pending : "-"}</td>`;
    html += `<td>${hasPrintableValue(c.remarks) ? c.remarks : "-"}</td>`;
    html += `<td>${hasPrintableValue(p.received) ? p.received : "-"}</td>`;
    html += `<td>${hasPrintableValue(p.pending) ? p.pending : "-"}</td>`;
    html += `<td>${hasPrintableValue(p.remarks) ? p.remarks : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

const convertP9EssentialQ4ProductRecallsToHtml = (data) => {
  if (!Array.isArray(data.q4_productRecalls) || data.q4_productRecalls.length === 0) return "";
  const rows = data.q4_productRecalls.filter((row) => rowHasPrintableValues(row));
  if (rows.length === 0) return "";
  let html = "<table><thead><tr><th>Type</th><th>Number</th><th>Reasons for recall</th></tr></thead><tbody>";
  rows.forEach((row) => {
    html += "<tr>";
    html += `<td>${hasPrintableValue(row.type) ? row.type : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.number) ? row.number : "-"}</td>`;
    html += `<td>${hasPrintableValue(row.reasons) ? row.reasons : "-"}</td>`;
    html += "</tr>";
  });
  html += "</tbody></table>";
  return html;
};

// Router function to get the appropriate converter for a specific question
const getQuestionHtml = (principleNum, type, questionKey, principleData, financialYears = {}) => {
  const data = principleData[questionKey];
  if (principleNum === 5 && type === "Essential" && questionKey === "q4_focalPoint") {
    if (!hasPrintableValue(principleData.q4_focalPoint) && !hasPrintableValue(principleData.q4_focalPointDetails)) return "";
  } else if (principleNum === 5 && type === "Essential" && questionKey === "q9_businessAgreements") {
    if (!hasPrintableValue(principleData.q9_businessAgreements) && !hasPrintableValue(principleData.q9_businessAgreementsDetails)) return "";
  } else if (principleNum === 6 && type === "Essential" && questionKey === "q6_airEmissions") {
    // For Q6 air emissions, the Yes/No note lives in q6_airEmissionsExternalAssessment
    // (separate from q6_airEmissions table rows). Don't skip the converter when the
    // table is empty but the external assessment selection exists.
    const ext = principleData?.q6_airEmissionsExternalAssessment;
    if (!hasPrintableValue(ext?.hasAssessment) && (!data || !hasMeaningfulContent(data))) return "";
  } else if (!data || !hasMeaningfulContent(data)) return "";

  // Special converters for specific questions
  if (principleNum === 1 && type === "Essential") {
    if (questionKey === "q1_training") return convertP1Q1TrainingToHtml(principleData);
    if (questionKey === "q2_fines") return convertP1Q2FinesToHtml(principleData);
    if (questionKey === "q3_appeals") return convertP1Q3AppealsToHtml(principleData);
    // Principle 1 Essential: route known "table" questions to headered table converters.
    // Without this, the generic nested-object converter omits <thead>, which causes
    // the PDF table headers to not render properly.
    if (questionKey === "q5_disciplinary") return convertP1Q5DisciplinaryToHtml(principleData, financialYears);
    if (questionKey === "q6_conflict") return convertP1Q6ConflictToHtml(principleData, financialYears);
    if (questionKey === "q8_accountsPayable") return convertP1Q8AccountsPayableToHtml(principleData, financialYears);
    if (questionKey === "q9_openness") return convertP1Q9OpennessToHtml(principleData, financialYears);
  }

  if (principleNum === 1 && type === "Leadership") {
    if (questionKey === "q1_valueChainAwareness") return convertP1LeadershipQ1ValueChainToHtml(data);
  }

  if (principleNum === 2 && type === "Leadership") {
    if (questionKey === "q1_lca") return convertP2LeadershipQ1LcaToHtml(data);
    if (questionKey === "q2_concerns") return convertP2LeadershipQ2ConcernsToHtml(data);
    if (questionKey === "q3_recycledMaterial") return convertP2LeadershipQ3RecycledToHtml(data, financialYears);
    if (questionKey === "q4_reclaimed") return convertP2LeadershipQ4ReclaimedToHtml(data, financialYears);
    if (questionKey === "q5_reclaimedPercentage") return convertP2LeadershipQ5ReclaimedPercentageToHtml(data);
  }

  if (principleNum === 2 && type === "Essential") {
    // These fields are nested objects and the generic converter produces tables without <thead>,
    // causing missing headers in PDF.
    if (questionKey === "q1_rdCapex") return convertP2Q1RdCapexToHtml(principleData, financialYears);
    if (questionKey === "q3_reclaimProcesses") return convertP2Q3ReclaimProcessesToHtml(principleData);
  }

  if (principleNum === 3 && type === "Essential") {
    if (questionKey === "q1a_employeeWellbeing") return convertP3Q1aEmployeeWellbeingToHtml(principleData);
    if (questionKey === "q1b_workerWellbeing") return convertP3Q1bWorkerWellbeingToHtml(principleData);
    if (questionKey === "q1c_wellbeingSpending") return convertP3Q1cWellbeingSpendingToHtml(principleData, financialYears);
    if (questionKey === "q2_retirementBenefits") return convertP3EssentialQ2RetirementToHtml(principleData, financialYears);
    if (questionKey === "q5_returnRetention") return convertP3EssentialQ5ReturnRetentionToHtml(principleData);
    if (questionKey === "q6_grievance") return convertP3EssentialQ6GrievanceToHtml(principleData);
    if (questionKey === "q7_membership") return convertP3EssentialQ7MembershipToHtml(principleData, financialYears);
    if (questionKey === "q8_training") return convertP3EssentialQ8TrainingToHtml(principleData, financialYears);
    if (questionKey === "q9_performanceReviews") return convertP3EssentialQ9PerformanceToHtml(principleData, financialYears);
    if (questionKey === "q10_healthSafety") return convertP3Q10HealthSafetyToHtml(principleData);
    if (questionKey === "q11_safetyIncidents") return convertP3EssentialQ11SafetyIncidentsToHtml(principleData, financialYears);
    if (questionKey === "q13_complaints") return convertP3Q13ComplaintsToHtml(principleData, financialYears);
    if (questionKey === "q14_assessments") return convertP3EssentialQ14AssessmentsToHtml(principleData);
  }

  if (principleNum === 3 && type === "Leadership") {
    if (questionKey === "q1_lifeInsurance") return convertP3LeadershipQ1LifeInsuranceToHtml(data);
    if (questionKey === "q3_rehabilitation") return convertP3LeadershipQ3RehabilitationToHtml(data, financialYears);
    if (questionKey === "q5_valueChainAssessment") return convertP3LeadershipQ5ValueChainAssessmentToHtml(data);
  }

  if (principleNum === 4 && type === "Essential") {
    if (questionKey === "q2_stakeholderGroups") return convertP4EssentialQ2StakeholderGroupsToHtml(data);
  }

  if (principleNum === 4 && type === "Leadership") {
    if (questionKey === "q2_stakeholderConsultation") return convertP4LeadershipQ2StakeholderConsultationToHtml(data);
  }

  if (principleNum === 5 && type === "Essential") {
    if (questionKey === "q1_humanRightsTraining") return convertP5EssentialQ1HumanRightsTrainingToHtml(principleData, financialYears);
    if (questionKey === "q2_minimumWages") return convertP5EssentialQ2MinimumWagesToHtml(principleData, financialYears);
    if (questionKey === "q3a_medianRemuneration") return convertP5EssentialQ3aMedianRemunerationToHtml(principleData);
    if (questionKey === "q3b_grossWagesFemales") return convertP5EssentialQ3bGrossWagesToHtml(principleData, financialYears);
    if (questionKey === "q4_focalPoint") return convertP5Q4FocalPointToHtml(principleData);
    if (questionKey === "q6_complaints") return convertP5EssentialQ6ComplaintsToHtml(principleData, financialYears);
    if (questionKey === "q7_poshComplaints") return convertP5EssentialQ7PoshToHtml(principleData, financialYears);
    if (questionKey === "q9_businessAgreements") return convertP5Q9BusinessAgreementsToHtml(principleData);
    if (questionKey === "q10_assessments") return convertP5EssentialQ10AssessmentsToHtml(principleData);
  }

  if (principleNum === 5 && type === "Leadership") {
    if (questionKey === "q4_valueChainAssessment") return convertP5LeadershipQ4ValueChainToHtml(principleData);
  }

  if (principleNum === 6 && type === "Essential") {
    if (questionKey === "q1_energyConsumption") return convertP6Q1EnergyConsumptionToHtml(principleData, financialYears);
    if (questionKey === "q3_waterDetails") return convertP6Q3WaterDetailsToHtml(principleData, financialYears);
    if (questionKey === "q4_waterDischarge") return convertP6Q4WaterDischargeToHtml(principleData, financialYears);
    if (questionKey === "q6_airEmissions") return convertP6Q6AirEmissionsToHtml(principleData, financialYears);
    if (questionKey === "q7_ghgEmissions") return convertP6Q7GhgEmissionsToHtml(principleData, financialYears);
    if (questionKey === "q9_wasteManagement") return convertP6Q9WasteManagementToHtml(principleData, financialYears);
    if (questionKey === "q11_ecologicallySensitive") return convertP6Q11EcoSensitiveToHtml(principleData, financialYears);
    if (questionKey === "q12_eia") return convertP6Q12EiaToHtml(principleData, financialYears);
    if (questionKey === "q13_environmentalCompliance") return convertP6Q13EnvironmentalComplianceToHtml(principleData, financialYears);
  }
  if (principleNum === 6 && type === "Leadership") {
    if (questionKey === "q1_waterStress") return convertP6LeadershipQ1WaterStressToHtml(principleData, financialYears);
    if (questionKey === "q2_scope3Emissions") return convertP6LeadershipQ2Scope3EmissionsToHtml(principleData, financialYears);
    if (questionKey === "q4_resourceEfficiency") return convertP6LeadershipQ4ResourceEfficiencyToHtml(principleData, financialYears);
  }

  if (principleNum === 7 && type === "Essential") {
    if (questionKey === "q1b_tradeChambers") return convertP7Q1bTradChambersToHtml(principleData);
    if (questionKey === "q2_correctiveAction") return convertP7Q2CorrectiveActionToHtml(principleData);
  }

  if (principleNum === 7 && type === "Leadership") {
    if (questionKey === "q1_publicPolicy") return convertP7LeadershipQ1PublicPolicyToHtml(principleData);
  }

  if (principleNum === 8 && type === "Essential") {
    if (questionKey === "q1_sia") return convertP8EssentialQ1SiaToHtml(principleData);
    if (questionKey === "q2_rrProjects") return convertP8EssentialQ2RrProjectsToHtml(principleData);
    if (questionKey === "q4_inputMaterial") return convertP8EssentialQ4InputMaterialToHtml(principleData, financialYears);
    if (questionKey === "q5_jobCreation") return convertP8EssentialQ5JobCreationToHtml(principleData, financialYears);
  }

  if (principleNum === 8 && type === "Leadership") {
    if (questionKey === "q1_mitigateImpacts") return convertP8LeadershipQ1MitigateToHtml(principleData);
    if (questionKey === "q2_csrAspirations") return convertP8LeadershipQ2CsrAspirationsToHtml(principleData);
    if (questionKey === "q4_ipBenefits") return convertP8LeadershipQ4IpBenefitsToHtml(principleData);
    if (questionKey === "q5_ipDisputes") return convertP8LeadershipQ5IpDisputesToHtml(principleData);
    if (questionKey === "q6_csrBeneficiaries") return convertP8LeadershipQ6CsrBeneficiariesToHtml(principleData);
  }

  if (principleNum === 9 && type === "Essential") {
    if (questionKey === "q2_productInfoTurnover") return convertP9EssentialQ2ProductInfoTurnoverToHtml(principleData);
    if (questionKey === "q3_complaintNumbers") return convertP9EssentialQ3ComplaintNumbersToHtml(principleData, financialYears);
    if (questionKey === "q4_productRecalls") return convertP9EssentialQ4ProductRecallsToHtml(principleData);
  }

  // Default converter for other questions
  return convertSingleQuestionToHtml(principleNum, type, questionKey, data, financialYears);
};

// ============================================================================
// Updated updateStep4WithSectionC - Creates per-question entries
// ============================================================================

// Update step4 with Section C data for PDF generation
export const updateStep4WithSectionC = (reportId, sectionData) => {
  try {
    const existingStep4 = JSON.parse(localStorage.getItem("step4") || "[]");

    // Remove old Section C entries
    const filtered = Array.isArray(existingStep4) ? existingStep4.filter((item) => item && item.module !== "Section C") : [];

    const entries = [];

    // Principle labels
    const principleLabels = {
      1: "Businesses should conduct and govern themselves with integrity, and in a manner that is Ethical, Transparent and Accountable.",
      2: "Businesses should provide goods and services in a manner that is sustainable and safe",
      3: "Businesses should respect and promote the well-being of all employees, including those in their value chains",
      4: "Businesses should respect the interests of and be responsive to all its stakeholders",
      5: "Businesses should respect and promote human rights",
      6: "Businesses should respect and make efforts to protect and restore the environment",
      7: "Businesses, when engaging in influencing public and regulatory policy, should do so in a manner that is responsible and transparent",
      8: "Businesses should promote inclusive growth and equitable development",
      9: "Businesses should engage with and provide value to their consumers in a responsible manner",
    };

    const sectionCFY = { ...deriveExpandedFinancialYearLabelsFromStep1(), ...getSectionCFinancialYearsForPdf(sectionData) };

    // For each principle P1-P9, process Essential and Leadership questions individually
    for (let p = 1; p <= 9; p++) {
      const essentialKey = `principle${p}Essential`;
      const leadershipKey = `principle${p}Leadership`;
      const principleTitle = `Principle ${p}: ${principleLabels[p]}`;
      const questionMaps = SECTION_C_QUESTION_MAPS[p];

      // Process Essential questions
      if (sectionData[essentialKey] && questionMaps && questionMaps.essential) {
        // Iterate through each question key in the principle data
        Object.keys(sectionData[essentialKey]).forEach((qKey) => {
          const qData = sectionData[essentialKey][qKey];
          const ess5 = sectionData[essentialKey];
          if (qKey === "q4_focalPointDetails" || qKey === "q9_businessAgreementsDetails") return;
          if (p === 5 && qKey === "q4_focalPoint") {
            if (!hasPrintableValue(ess5.q4_focalPoint) && !hasPrintableValue(ess5.q4_focalPointDetails)) return;
          } else if (p === 5 && qKey === "q9_businessAgreements") {
            if (!hasPrintableValue(ess5.q9_businessAgreements) && !hasPrintableValue(ess5.q9_businessAgreementsDetails)) return;
          } else if (!hasMeaningfulContent(qData)) return;

          // Get the question text from the mapping
          const questionText = questionMaps.essential[qKey];
          if (!questionText) return; // Skip if no question mapping

          // Principle 2 Essential Q2: split 2a (Yes/No) and 2b (%) into two PDF rows (matches SectionC.js)
          if (p === 2 && qKey === "q2_sustainableSourcing") {
            const d = qData;
            const ans2a = d.hasProcedures === "Yes" || d.hasProcedures === "No" ? d.hasProcedures : "-";
            let ans2b = "-";
            if (d.hasProcedures === "Yes") {
              ans2b = formatP2PercentageCell(d.percentage);
            }
            entries.push({
              id: "SectionC-P2-Essential-q2a_sustainableSourcing",
              module: "Section C",
              title: principleTitle,
              questionType: "Essential Indicators",
              question: questionText,
              answer: ans2a,
            });
            entries.push({
              id: "SectionC-P2-Essential-q2b_sustainableSourcing",
              module: "Section C",
              title: principleTitle,
              questionType: "Essential Indicators",
              question: "2b. If yes, what percentage of inputs were sourced sustainably?",
              answer: ans2b,
            });
            return;
          }

          // Get the HTML for this question
          const answerHtml = getQuestionHtml(p, "Essential", qKey, sectionData[essentialKey], sectionCFY);
          if (!answerHtml) return;

          entries.push({
            id: `SectionC-P${p}-Essential-${qKey}`,
            module: "Section C",
            title: principleTitle,
            questionType: "Essential Indicators",
            question: questionText,
            answer: answerHtml,
          });
        });
      }

      // Process Leadership questions
      if (sectionData[leadershipKey] && questionMaps && questionMaps.leadership) {
        // Iterate through each question key in the principle data
        Object.keys(sectionData[leadershipKey]).forEach((qKey) => {
          const qData = sectionData[leadershipKey][qKey];
          if (!hasMeaningfulContent(qData)) return;

          // Get the question text from the mapping
          const questionText = questionMaps.leadership[qKey];
          if (!questionText) return; // Skip if no question mapping

          // Get the HTML for this question
          const answerHtml = getQuestionHtml(p, "Leadership", qKey, sectionData[leadershipKey], sectionCFY);
          if (!answerHtml) return;

          entries.push({
            id: `SectionC-P${p}-Leadership-${qKey}`,
            module: "Section C",
            title: principleTitle,
            questionType: "Leadership Indicators",
            question: questionText,
            answer: answerHtml,
          });
        });
      }
    }

    // Save updated step4
    localStorage.setItem("step4", JSON.stringify([...filtered, ...entries]));

    return entries;
  } catch (e) {
    console.error("Failed to update step4 with Section C:", e);
    return [];
  }
};

// Update step4 with Section A data for PDF generation
export const updateStep4WithSectionA = (reportId, sectionData) => {
  try {
    const existingStep4 = JSON.parse(localStorage.getItem("step4") || "[]");

    // Remove old Section A entries
    const filtered = Array.isArray(existingStep4) ? existingStep4.filter((item) => item && item.module !== "Section A") : [];

    // Add new Section A entries
    const newEntries = [];

    const sectionAFY = { ...deriveExpandedFinancialYearLabelsFromStep1(), ...(sectionData.financialYears || {}) };

    // 1. Company Details
    if (sectionData.formData) {
      const companyDetailsHtml = convertCompanyDetailsToHtml(sectionData.formData);
      if (companyDetailsHtml) {
        newEntries.push({
          id: "SectionA-CompanyDetails",
          module: "Section A",
          title: "I. Details of the listed entity",
          question: "",
          answer: companyDetailsHtml,
        });
      }
    }

    // 2. Business Activities
    if (sectionData.businessActivities && sectionData.businessActivities.length > 0) {
      const hasData = sectionData.businessActivities.some((row) => row.mainActivity || row.businessActivity || row.turnoverPercent);
      if (hasData) {
        newEntries.push({
          id: "SectionA-BusinessActivities",
          module: "Section A",
          title: "II. Products/Services",
          question: "16. Details of business activities (accounting for 90% of the turnover)",
          answer: convertBusinessActivitiesToHtml(sectionData.businessActivities),
        });
      }
    }

    // 3. Products/Services
    if (sectionData.productsServices && sectionData.productsServices.length > 0) {
      const hasData = sectionData.productsServices.some((row) => row.productService || row.nicCode || row.turnoverPercent);
      if (hasData) {
        newEntries.push({
          id: "SectionA-ProductsServices",
          module: "Section A",
          title: "II. Products/Services",
          question: "17. Products/Services sold by the entity",
          answer: convertProductsServicesToHtml(sectionData.productsServices),
        });
      }
    }

    const fp = (v) => v !== undefined && v !== null && String(v).trim() !== "";

    // 4. Operations/Locations (Q18 + Q19 a/b/c — include 0 and auto-populated site values)
    if (sectionData.operationsData) {
      const ops = sectionData.operationsData;
      const hasQ18 = fp(ops.plantsNational) || fp(ops.plantsInternational) || fp(ops.officesNational) || fp(ops.officesInternational) || ops._locationsAutoPopulated === true;
      if (hasQ18) {
        newEntries.push({
          id: "SectionA-Operations",
          module: "Section A",
          title: "III. Operations",
          question: "18. Number of locations where plants and/or operations/offices of the entity are situated:",
          answer: convertOperationsToHtml(sectionData.operationsData),
        });
      }

      const marketsHtml = convertMarketsServedToHtml(ops);
      if (marketsHtml) {
        newEntries.push({
          id: "SectionA-MarketsServed-19a",
          module: "Section A",
          title: "III. Operations",
          question: "19. a. Number of locations",
          questionStem: "Markets served by the entity",
          answer: marketsHtml,
        });
      }

      if (fp(ops.exportsPercentage)) {
        newEntries.push({
          id: "SectionA-Exports",
          module: "Section A",
          title: "III. Operations",
          question: "19. b. What is the contribution of exports as a percentage of the total turnover of the entity?",
          questionStem: "Markets served by the entity",
          answer: `${ops.exportsPercentage}%`,
        });
      }

      if (fp(ops.customerTypes)) {
        newEntries.push({
          id: "SectionA-CustomerTypes",
          module: "Section A",
          title: "III. Operations",
          question: "19. c. A brief on types of customers",
          questionStem: "Markets served by the entity",
          answer: ops.customerTypes,
        });
      }
    }

    // 5. Employees
    if (sectionData.employeesData) {
      const emp = sectionData.employeesData;
      const hasEmployeesData = fp(emp.empPermanentTotal) || fp(emp.empPermanentMale) || fp(emp.empPermanentFemale) || fp(emp.empOtherTotal) || fp(emp.empOtherMale) || fp(emp.empOtherFemale) || fp(emp.workerPermanentTotal) || fp(emp.workerPermanentMale) || fp(emp.workerPermanentFemale) || fp(emp.workerOtherTotal) || fp(emp.workerOtherMale) || fp(emp.workerOtherFemale);
      const diffAbledKeys = ["diffAbledEmpPermanentTotal", "diffAbledEmpPermanentMale", "diffAbledEmpPermanentFemale", "diffAbledEmpOtherTotal", "diffAbledEmpOtherMale", "diffAbledEmpOtherFemale", "diffAbledWorkerPermanentTotal", "diffAbledWorkerPermanentMale", "diffAbledWorkerPermanentFemale", "diffAbledWorkerOtherTotal", "diffAbledWorkerOtherMale", "diffAbledWorkerOtherFemale"];
      const hasDiffAbled = diffAbledKeys.some((k) => fp(emp[k]));
      const hasWomenData = fp(emp.womenBoardTotal) || fp(emp.womenBoardCount) || fp(emp.womenSeniorTotal) || fp(emp.womenSeniorCount) || fp(emp.womenMiddleTotal) || fp(emp.womenMiddleCount) || fp(emp.womenJuniorTotal) || fp(emp.womenJuniorCount);
      const hasTurnoverData = fp(emp.turnoverEmpCurrentMale) || fp(emp.turnoverEmpCurrentFemale) || fp(emp.turnoverWorkerCurrentMale) || fp(emp.turnoverWorkerCurrentFemale) || fp(emp.turnoverEmpPreviousMale) || fp(emp.turnoverEmpPreviousFemale) || fp(emp.turnoverWorkerPreviousMale) || fp(emp.turnoverWorkerPreviousFemale) || fp(emp.turnoverEmpPriorMale) || fp(emp.turnoverEmpPriorFemale) || fp(emp.turnoverWorkerPriorMale) || fp(emp.turnoverWorkerPriorFemale);
      const empQuestionStem = "Details as at the end of Financial Year (for the reporting year), in the following format:";

      if (hasEmployeesData) {
        newEntries.push({
          id: "SectionA-Employees",
          module: "Section A",
          title: "IV. Employees",
          question: "20. a. Employees and workers (including differently abled)",
          questionStem: empQuestionStem,
          answer: convertEmployeesToHtml(sectionData.employeesData),
        });
      }

      if (hasDiffAbled) {
        newEntries.push({
          id: "SectionA-DifferentlyAbled",
          module: "Section A",
          title: "IV. Employees",
          question: "20. b. Differently abled Employees and workers",
          questionStem: empQuestionStem,
          answer: convertDifferentlyAbledToHtml(sectionData.employeesData),
        });
      }

      if (hasWomenData) {
        newEntries.push({
          id: "SectionA-WomenParticipation",
          module: "Section A",
          title: "IV. Employees",
          question: "21. Participation/ inclusion/ representation of women",
          answer: convertWomenParticipationToHtml(sectionData.employeesData),
        });
      }

      if (hasTurnoverData) {
        newEntries.push({
          id: "SectionA-TurnoverRate",
          module: "Section A",
          title: "IV. Employees",
          question: "22. Turnover rate for permanent employees and workers",
          answer: convertTurnoverRateToHtml(sectionData.employeesData, sectionAFY),
        });
      }
    }

    // 6. Holding Companies
    if (sectionData.holdingCompanies && sectionData.holdingCompanies.length > 0) {
      const hasData = sectionData.holdingCompanies.some((row) => row.name || row.type || row.sharePercentage);
      if (hasData) {
        newEntries.push({
          id: "SectionA-HoldingCompanies",
          module: "Section A",
          title: "V. Holding, Subsidiary and Associate Companies",
          question: "23. Names of holding/subsidiary/associate companies",
          answer: convertHoldingCompaniesToHtml(sectionData.holdingCompanies),
        });
      }
    }

    // 7. CSR Data — one main number (24.) with (i)/(ii)/(iii) sub-parts for PDF layout
    if (sectionData.csrData) {
      const csr = sectionData.csrData;
      const csrStem = "Details of Corporate Social Responsibility (CSR):";
      const csrTitle = "VI. CSR Details";
      if (csr.csrApplicable) {
        newEntries.push({
          id: "SectionA-CSR-i",
          module: "Section A",
          title: csrTitle,
          question: "24. (i) Whether CSR is applicable as per Section 135 of the Companies Act, 2013 (Yes / No):",
          questionStem: csrStem,
          answer: formatCsrApplicableAnswerForPdf(csr),
        });
      }
      if (csr.turnover) {
        newEntries.push({
          id: "SectionA-CSR-ii",
          module: "Section A",
          title: csrTitle,
          question: "24. (ii) Turnover (in Rs.):",
          questionStem: csrStem,
          answer: `Rs. ${String(csr.turnover).trim()}`,
        });
      }
      if (csr.netWorth) {
        newEntries.push({
          id: "SectionA-CSR-iii",
          module: "Section A",
          title: csrTitle,
          question: "24. (iii) Net worth (in Rs.):",
          questionStem: csrStem,
          answer: `Rs. ${String(csr.netWorth).trim()}`,
        });
      }
    }

    const transparencyTitle = "VII. Transparency and Disclosures Compliances";

    // 8. Complaints/Grievances (25 — before 26 in PDF; same section title as Material)
    if (sectionData.complaintsData) {
      const hasComplaintsData = Object.values(sectionData.complaintsData).some((row) => row && Object.values(row).some((v) => fp(v)));
      if (hasComplaintsData) {
        newEntries.push({
          id: "SectionA-Complaints",
          module: "Section A",
          title: transparencyTitle,
          question: "25. Complaints/Grievances on any of the principles (Principles 1 to 9) under the National Guidelines on Responsible Business Conduct:",
          answer: convertComplaintsDataToHtml(sectionData.complaintsData, sectionAFY),
        });
      }
    }

    // 9. Material issues (26 — under same VII as complaints in the form)
    if (sectionData.materialIssues && sectionData.materialIssues.length > 0) {
      const hasMaterialData = sectionData.materialIssues.some((row) => row && (fp(row.issue) || fp(row.type) || fp(row.rationale) || fp(row.approach) || fp(row.financialImplications)));
      if (hasMaterialData) {
        newEntries.push({
          id: "SectionA-MaterialIssues",
          module: "Section A",
          title: transparencyTitle,
          question: "26. Overview of the entity's material responsible business conduct issues",
          answer: convertMaterialIssuesToHtml(sectionData.materialIssues),
        });
      }
    }

    // Save updated step4
    localStorage.setItem("step4", JSON.stringify([...filtered, ...newEntries]));

    return newEntries;
  } catch (e) {
    console.error("Failed to update step4 with Section A:", e);
    return [];
  }
};

// Collect all section data and format for step4 (for PDF generation)
export const collectAllSectionData = (reportId) => {
  // Section A
  const sectionA = loadSectionData(reportId, "A");
  if (sectionA) {
    updateStep4WithSectionA(reportId, sectionA);
  }

  // Section B
  const sectionB = loadSectionData(reportId, "B");
  if (sectionB) {
    updateStep4WithSectionB(reportId, sectionB);
  }

  // Section C
  const sectionC = loadSectionData(reportId, "C");
  if (sectionC) {
    updateStep4WithSectionC(reportId, sectionC);
  }

  // Return current step4 from localStorage
  try {
    return JSON.parse(localStorage.getItem("step4") || "[]");
  } catch (e) {
    return [];
  }
};
